import { i18n } from '../i18n.js';
import { MoonfallClickGuard } from '../utils/click-guard.js';
import { MoonfallAnalytics } from '../analytics/posthog-init.js';
import { MoonfallCloudGuard } from '../utils/cloud-mutation-guard.js';
import { isPageLeaving } from '../utils/page-leaving.js';
import { MoonfallCloud } from '../convex-client.js';
/**
 * Deck Builder UI Module
 * Allows players to create, edit, and manage custom decks
 */


import { AssetPreloader } from './asset-preloader.js';
import { Cards } from './cards.js';
import { CardTilt } from './card-tilt.js';
import { CollectionLayout } from './collection-layout.js';
import { Firebase } from '../firebase-loader.js';
import { fetchPageAnswer, normalizePageAnswer, pageLabelFor } from './collection-page-answer.js';
import { HoverHandoff } from './hover-handoff.js';
import { createHoverIntent } from './hover-intent.js';
import { Pagination } from './pagination.js';
import { Platform } from './platform.js';
import { StellarAssets } from './stellar-assets.js';
import { SurfaceEntryHold } from './surface-entry-hold.js';
import { SwipePager } from './swipe-pager.js';
import { TouchPreview } from './touch-preview.js';
import { cssLengthPx, uiPx } from './ui-scale.js';
let DeckBuilder = {};

DeckBuilder = (function () {
    let allCards = [];
    let cardById = new Map();
    let ownedCards = [];
    let ownedCardMap = new Map();
    let ownedCardStellarMap = new Map();
    let currentDeck = { name: '', character: null, cardIds: [] };
    let editingDeckId = null;
    /* AN UNSAVED DECK OUTLIVES A CLOSE. Closing the Collection (its ×, Esc,
       or a click on its backdrop a few pixels off the panel) used to clear
       the deck being built: a player lost 14 of 15 cards that way and built
       them again (replay 01a175f7, 26 Oct 2026). close() keeps the edit for
       its account and the next open() puts it back; Cancel and Save end it. */
    /** @type {{ account: string, editingDeckId: string | null, name: string, character: string, cardIds: string[] } | null} */
    let keptEdit = null;
    let readOnlyDeck = null;
    /* A preview answers a pointer the player MOVED (hover-intent.js), as on the
       Collection's grid: a catalog page swapped in, or the deck's cards sliding
       up after a removal, under a resting pointer opens nothing until it moves.
       The catalog filling its pane (27 October 2026) put cards where a resting
       pointer used to find an empty band. */
    const hoverIntent = createHoverIntent({
        selector: '.deck-card-item, .deck-card-mini',
        within: () => document.getElementById('deck-builder-container'),
    });
    let isOpen = false;
    let activeTab = 'characters'; // 'characters' or 'spells'
    let currentSort = 'cost-asc';
    let currentFilters = { collection: '', mana: '' };
    let currentSearch = '';
    let draggedDeckCardId = null;
    const COLLECTION_PAGE_SIZE = 8;
    const MAX_COLLECTION_PAGE_SIZE = 24;
    const MOBILE_COLLECTION_PAGE_SIZE_FALLBACK = 4;
    const DESKTOP_COLLECTION_CARD_SCALE = 1.05;
    const DESKTOP_COLLECTION_READABLE_WIDTH = 132;
    const DESKTOP_COLLECTION_MAX_SCALE = 2.2;
    /* How far a desktop catalog card may shrink below the readable size so
       one more full row fits (CollectionLayout.chooseFilledGrid). 0.72 gives
       the shortest desktop box, 1400x640, two rows of 99px cards (132px is
       the readable size) instead of one row of six. */
    const DESKTOP_COLLECTION_MIN_FILL_SCALE = 0.72;
    const MOBILE_COLLECTION_CARD_SCALE = 1.7;
    const SHORT_LANDSCAPE_COLLECTION_CARD_SCALE = 1.28;
    const COLLECTION_CARD_FIT_SAFETY_INSET = 1;
    const DECK_PAGE_SIZE_FALLBACK = 5;
    const CHARACTER_PAGE_SIZE = 4;
    const CHARACTER_DETAILS_BATCH_SIZE = 20;
    const SAVED_DECK_PAGE_SIZE = 6;
    // Two rows of six on a tall desktop window (screen-fit round 3, 28 October
    // 2026: one row left a third of a 1920x1080 Decks screen empty).
    const TALL_DESKTOP_SAVED_DECK_PAGE_SIZE = 12;
    const LOW_HEIGHT_TABLET_SAVED_DECK_PAGE_SIZE = 4;
    const MOBILE_SAVED_DECK_PAGE_SIZE = 2;
    const DECK_MINI_ART_OBSERVATION_TIMEOUT_MS = 1500;
    // One portrait retry per delay, per open: the budget ends with the list.
    const SAVED_DECK_PORTRAIT_RETRY_DELAYS_MS = Object.freeze([1000, 3000, 8000, 20000, 30000]);
    let collectionPage = 1;
    let collectionPageSize = COLLECTION_PAGE_SIZE;
    let collectionGridColumns = 4;
    let deckPage = 1;
    let deckPageSize = DECK_PAGE_SIZE_FALLBACK;
    let deckRenderRequestId = 0;
    let characterPage = 1;
    let savedDeckPage = 1;
    let currentSavedDeckPageSize = SAVED_DECK_PAGE_SIZE;
    let savedDecksData = [];
    let currentPageEntries = [];
    let characterPageNextCursor = null;
    let characterPageIsDone = true;
    let characterPageCursors = new Map([[1, null]]);
    let characterById = new Map();
    let lastCollectionRequestId = 0;
    /** @type {AbortController | null} */
    let collectionWarmController = null;
    const collectionWarmOwners = new Set();
    let collectionStaleTimer = null;
    let collectionPagerAck = null;
    /* The catalog page on screen, as its commit left it: the question and
       page it answers (collectionPageRequestKey), the render that committed
       it and its answer. A press back to it ends a turn at once
       (endCatalogTurnOnShownPage). Null when its answers went stale. */
    /** @type {{ key: string, requestId: number, pagination: any, warmController: AbortController } | null} */
    let shownCatalogTurn = null;
    /** @type {{ key: string, promise: Promise<any>, requestId: number } | null} the latest render's page read */
    let catalogReadOnItsWay = null;
    /** @type {{ key: string, promise: Promise<any> } | null} a read a turn left behind, for the page after the one on screen */
    let keptCatalogRead = null;
    let lastSavedDeckRequestId = 0;
    let lastSavedDeckRenderRequestId = 0;
    let lastLanguageRefreshId = 0;
    let lastCollectionPagination = { page: 1, totalPages: 1 };
    let availableCollections = [];
    let lastDeckPagination = { page: 1, totalPages: 1 };
    let lastSavedDeckPagination = { page: 1, totalPages: 1 };
    let responsiveCollectionMeasureRequest = 0;
    let savedDeckPortraitRetryTimer = null;
    let savedDeckPortraitRetryAttempt = 0;
    let savedDeckPortraitRetryInFlight = false;
    // A retry answering after its open ended (or a new one began) is dropped.
    let savedDeckPortraitRetryGeneration = 0;
    let savedDeckPortraitRetryListenersBound = false;

    const MIN_DECK_SIZE = 15;
    const MAX_DECK_SIZE = 15;
    // CHARACTERS removed in favor of dynamic loading
    let charactersData = [];
    let characterPortraitByKey = new Map();
    let onCloseCallback = null;
    let isEmbeddedView = false;
    let embeddedHost = null;
    let i18nListenersReady = false;
    let characterGridRenderRequestId = 0;
    let intentWarmListenersReady = false;
    let prebuildYieldListenersReady = false;
    // The catalog's next page, built ahead at idle (`scheduleCatalogPrebuild`).
    let catalogPrebuild = null;
    /** Stellar tiles built ahead from the still card, with their card: the clips are attached as they commit. */
    const catalogTilesAwaitingClips = new WeakMap();

    /* THE ROOT VIEW — the summoner chooser's first page and the saved decks —
       is what the Decks tab shows on entry. It is prepared ahead of the tab
       into the container, which stays connected while hidden, so the tab's
       first frame can be the finished view. What is read ahead follows the
       owner's rule for background reads (26 September 2026: a page or two
       ahead is fine, a player's whole library is not):
         - `warm()` without intent (the Collection's open) reads the chooser's
           first summoner page only;
         - intent on the tab (a pointer, focus, the first touch) or the press
           reads the saved decks too — every deck, the starters and their
           summoners' details — which is a whole list, so never before.
       `chooserView` and `rootView` record whose view the DOM holds, when it
       was read, and whether its portraits have settled. A view read without
       error and younger than ROOT_VIEW_FRESH_MS is adopted as it stands: a
       press just after intent reads nothing again (it used to read it all a
       second time). An older one is presented and re-read behind; the
       reconciling render changes nothing unless the answer did. */
    const ROOT_VIEW_FRESH_MS = 30000;
    let chooserView = null; // { key, at, complete, settlement }
    let chooserViewTask = null; // { key, promise }
    let rootView = null; // { key, at, complete, warmed, settled }
    let rootViewTask = null; // { key, promise }
    let rootViewSettlement = Promise.resolve(true);
    let entryHold = null;

    const STELLAR_PREFIX = 'stellar:';
    const HOVER_PREVIEW_Z_INDEX = '2147483647';
    // Bounded atomic page commit (docs/asset-loading.md law 2 + cohort rule).
    const DECK_BUILDER_REVEAL_CAP_MS = 4000;
    // A re-query keeps the page on screen; past the shared art grace a light
    // sweeps it so a slow answer never reads as the answer (collection.js twin;
    // deck-builder.css, a still dim until 28 September 2026).
    const COLLECTION_STALE_GRACE_MS = 450;
    // A press answered within a few frames needs no busy look of its own.
    const PRESS_ACK_DELAY_MS = 60;
    /* The catalog's next page is built ahead at idle (`scheduleCatalogPrebuild`,
       the Collection grid's twin). An idle period with less room than this
       starts no tile; without requestIdleCallback (WebKit) a timer stands in
       for the idle period, a beat after the page's own frames. */
    const PREBUILD_MIN_IDLE_MS = 10;
    const PREBUILD_FALLBACK_DELAY_MS = 120;
    const PREBUILD_FALLBACK_SLICE_MS = 16;
    // A hidden catalog (another tab, the phone's deck view) is looked at again this often.
    const PREBUILD_HIDDEN_RECHECK_MS = 1000;
    /* An input (a press, a key, a wheel, a hover preview starting) holds the
       build ahead until input has been quiet this long, every input starting
       the wait again. A press is held 80-150 ms; the idle period inside it
       rebuilt the tile the press had just dropped and started its raster,
       which the click then threw away (collection.js twin). */
    const PREBUILD_INPUT_QUIET_MS = 400;
    const HOVER_PREVIEW_MARGIN = 16;
    const HOVER_PREVIEW_OPTIONS = {
        baseScale: 3.5,
        minScale: 1.6,
        margin: HOVER_PREVIEW_MARGIN,
        origin: 'center center',
        translateYPercent: 0,
    };

    function uiText(key, fallback, replacements = {}) {
        const translated = i18n?.t?.(key, replacements);
        const text = translated && translated !== key ? translated : fallback;
        /* BOTH brace styles, double FIRST.
           The copy uses `{{name}}` (as ui.json writes it) and, in a few
           places, single `{name}`. A helper that knows only one leaves the
           other on screen whenever i18n has not resolved. Order matters:
           substituting the single form first turns `{{count}}` into `{3}`,
           which is exactly how starter-reward.js was rendering it. */
        return Object.entries(replacements || {}).reduce(
            (result, [name, value]) => String(result)
                .split(`{{${name}}}`).join(String(value))
                .split(`{${name}}`).join(String(value)),
            String(text),
        );
    }

    async function waitForBoundedCardWarm(warm) {
        if (!warm || typeof warm.then !== 'function') return;
        const gateWarm = AssetPreloader?.gateWarm;
        if (typeof gateWarm !== 'function') {
            // Warming is best-effort. Without the bounded gate, observe the
            // promise but never make interaction latency depend on it.
            void Promise.resolve(warm).catch(() => undefined);
            return;
        }
        try {
            await gateWarm(warm);
        } catch (_) {
            // Final-size raster settlement below is the authority.
        }
    }

    async function settleCollectionCardRaster(cardElement, cardData) {
        const settle = Cards?.settleCardRasterForPresentation;
        if (typeof settle !== 'function') return false;
        try {
            return await settle(cardElement, cardData, {
                oversample: 1,
                stableGeometry: true,
            }) === true;
        } catch (_) {
            return false;
        }
    }

    function isOfficialDeck(deck) {
        return deck?.isOfficialStarter === true || String(deck?.id || '').startsWith('starter:');
    }

    function isReadOnlyDeckMode() {
        return isOfficialDeck(readOnlyDeck);
    }

    function cardFaceRenderOptions() {
        return undefined;
    }

    function deckDisplayName(deck) {
        if (!isOfficialDeck(deck) || !deck?.templateId) return String(deck?.name || '');
        return uiText(
            `deckSelector.starterDeckNames.${deck.templateId}`,
            String(deck.name || deck.templateId),
        );
    }

    function mergeDeckBuilderDecks(personalDecks, officialDecks) {
        const crafted = [];
        const predefined = [];
        const seenIds = new Set();
        [...(personalDecks || []), ...(officialDecks || [])].forEach((deck) => {
            const id = String(deck?.id || '');
            if (!id || seenIds.has(id)) return;
            seenIds.add(id);
            (isOfficialDeck(deck) ? predefined : crafted).push(deck);
        });
        return [...crafted, ...predefined];
    }

    function syncReadOnlyDeckPresentation() {
        const readOnly = isReadOnlyDeckMode();
        const container = document.getElementById('deck-builder-container');
        const nameInput = document.getElementById('deck-name-input');
        const saveButton = document.getElementById('deck-save-btn');
        const clearButton = document.getElementById('deck-clear-btn');
        container?.classList.toggle('deck-builder-readonly', readOnly);

        if (nameInput) {
            nameInput.readOnly = readOnly;
            nameInput.setAttribute('aria-readonly', String(readOnly));
            if (readOnly) {
                const name = deckDisplayName(readOnlyDeck);
                nameInput.value = name;
                currentDeck.name = name;
            }
        }
        if (saveButton) {
            saveButton.hidden = readOnly;
            if (readOnly) saveButton.disabled = true;
        }
        if (clearButton) {
            clearButton.textContent = readOnly
                ? uiText('deckBuilder.backToDecks', 'Back to decks')
                : uiText('deckBuilder.cancel', 'Cancel');
        }
        if (readOnly) setDeckBuilderMobilePane('deck');
    }

    function applyDeckBuilderTranslations() {
        if (i18n?.applyTranslations) {
            i18n.applyTranslations();
        }
    }

    function isMobileDeckBuilderUi() {
        return !!Platform?.isMobileUi?.();
    }

    function isShortLandscapeDeckBuilderViewport() {
        return Boolean(window.matchMedia?.(
            '(orientation: landscape) and (max-height: 560px)',
        )?.matches);
    }

    function readLayoutNumber(style, property, fallback = 0) {
        const value = cssLengthPx(style?.getPropertyValue?.(property));
        return Number.isFinite(value) && value >= 0 ? value : fallback;
    }

    function preferredCollectionCardScale(itemWidth = 0) {
        const shortLandscape = isShortLandscapeDeckBuilderViewport();
        if (shortLandscape) return SHORT_LANDSCAPE_COLLECTION_CARD_SCALE;
        if (isMobileDeckBuilderUi()) return MOBILE_COLLECTION_CARD_SCALE;
        return CollectionLayout?.fitReadableScale?.({
            itemWidth,
            readableWidth: uiPx(DESKTOP_COLLECTION_READABLE_WIDTH),
            minScale: DESKTOP_COLLECTION_CARD_SCALE,
            maxScale: DESKTOP_COLLECTION_MAX_SCALE,
        }) || DESKTOP_COLLECTION_CARD_SCALE;
    }

    function measureCollectionCardToken(grid, fallbackSize, shellZoom = 1) {
        const fallbackLayoutWidth = Number(fallbackSize?.width) / shellZoom;
        const fallbackLayoutHeight = Number(fallbackSize?.height) / shellZoom;
        const fallbackPhysicalWidth = Number(fallbackSize?.width);
        const fallbackPhysicalHeight = Number(fallbackSize?.height);
        if (!grid) {
            return {
                layoutWidth: fallbackLayoutWidth,
                layoutHeight: fallbackLayoutHeight,
                physicalWidth: fallbackPhysicalWidth,
                physicalHeight: fallbackPhysicalHeight,
            };
        }

        const probe = document.createElement('span');
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText = 'position:absolute;left:-9999px;top:0;visibility:hidden;'
            + 'pointer-events:none;width:var(--card-width);height:var(--card-height);';
        grid.appendChild(probe);
        const probeStyle = window.getComputedStyle?.(probe);
        const probeRect = probe.getBoundingClientRect?.();
        const token = {
            layoutWidth: readLayoutNumber(probeStyle, 'width', fallbackLayoutWidth),
            layoutHeight: readLayoutNumber(probeStyle, 'height', fallbackLayoutHeight),
            physicalWidth: Number(probeRect?.width) || fallbackPhysicalWidth,
            physicalHeight: Number(probeRect?.height) || fallbackPhysicalHeight,
        };
        probe.remove();
        return token;
    }

    function applyCollectionGridColumns(entryCount = collectionPageSize) {
        const grid = document.getElementById('deck-builder-collection-cards');
        if (!grid) return;
        if (isMobileDeckBuilderUi() || collectionGridColumns <= 0) {
            grid.classList.remove('deck-builder-cards-balanced');
            grid.style.removeProperty('--deckbuilder-card-track-width');
            if (grid.dataset.balancedColumns === 'true') {
                grid.style.removeProperty('--deckbuilder-column-count');
                delete grid.dataset.balancedColumns;
            }
            return;
        }
        const visibleColumns = Math.max(1, Math.min(
            collectionGridColumns,
            Math.max(1, Number(entryCount) || 0),
        ));
        grid.classList.add('deck-builder-cards-balanced');
        grid.dataset.balancedColumns = 'true';
        grid.style.setProperty('--deckbuilder-column-count', String(visibleColumns));
    }

    function measureCollectionPageSize() {
        const fallback = Platform?.pageSize
            ? Platform.pageSize(COLLECTION_PAGE_SIZE, MOBILE_COLLECTION_PAGE_SIZE_FALLBACK)
            : (isMobileDeckBuilderUi() ? MOBILE_COLLECTION_PAGE_SIZE_FALLBACK : COLLECTION_PAGE_SIZE);
        const grid = document.getElementById('deck-builder-collection-cards');
        const cardSize = Cards?.getCardPixelSize?.();
        const rect = grid?.getBoundingClientRect?.();
        // The Collection shell may be uniformly transformed to fit a short
        // viewport. Card/grid CSS dimensions are authored in pre-transform
        // layout pixels, so using the transformed bounding box here makes each
        // catalog row too tall by that scale factor. Prefer client dimensions;
        // retain the rect fallback for detached/test geometry.
        const layoutWidth = Number(grid?.clientWidth) > 1
            ? Number(grid.clientWidth)
            : Number(rect?.width);
        const layoutHeight = Number(grid?.clientHeight) > 1
            ? Number(grid.clientHeight)
            : Number(rect?.height);
        if (!grid || !CollectionLayout?.fitCapacity
            || !(layoutWidth > 1) || !(layoutHeight > 1)
            || !(Number(cardSize?.width) > 0) || !(Number(cardSize?.height) > 0)) {
            return fallback;
        }

        const style = window.getComputedStyle?.(grid);
        const shell = grid.closest?.('.collection-container');
        const shellZoom = readLayoutNumber(
            shell ? window.getComputedStyle?.(shell) : null,
            'zoom',
            1,
        ) || 1;
        const horizontalInset = readLayoutNumber(style, 'padding-left')
            + readLayoutNumber(style, 'padding-right');
        const verticalInset = readLayoutNumber(style, 'padding-top')
            + readLayoutNumber(style, 'padding-bottom');
        // Measure the card token inside this exact CSS scope. A body-level
        // cached probe can retain the preceding viewport's vmin, while the
        // Collection shell may also counter-scale its own --card-unit. The
        // local token keeps physical readability and layout tracks in sync.
        const cardToken = measureCollectionCardToken(grid, cardSize, shellZoom);
        const preferredScale = preferredCollectionCardScale(cardToken.physicalWidth);
        const columnGap = readLayoutNumber(style, 'column-gap', uiPx(12));
        const rowGap = readLayoutNumber(style, 'row-gap', uiPx(12));
        const availableWidth = Math.max(1, layoutWidth - horizontalInset);
        const availableHeight = Math.max(
            1,
            layoutHeight - verticalInset - COLLECTION_CARD_FIT_SAFETY_INSET,
        );
        const applyCardScale = (scale) => {
            const fittedScale = String(scale);
            grid.style.setProperty('--deckbuilder-fit-card-scale', fittedScale);
            // Keep the grid tracks and their physical card wrappers on the same
            // value in the current frame. Relying on the two chained custom-property
            // aliases can leave existing children at the preceding fit for one
            // layout pass after a live viewport change.
            grid.style.setProperty('--catalog-card-scale', fittedScale);
            grid.style.setProperty('--collection-card-scale', fittedScale);
        };
        if (!isMobileDeckBuilderUi() && CollectionLayout.chooseFilledGrid) {
            grid.style.removeProperty('--deckbuilder-fit-columns');
            const filled = CollectionLayout.chooseFilledGrid({
                width: availableWidth,
                height: availableHeight,
                itemWidth: cardToken.layoutWidth * preferredScale,
                itemHeight: cardToken.layoutHeight * preferredScale,
                columnGap,
                rowGap,
                minScale: DESKTOP_COLLECTION_MIN_FILL_SCALE,
                maxItems: MAX_COLLECTION_PAGE_SIZE,
            });
            if (filled.pageSize > 0) {
                const scale = Math.floor(preferredScale * filled.scale * 1000) / 1000;
                applyCardScale(scale);
                // The embedded Collection shell can scale the wrapper and the root
                // vmin card token by different cascades. Use the live measured
                // wrapper width as the track authority so a readable card never
                // occupies a narrower track or spills into the current-deck pane.
                grid.style.setProperty('--deckbuilder-card-track-width', `${cardToken.layoutWidth * scale}px`);
                collectionGridColumns = filled.columns;
                /* A page on screen keeps the column count of the cards it
                   shows; the next page's count arrives with that page, in
                   the commit. Measuring at the start of every re-query used
                   to write the FULL count here, so a two-card answer jumped
                   into four columns on the next keystroke (fix review #2,
                   25 September 2026). An empty grid takes the full count now,
                   so its stage settles at the committed geometry. */
                applyCollectionGridColumns(committedCatalogEntryCount(grid) ?? filled.pageSize);
                return filled.pageSize;
            }
        }
        const needsTwoCompactRows = isMobileDeckBuilderUi()
            && isShortLandscapeDeckBuilderViewport()
            && Number(rect.height) >= 160;
        const targetRows = needsTwoCompactRows ? 2 : 1;
        const scaleAvailable = Math.max(
            1,
            (
                layoutHeight
                - verticalInset
                - COLLECTION_CARD_FIT_SAFETY_INSET
                - (rowGap * (targetRows - 1))
            ) / targetRows,
        );
        const scale = CollectionLayout.fitItemScale({
            available: scaleAvailable,
            item: cardToken.layoutHeight,
            preferredScale,
            inset: 0,
            safetyInset: 0,
        });
        applyCardScale(scale);
        const itemWidth = cardToken.layoutWidth * scale;
        const itemHeight = cardToken.layoutHeight * scale;
        collectionGridColumns = 0;
        applyCollectionGridColumns();
        const fittedColumns = CollectionLayout.fitCapacity({
            width: availableWidth,
            height: itemHeight,
            itemWidth,
            itemHeight,
            columnGap,
            rowGap,
            maxRows: 1,
            maxItems: MAX_COLLECTION_PAGE_SIZE,
        });
        const fittedRows = CollectionLayout.fitCapacity({
            width: itemWidth,
            height: availableHeight,
            itemWidth,
            itemHeight,
            columnGap,
            rowGap,
            maxItems: MAX_COLLECTION_PAGE_SIZE,
        });
        const declaredColumns = Math.floor(readLayoutNumber(style, '--deckbuilder-column-count', 0));
        const columns = declaredColumns > 0
            ? Math.min(declaredColumns, fittedColumns)
            : fittedColumns;
        // The grid lays out the columns the page was cut for, never more
        // tracks than its cards fill (deck-builder.css).
        grid.style.setProperty('--deckbuilder-fit-columns', String(Math.max(1, columns)));
        const capacity = Math.min(MAX_COLLECTION_PAGE_SIZE, columns * fittedRows);
        return capacity > 0 ? capacity : fallback;
    }

    function syncCollectionPageSize({ preserveOffset = true } = {}) {
        const nextPageSize = measureCollectionPageSize();
        if (nextPageSize === collectionPageSize) return false;
        if (preserveOffset && CollectionLayout?.pageForLogicalOffset) {
            collectionPage = CollectionLayout.pageForLogicalOffset({
                page: collectionPage,
                pageSize: collectionPageSize,
                nextPageSize,
            });
        }
        collectionPageSize = nextPageSize;
        return true;
    }

    function scheduleSettledCollectionMeasurement() {
        const requestId = ++responsiveCollectionMeasureRequest;
        const scheduleFrame = typeof window.requestAnimationFrame === 'function'
            ? window.requestAnimationFrame.bind(window)
            : (callback) => window.setTimeout(callback, 0);
        const measureSettledGrid = () => {
            if (requestId !== responsiveCollectionMeasureRequest || !isOpen || !currentDeck.character) {
                return;
            }
            const main = document.getElementById('deck-builder-main');
            if (!main || main.getClientRects().length === 0) return;
            refitCatalogPage();
        };
        scheduleFrame(() => {
            measureSettledGrid();
            scheduleFrame(measureSettledGrid);
        });
    }

    /* A resize changed how many cards a page holds: ask for the page of the
       new size. The page on screen stays until it lands (renderCollection),
       and it was cut for the old size, so until then it shows no more cards
       than the new grid holds: a shrink never pushes a row through the pager. */
    function refitCatalogPage() {
        if (!syncCollectionPageSize({ preserveOffset: true })) return;
        const grid = document.getElementById('deck-builder-collection-cards');
        Array.from(grid?.querySelectorAll(':scope > .deck-card-item') || []).forEach((tile, index) => {
            /** @type {HTMLElement} */ (tile).style.display = index < collectionPageSize ? '' : 'none';
        });
        void renderCollection(undefined, { resetScroll: true });
    }

    function measureDeckPageSize() {
        const container = document.getElementById('deck-builder-deck-cards');
        if (!container) return DECK_PAGE_SIZE_FALLBACK;
        if (isShortLandscapeDeckBuilderViewport()) return MAX_DECK_SIZE;
        const style = window.getComputedStyle?.(container);
        const rowHeight = readLayoutNumber(style, '--deckbuilder-deck-row-height', uiPx(42));
        const rowGap = readLayoutNumber(style, 'row-gap', uiPx(4));
        if (!(rowHeight > 0)) return DECK_PAGE_SIZE_FALLBACK;

        const pager = document.getElementById('deck-current-pagination');
        const measureLayoutHeight = () => (
            Number(container.clientHeight)
            || Number(container.getBoundingClientRect?.().height)
            || 0
        );
        let unpaginatedHeight = measureLayoutHeight();
        let paginatedHeight = unpaginatedHeight;

        if (pager) {
            const wasHidden = pager.hidden;
            const previousVisibility = pager.style.visibility;
            const previousPointerEvents = pager.style.pointerEvents;
            const previousAriaHidden = pager.getAttribute('aria-hidden');
            try {
                pager.style.visibility = 'hidden';
                pager.style.pointerEvents = 'none';
                pager.setAttribute('aria-hidden', 'true');

                pager.hidden = true;
                unpaginatedHeight = measureLayoutHeight();

                pager.hidden = false;
                paginatedHeight = measureLayoutHeight();
            } finally {
                pager.hidden = wasHidden;
                pager.style.visibility = previousVisibility;
                pager.style.pointerEvents = previousPointerEvents;
                if (previousAriaHidden === null) pager.removeAttribute('aria-hidden');
                else pager.setAttribute('aria-hidden', previousAriaHidden);
            }
        }

        if (!(unpaginatedHeight > 1) || !(paginatedHeight > 1)) {
            return DECK_PAGE_SIZE_FALLBACK;
        }

        if (CollectionLayout?.fitPaginatedCapacity) {
            return CollectionLayout.fitPaginatedCapacity({
                totalItems: currentDeck.cardIds.length,
                unpaginatedHeight,
                paginatedHeight,
                itemHeight: rowHeight,
                gap: rowGap,
                maxItems: MAX_DECK_SIZE,
            }).pageSize;
        }

        return Math.max(1, Math.min(
            MAX_DECK_SIZE,
            Math.floor((unpaginatedHeight + rowGap) / (rowHeight + rowGap)),
        ));
    }

    function syncDeckPageSize({ preserveOffset = true } = {}) {
        const nextPageSize = measureDeckPageSize();
        if (nextPageSize === deckPageSize) return false;
        if (preserveOffset && CollectionLayout?.pageForLogicalOffset) {
            deckPage = CollectionLayout.pageForLogicalOffset({
                page: deckPage,
                pageSize: deckPageSize,
                nextPageSize,
            });
        }
        deckPageSize = nextPageSize;
        return true;
    }

    function savedDeckPageSize() {
        if (isTightDeckBuilderViewport()) return MOBILE_SAVED_DECK_PAGE_SIZE;
        if (isLowHeightTabletDeckBuilderViewport()) {
            return LOW_HEIGHT_TABLET_SAVED_DECK_PAGE_SIZE;
        }
        if (isTallDesktopDeckBuilderViewport()) return TALL_DESKTOP_SAVED_DECK_PAGE_SIZE;
        return SAVED_DECK_PAGE_SIZE;
    }

    function syncSavedDeckPageSize({ preserveOffset = true } = {}) {
        const nextPageSize = savedDeckPageSize();
        if (nextPageSize === currentSavedDeckPageSize) return false;
        if (preserveOffset) {
            if (CollectionLayout?.pageForLogicalOffset) {
                savedDeckPage = CollectionLayout.pageForLogicalOffset({
                    page: savedDeckPage,
                    pageSize: currentSavedDeckPageSize,
                    nextPageSize,
                });
            } else {
                const logicalOffset = Math.max(0, (savedDeckPage - 1) * currentSavedDeckPageSize);
                savedDeckPage = Math.floor(logicalOffset / nextPageSize) + 1;
            }
        }
        currentSavedDeckPageSize = nextPageSize;
        return true;
    }

    function clamp(value, min, max) {
        if (!Number.isFinite(value)) return min;
        if (!Number.isFinite(min)) return value;
        if (!Number.isFinite(max)) return value;
        if (min > max) return min;
        return Math.min(Math.max(value, min), max);
    }

    /* The preview the pointer can still own: the one holding the id. One
       handed to a neighbour (HoverHandoff) gave the id up and stays on screen
       by its class until the neighbour's own preview is revealed, so this
       answers with the one being built, or null before it exists. */
    function currentHoverPreview() {
        if (typeof HoverHandoff?.currentPreview === 'function') return HoverHandoff.currentPreview();
        return document.getElementById('card-hover-preview');
    }

    /* `keepHandedOff`: a hover starting or ending on a card leaves a preview
       handed to a neighbour alone — it leaves in the task the neighbour's is
       revealed, or when no hover is left to reveal one. Everything else is a
       tear-down and takes it too. */
    function clearHoverPreview({ keepHandedOff = false } = {}) {
        if (!keepHandedOff) HoverHandoff?.releaseHandedOff?.();
        const preview = currentHoverPreview();
        if (!preview) return;
        const previewCard = preview.querySelector('.card');
        if (previewCard?.classList?.contains('stellar-card') && Cards?.setStellarHover) {
            Cards.setStellarHover(previewCard, false);
        }
        preview.remove();
    }

    function isScrollableHost(element) {
        if (!element || typeof element.scrollTop !== 'number') return false;
        return (element.scrollHeight - element.clientHeight) > 1;
    }

    function findScrollableAncestor(element) {
        let current = element;
        while (current && current !== document.body && current !== document.documentElement) {
            if (isScrollableHost(current)) return current;
            current = current.parentElement;
        }
        return null;
    }

    function findScrollHostBySelectors(target, selectors = []) {
        for (const selector of selectors) {
            if (!selector) continue;
            const fromTarget = target?.closest ? target.closest(selector) : null;
            if (fromTarget && typeof fromTarget.scrollTop === 'number') return fromTarget;
            const fromDocument = document.querySelector(selector);
            if (fromDocument && typeof fromDocument.scrollTop === 'number') return fromDocument;
        }
        return null;
    }

    function forwardWheelScroll(preview, event, fallbackSelectors = []) {
        if (!preview || !event) return;

        preview.style.pointerEvents = 'none';
        const target = typeof document.elementFromPoint === 'function'
            ? document.elementFromPoint(event.clientX, event.clientY)
            : null;
        preview.style.pointerEvents = 'auto';

        const scrollHost = findScrollableAncestor(target)
            || findScrollHostBySelectors(target, fallbackSelectors);
        if (scrollHost) {
            scrollHost.scrollTop += event.deltaY;
            if (event.cancelable) event.preventDefault();
            return;
        }

        if (typeof window.scrollBy === 'function') {
            window.scrollBy(0, event.deltaY);
            if (event.cancelable) event.preventDefault();
        }
    }

    /* Every caller builds the popup card with `Cards.createCardElement` and
       bails when it is missing, so the module that owns `applyPreviewScale` is
       always loaded by the time this runs. There used to be a local copy of the
       enlargement here as a partial-load fallback; it was unreachable, and it
       silently kept the transform model after the shared one moved to physical
       sizing — a second, blurrier definition of the same behaviour. One
       implementation, in one place; if it is genuinely absent the popup opens
       at rest size, which is visibly degraded rather than quietly wrong. */
    /* `sourceCard` is the card the popup enlarges: the popup adopts its
       description (size step + lines) so the text never re-wraps between the
       resting card and its enlargement (Cards: ENGRAVED DESCRIPTIONS). */
    function applyStableHoverPreviewScale(cardEl, variant, anchorRect, preview, sourceCard = null) {
        Cards?.applyPreviewScale?.(
            cardEl,
            variant,
            anchorRect,
            preview,
            sourceCard ? { ...HOVER_PREVIEW_OPTIONS, sourceCard } : HOVER_PREVIEW_OPTIONS,
        );
    }

    async function buildAuthHeaders() {
        if (Firebase?.getIdToken) {
            try {
                const token = await Firebase.getIdToken();
                if (token) {
                    return { Authorization: `Bearer ${token}` };
                }
            } catch (_) {
                return {};
            }
        }
        return {};
    }

    function sanitizeImageUrlForDirectClient(raw) {
        if (!raw || typeof raw !== 'string') return null;
        const value = raw.trim();
        if (!value) return null;
        try {
            let pathname = new URL(
                value,
                window.location?.href || 'http://localhost/',
            ).pathname;
            for (let count = 0; count < 4; count += 1) {
                const decoded = decodeURIComponent(pathname);
                if (decoded === pathname) break;
                pathname = decoded;
            }
            if (pathname === '/assets/art_placeholder.webp') return null;
        } catch (_) {
            return null;
        }
        if (value.startsWith('/api/card-image/')) return null;
        if (value.startsWith('http') || value.startsWith('data:') || value.startsWith('blob:')) {
            return value;
        }
        if (value.startsWith('/assets/') || value.startsWith('assets/')) {
            return value;
        }
        return null;
    }

    function loadDeckBuilderExactImage(image, rawSource, profile = {}) {
        if (!image) return false;
        Cards?.disposeCardArtImage?.(image);
        window.MoonfallRetryingImage?.dispose?.(image, { clearSource: true });
        delete image.dataset.deckBuilderExactSource;
        const source = sanitizeImageUrlForDirectClient(rawSource);
        if (!source) {
            image.removeAttribute('src');
            image.classList.add('card-art-unavailable');
            trackPortraitReadiness(image, null);
            return false;
        }

        image.dataset.deckBuilderArtOwner = 'true';
        image.dataset.deckBuilderExactSource = source;
        const cards = Cards;
        if (typeof cards?.loadCardArtImage === 'function') {
            try {
                const loading = cards.loadCardArtImage(
                    image,
                    source,
                    profile,
                    { directArtOnly: true },
                );
                trackPortraitReadiness(image, loading);
                return true;
            } catch (_) {
                // The shared exact-source owner is optional in isolated shells.
                // Continue to the local exact-source path without inventing art.
            }
        }
        trackPortraitReadiness(image, null);
        if (typeof window.MoonfallRetryingImage?.load === 'function') {
            window.MoonfallRetryingImage.load(image, source);
            return true;
        }

        image.setAttribute('src', source);
        if (typeof cards?.enhanceCardArt === 'function') {
            try {
                const enhancement = cards.enhanceCardArt(image, source, profile);
                void Promise.resolve(enhancement).catch(() => undefined);
            } catch (_) {
                // Direct pixels remain authoritative when enhancement is absent.
            }
        }
        return false;
    }

    function disposeDeckBuilderExactImages(root) {
        root?.querySelectorAll?.(
            'img[data-deck-builder-exact-source], img[data-deck-builder-art-owner]',
        ).forEach((image) => {
            Cards?.disposeCardArtImage?.(image);
            window.MoonfallRetryingImage?.dispose?.(image);
        });
    }

    /* PORTRAIT READINESS, PER NODE.
       `Cards.loadCardArtImage` answers for the exact node it loads: its promise
       resolves true once THAT node's pixels are drawable. The Decks tab joins
       that answer — plus a decode, so the reveal frame does not pay for it —
       before it shows a portrait, so no summoner is painted as a well that
       fills in later. A node nothing can answer for (an isolated shell with no
       shared loader) gates nothing. */
    const portraitReadiness = new WeakMap();

    function trackPortraitReadiness(image, readiness) {
        if (!image) return;
        if (!readiness || typeof readiness.then !== 'function') {
            portraitReadiness.delete(image);
            return;
        }
        const record = { settled: false, promise: null };
        record.promise = Promise.resolve(readiness).then(async (ready) => {
            if (ready === true && typeof image.decode === 'function') {
                try {
                    await image.decode();
                } catch (_) {
                    // Drawable pixels already exist; the browser decodes them
                    // at paint instead.
                }
            }
            record.settled = ready === true;
            return ready === true;
        }, () => false);
        portraitReadiness.set(image, record);
    }

    function portraitIsSettled(image) {
        const record = portraitReadiness.get(image);
        return !record || record.settled === true;
    }

    /** Resolves when every portrait has settled, or at the shared reveal cap. */
    function waitForPortraits(images, capMs = DECK_BUILDER_REVEAL_CAP_MS) {
        const pending = images
            .map((image) => portraitReadiness.get(image)?.promise)
            .filter(Boolean);
        if (pending.length === 0) return Promise.resolve(true);
        let timer = null;
        return Promise.race([
            Promise.all(pending).then((results) => results.every(Boolean)),
            new Promise((resolve) => {
                timer = setTimeout(() => resolve(false), capMs);
            }),
        ]).then((settled) => {
            clearTimeout(timer);
            return settled;
        });
    }

    /* A portrait node is only worth keeping while it still has its source, or
       its loader is still working on one. Closing ends the loads that never
       finished (so no hidden retry outlives the surface), which leaves those
       nodes empty; the next render rebuilds exactly them. */
    function portraitIsLive(image) {
        if (!image) return true;
        return Boolean(image.getAttribute('src') || image.dataset.cardArtLoaderId);
    }

    function deckBuilderIsPresented() {
        return isOpen && !entryHold?.active;
    }

    /* A RE-RENDER RECONCILES; A NEW PAGE IS STAGED, THEN SWAPPED WHOLE.
       `nodes` is the target's next children, in order; `fresh` are the ones
       built for this render (the rest are kept nodes, already on screen). While
       the tab is presented, fresh nodes wait in a hidden, connected stage until
       their portraits settle (capped), then the whole set lands in one
       `replaceChildren` — the old page stays up until then, so the player never
       sees a half page, an empty well or a skeleton. While the tab is held or
       hidden there is nothing to protect: the nodes are committed at once.
       Returns `committed` (the swap is done) and `settled` (every portrait the
       target now shows has settled, capped) — the hold waits on the second. */
    function commitDeckBuilderNodes(target, nodes, fresh, { isCurrent, portraitSelector, onCommit }) {
        const unchanged = nodes.length === target.children.length
            && nodes.every((node, index) => target.children[index] === node);
        const portraitsOf = (list) => list.flatMap((node) => Array.from(node.querySelectorAll(portraitSelector)));
        const commit = () => {
            if (!unchanged) {
                const kept = new Set(nodes);
                Array.from(target.children).forEach((child) => {
                    if (!kept.has(child)) disposeDeckBuilderExactImages(child);
                });
                target.replaceChildren(...nodes);
            }
            if (typeof onCommit === 'function') onCommit();
        };
        if (unchanged || fresh.length === 0 || !deckBuilderIsPresented()) {
            commit();
            return { committed: Promise.resolve(true), settled: waitForPortraits(portraitsOf(nodes)) };
        }

        let stage = target.nextElementSibling;
        if (!stage?.classList.contains('deck-builder-paint-stage')) {
            stage = document.createElement('div');
            stage.className = 'deck-builder-paint-stage';
            stage.setAttribute('aria-hidden', 'true');
            stage.inert = true;
            target.after(stage);
        }
        stage.append(...fresh);
        const retireStage = () => {
            if (stage.isConnected && stage.childElementCount === 0) stage.remove();
        };
        const swapped = waitForPortraits(portraitsOf(fresh)).then(() => {
            if (!isCurrent()) {
                fresh.forEach((node) => {
                    if (node.parentElement !== stage) return;
                    disposeDeckBuilderExactImages(node);
                    node.remove();
                });
                retireStage();
                return false;
            }
            commit();
            retireStage();
            return true;
        });
        return { committed: swapped, settled: swapped };
    }

    async function hydrateCardImagesDirectFromConvex(cards) {
        if (!Array.isArray(cards)) return;

        cards.forEach((card) => {
            if (!card || typeof card !== 'object') return;
            card.image = sanitizeImageUrlForDirectClient(card.image);
        });
    }

    function getCharacterPortraitUrl(characterKey) {
        if (!characterKey) return null;
        return sanitizeImageUrlForDirectClient(characterPortraitByKey.get(characterKey));
    }

    function parseDeckCardId(cardId) {
        if (typeof cardId !== 'string') {
            return { baseId: '', variant: 'normal' };
        }
        if (cardId.startsWith(STELLAR_PREFIX)) {
            return { baseId: cardId.slice(STELLAR_PREFIX.length), variant: 'stellar' };
        }
        return { baseId: cardId, variant: 'normal' };
    }

    function formatDeckCardId(baseId, variant = 'normal') {
        if (!baseId) return '';
        return variant === 'stellar' ? `${STELLAR_PREFIX}${baseId}` : baseId;
    }

    function getStellarAsset(cardId) {
        if (!StellarAssets) return null;
        return StellarAssets.get(cardId);
    }

    async function loadStellarAssets() {
        if (!StellarAssets?.load) return;
        const ids = Array.from(ownedCardStellarMap.keys());
        if (ids.length === 0) return;
        try {
            await StellarAssets.load(ids);
        } catch (err) {
            console.warn('[DeckBuilder] Failed to load stellar assets:', err);
        }
    }

    function buildCardElementData(card, variant = 'normal', stellarAsset = null) {
        const isStellar = variant === 'stellar';
        const data = {
            id: card.id,
            instanceId: card.id,
            name: card.name,
            type: card.type,
            manaCost: card.cost || 0,
            picture: card.image,
            imageUrl: card.image,
            description: card.description || '',
            targetingType: card.targetingType || 'None',
            character: card.character,
            isStellar,
        };

        if (isStellar && stellarAsset?.status === 'complete') {
            StellarAssets?.applyMediaToCard?.(data, stellarAsset);
        }

        return data;
    }

    function getOwnedNormalQty(cardId) {
        return ownedCardMap.get(cardId) || 0;
    }

    function getOwnedStellarQty(cardId) {
        return ownedCardStellarMap.get(cardId) || 0;
    }

    function getOwnedVariantQty(cardId, variant = 'normal') {
        return variant === 'stellar' ? getOwnedStellarQty(cardId) : getOwnedNormalQty(cardId);
    }

    function isCardOwned(cardId) {
        return getOwnedNormalQty(cardId) > 0 || getOwnedStellarQty(cardId) > 0;
    }

    function isCardBalanceBanned(card) {
        return Boolean(card && (card.isBalanceBanned === true
            || card.balanceStatus === 'banned'
            || Number(card.cost) === 11));
    }

    function isDeckCardBalanceBanned(deckCardId) {
        const { baseId } = parseDeckCardId(deckCardId);
        return isCardBalanceBanned(getCachedCard(baseId));
    }

    function setAllCardsOwned() {
        ownedCardMap = new Map();
        ownedCardStellarMap = new Map();
        ownedCards = allCards.map(card => card.id);
        ownedCards.forEach(cardId => ownedCardMap.set(cardId, 1));
    }

    function invalidateCollectionCache() {
        collectionPage = 1;
        currentPageEntries = [];
    }

    function replaceCardCache(cards) {
        cardById = new Map();
        allCards = [];
        upsertCardCache(cards);
    }

    function upsertCardCache(cards) {
        (cards || []).forEach((card) => {
            if (!card || !card.id) return;
            const existing = cardById.get(card.id) || {};
            const next = {
                ...existing,
                ...card,
            };
            cardById.set(card.id, next);
        });
        allCards = Array.from(cardById.values());
    }

    function getCachedCard(cardId) {
        if (!cardId) return null;
        return cardById.get(cardId) || null;
    }

    function normalizeSearchText(value) {
        return String(value || '').trim().toLowerCase();
    }

    function setCurrentSearch(value) {
        if (value !== undefined) {
            currentSearch = normalizeSearchText(value);
        }
        return currentSearch;
    }

    function paginateList(items, pageSize, page) {
        if (Pagination?.paginate) {
            return Pagination.paginate(items, pageSize, page);
        }
        const list = Array.isArray(items) ? items : [];
        const size = Number(pageSize) > 0 ? Number(pageSize) : 1;
        const totalItems = list.length;
        const totalPages = Math.max(1, Math.ceil(totalItems / size));
        const currentPage = Math.min(Math.max(Number(page) || 1, 1), totalPages);
        const startIndex = (currentPage - 1) * size;
        const endIndex = startIndex + size;
        return {
            totalItems,
            totalPages,
            page: currentPage,
            pageSize: size,
            startIndex,
            endIndex: Math.min(endIndex, totalItems),
            pageItems: list.slice(startIndex, endIndex),
        };
    }

    async function fetchCardDetails(cardIds, { refresh = false } = {}) {
        const uniqueIds = Array.from(new Set((cardIds || []).map((id) => String(id || '').trim()).filter(Boolean)));
        if (uniqueIds.length === 0) return [];

        const headers = await buildAuthHeaders();
        const params = new URLSearchParams({ cardIds: uniqueIds.join(',') });
        params.set('lang', i18n?.getLanguage?.() || 'en');
        if (refresh) params.set('refresh', '1');
        const response = await fetch(`/api/cards/details?${params.toString()}`, { headers });
        if (!response.ok) {
            throw new Error('Failed to fetch card details');
        }
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        upsertCardCache(list);
        list.forEach((card) => {
          const ownedNormalQty = Number(card?.ownedNormalQty) || 0;
          const ownedStellarQty = Number(card?.ownedStellarQty) || 0;
          if (ownedNormalQty > 0) {
            ownedCardMap.set(card.id, ownedNormalQty);
            if (!ownedCards.includes(card.id)) ownedCards.push(card.id);
          }
          if (ownedStellarQty > 0) {
            ownedCardStellarMap.set(card.id, ownedStellarQty);
            if (!ownedCards.includes(card.id)) ownedCards.push(card.id);
          }
        });
        return list;
    }

    function collectionPageRequestKey(page) {
        return new URLSearchParams({
            page: String(page),
            pageSize: String(collectionPageSize),
            tab: activeTab,
            character: currentDeck.character || '',
            collection: currentFilters.collection || '',
            mana: currentFilters.mana || '',
            sort: currentSort || 'cost-asc',
            search: currentSearch || '',
            lang: i18n?.getLanguage?.() || 'en',
        }).toString();
    }

    async function fetchCollectionPage({ refresh = false, page = collectionPage, prefetch = false } = {}) {
        const headers = await buildAuthHeaders();
        const params = new URLSearchParams(collectionPageRequestKey(page));
        if (refresh) params.set('refresh', '1');
        // A look-ahead never waits on a very large collection's index; the
        // page the player asked for does, as long as the Deck Builder is open.
        const response = await fetchPageAnswer(`/api/deck-builder/cards-page?${params.toString()}`, { headers },
            prefetch ? { maxWaitMs: 0 } : { wanted: () => isOpen, onWaiting: showCatalogIndexWait });
        // A page still not reached when the wait ended (202) is no page either.
        if (!response.ok || response.status === 202) {
            throw new Error('Failed to fetch deck builder page');
        }
        return normalizePageAnswer(await response.json());
    }

    /* A VERY LARGE COLLECTION NOT READY TO ANSWER (collection-page-answer.js):
       past the wait's grace the catalog says so where it says "Loading
       cards...", and the page replaces it as it replaces that line. */
    function showCatalogIndexWait(state) {
        const container = document.getElementById('deck-builder-collection-cards');
        if (!container || !state) return;
        dropCatalogPrebuild();
        disposeDeckBuilderExactImages(container);
        const notice = document.createElement('div');
        notice.className = 'collection-empty';
        notice.dataset.indexWait = state;
        notice.setAttribute('role', 'status');
        notice.textContent = state === 'searching'
            ? uiText('collection.cards.searching', 'Still searching your collection...')
            : uiText('collection.cards.preparing', 'Preparing your collection. A very large one can take a while.');
        container.replaceChildren(notice);
        container.setAttribute('aria-busy', 'true');
    }

    // Likely-next prefetch (docs/asset-loading.md law 3) — mirrors the
    // Collection screen: page N+1's metadata plus a low-priority art warm run
    // while page N is being read. Best-effort; misses fall back to a fetch.
    // The last next-page answer, readable without taking it from the prefetch
    // (which hands it to the page turn): the page built ahead is built from it.
    let prefetchedCatalogPage = null; // { key, pagination }
    const collectionPagePrefetch = Pagination.createPagePrefetch({
        requestKey: collectionPageRequestKey,
        fetchPage: (page) => {
            const key = collectionPageRequestKey(page);
            return fetchCollectionPage({ page, prefetch: true }).then((pagination) => {
                if (pagination) {
                    prefetchedCatalogPage = { key, pagination };
                    resumeCatalogPrebuildOnData(key);
                }
                return pagination;
            });
        },
        warmEntries: (entries) => {
            const cards = entries.map((entry) => entry?.card).filter(Boolean);
            if (cards.length > 0) {
                AssetPreloader?.warmCards?.(cards, { priority: 'low' });
            }
        },
    });

    // Label lookup for the Collection dropdown, mirroring the Collection
    // surface. `Spells` is never offered here — the Spells tab already filters
    // by summoner, so the option would restate the tab.
    const COLLECTION_FILTER_LABELS = {
        'Classic': ['deckBuilder.filters.classic', 'Classic'],
        'Mythos': ['deckBuilder.filters.mythology', 'Mythology'],
        'Anime Archetypes': ['deckBuilder.filters.anime', 'Anime'],
        'Public Domain': ['deckBuilder.filters.publicDomain', 'Public Domain'],
        'Brainrot': ['deckBuilder.filters.brainrot', 'Brainrot'],
        'Cute Creatures': ['deckBuilder.filters.cuteCreatures', 'Cute Creatures'],
        'Fun': ['deckBuilder.filters.fun', 'Fun'],
        'Moon Foundry': ['deckBuilder.filters.crafted', 'Crafted'],
    };

    let renderedCollectionOptionsKey = null;

    /**
     * How wide the engine would make this control if it held every label.
     *
     * Measured with a real, off-screen <select> rather than canvas text plus a
     * constant for the dropdown arrow: WebKit reserves more chrome than Blink,
     * so a canvas estimate under-shot it by 6px and the toolbar still moved.
     * The clone carries the same class and lives in the same parent, so it
     * inherits the identical computed font, padding and arrow.
     */
    function measureWidestSelectWidth(select, labels) {
        const parent = select.parentElement;
        if (!parent) return 0;
        const probe = document.createElement('select');
        probe.className = select.className;
        probe.setAttribute('aria-hidden', 'true');
        probe.tabIndex = -1;
        probe.style.cssText = 'position:absolute;left:-9999px;top:0;'
            + 'visibility:hidden;pointer-events:none;width:auto;min-width:0;max-width:none;';
        probe.innerHTML = labels
            .map((label) => `<option>${escapeHtml(label)}</option>`)
            .join('');
        parent.appendChild(probe);
        const width = Math.ceil(probe.getBoundingClientRect().width);
        probe.remove();
        return Number.isFinite(width) && width > 0 ? width : 0;
    }

    /**
     * Hold the collection filter at the width its widest POSSIBLE label needs.
     *
     * A <select> sizes to its widest option, so when the real list replaced the
     * two bootstrap options the control grew — 124px -> 131px at 1400x640,
     * 139px -> 147px at 1024x768 — and pushed the search box and the rest of the
     * inline toolbar sideways after first paint. Reserving the width up front
     * costs a few pixels of a control that was going to reach that size anyway,
     * and nothing moves when the list lands.
     *
     * Measured, not guessed: labels are localized and the font is viewport
     * derived, so a `ch` or px constant would be wrong in some language at some
     * size. Only the inline layout needs it — inside the phone popover the
     * select is already `width: 100%`.
     */
    /* What the last measurement was taken for: the labels, the viewport box
       and whether the webfonts had landed. Every catalog render asks (a
       keystroke in the search is one), and each measurement inserts a probe
       <select> and forces a layout of the whole page: five per fast query on
       a 4x-throttled phone, for a width that had not changed. */
    let collectionFilterWidthKey = null;

    function reserveCollectionFilterWidth() {
        const select = document.getElementById('filter-collection');
        if (!select) return;
        if (isTightDeckBuilderViewport()) {
            collectionFilterWidthKey = null;
            select.style.removeProperty('min-width');
            return;
        }
        const labels = [
            uiText('deckBuilder.filters.allCollections', 'All Collections'),
            uiText('deckBuilder.filters.stellar', 'Stellar'),
            uiText('deckBuilder.filters.collectionsLoading', 'Loading…'),
            ...Object.values(COLLECTION_FILTER_LABELS).map(([key, fallback]) => uiText(key, fallback)),
        ];
        const key = [
            labels.join('\u0001'),
            window.innerWidth,
            window.innerHeight,
            document.fonts?.status || '',
        ].join('|');
        if (key === collectionFilterWidthKey && select.style.minWidth) return;
        const reserved = measureWidestSelectWidth(select, labels);
        if (reserved > 0) {
            select.style.minWidth = `${reserved}px`;
            collectionFilterWidthKey = key;
        }
    }

    /**
     * The collection list is a property of the ACCOUNT, and it only ever
     * arrives with a catalog page. Until one lands, the two options that ship
     * in the markup are a BOOTSTRAP, not the player's holdings — so the control
     * has to say so. On a phone the dropdowns sit behind a "Filters" button the
     * player taps immediately, so they read the list inside exactly that
     * window; desktop hides it because the dropdowns are always on screen and
     * have filled in by the time anyone looks. Reported as "filters don't
     * include all options desktop has".
     */
    let collectionsResolved = false;

    function updateCollectionFilterOptions({ force = false } = {}) {
        const select = document.getElementById('filter-collection');
        if (!select) return;

        const names = Array.isArray(availableCollections) ? availableCollections : [];
        // Only redraw when the list actually changed, so a page turn cannot
        // reset a dropdown the player has open. The resolved flag is part of
        // the key: the pending marker has to come off the moment the real list
        // lands, even when that list is empty.
        const optionsKey = `${collectionsResolved ? 'r' : 'p'}\u0001${names.join('\u0000')}`;
        if (!force && optionsKey === renderedCollectionOptionsKey) {
            select.value = currentFilters.collection || '';
            return;
        }
        renderedCollectionOptionsKey = optionsKey;

        select.innerHTML = [
            `<option value="">${escapeHtml(uiText('deckBuilder.filters.allCollections', 'All Collections'))}</option>`,
            `<option value="Stellar">${escapeHtml(uiText('deckBuilder.filters.stellar', 'Stellar'))}</option>`,
            ...names.map((name) => {
                const entry = COLLECTION_FILTER_LABELS[name];
                const label = entry ? uiText(entry[0], entry[1]) : name;
                return `<option value="${escapeHtml(name)}">${escapeHtml(label)}</option>`;
            }),
            // Present, never selectable: a readiness state may report itself,
            // it may not withhold the control it is reporting on.
            collectionsResolved
                ? ''
                : `<option value="" disabled data-collections-pending="true">${escapeHtml(uiText('deckBuilder.filters.collectionsLoading', 'Loading…'))}</option>`,
        ].join('');
        if (collectionsResolved) select.removeAttribute('aria-busy');
        else select.setAttribute('aria-busy', 'true');

        const selected = currentFilters.collection || '';
        select.value = currentFilters.collection || '';
        // Only a RESOLVED list can say a collection is gone. An unresolved one
        // is empty for want of an answer, not because the account holds
        // nothing — clearing on that would silently drop the player's active
        // filter every time a page render starts.
        if (collectionsResolved && selected && selected !== 'Stellar' && !names.includes(selected)) {
            // The active filter's collection just left this account's holdings.
            // Clear it through the control's own change path so the grid and the
            // dropdown cannot disagree about what is being filtered.
            select.value = '';
            select.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    /**
     * HOLD THE CATALOG'S PAGE LABEL AT THE WIDEST IT HAS BEEN (the Collection
     * pager's twin, collection.js `reservePageInfoWidth`). Prev and Next are
     * `flex: 1` beside it, so a count that shrank with a search ("Page 3 of 33"
     * → "Page 1 of 1") narrowed the label and moved and resized both buttons
     * in the frame the results landed (snappy round 15,
     * `deck-builder-fast-search`: 5 morphs in every cell). The widest label of
     * a count is its last page's (tabular figures, deck-builder.css).
     */
    function reserveCatalogPageLabel(pageInfo, totalPages, totalsUnknown = false) {
        const key = `${totalPages}|${totalsUnknown ? 'open' : 'counted'}|${i18n?.getLanguage?.() || 'en'}`;
        if (!pageInfo?.parentElement || pageInfo.dataset.reservedFor === key) return;
        const probe = document.createElement('div');
        probe.className = pageInfo.className;
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText = 'position:absolute;left:-9999px;top:0;visibility:hidden;'
            + 'pointer-events:none;white-space:nowrap;min-width:0;width:auto;';
        probe.textContent = pageLabelFor(uiText, { page: totalPages, totalPages, totalsUnknown });
        pageInfo.parentElement.appendChild(probe);
        const width = Math.ceil(Number(probe.getBoundingClientRect?.().width) || 0);
        probe.remove();
        if (width <= 0) return;
        pageInfo.dataset.reservedFor = key;
        const held = Number.parseFloat(pageInfo.style.minWidth) || 0;
        if (width > held) pageInfo.style.minWidth = `${width}px`;
    }

    function updateCollectionPagination(pagination) {
        lastCollectionPagination = {
            page: Number(pagination?.page) || 1,
            totalPages: Number(pagination?.totalPages) || 1,
            // A very large collection's pages are not counted (collection-page-answer.js).
            totalsUnknown: pagination?.totalsUnknown === true,
        };
        const prevBtn = document.getElementById('deck-builder-prev');
        const nextBtn = document.getElementById('deck-builder-next');
        const pageInfo = document.getElementById('deck-builder-page-info');
        if (prevBtn) prevBtn.disabled = lastCollectionPagination.page <= 1;
        if (nextBtn) nextBtn.disabled = lastCollectionPagination.page >= lastCollectionPagination.totalPages;
        if (pageInfo) {
            reserveCatalogPageLabel(pageInfo, lastCollectionPagination.totalPages, lastCollectionPagination.totalsUnknown);
            pageInfo.textContent = pageLabelFor(uiText, lastCollectionPagination);
        }
    }

    function updateDeckPagination(pagination) {
        lastDeckPagination = {
            page: Number(pagination?.page) || 1,
            totalPages: Number(pagination?.totalPages) || 1,
        };
        const wrap = document.getElementById('deck-current-pagination');
        const prevBtn = document.getElementById('deck-current-prev');
        const nextBtn = document.getElementById('deck-current-next');
        const pageInfo = document.getElementById('deck-current-page-info');
        if (wrap) wrap.hidden = lastDeckPagination.totalPages <= 1;
        if (prevBtn) prevBtn.disabled = lastDeckPagination.page <= 1;
        if (nextBtn) nextBtn.disabled = lastDeckPagination.page >= lastDeckPagination.totalPages;
        if (pageInfo) {
            pageInfo.textContent = uiText('common.pageOf', `Page ${lastDeckPagination.page} of ${lastDeckPagination.totalPages}`, {
                page: lastDeckPagination.page,
                total: lastDeckPagination.totalPages,
            });
        }
    }

    function isTightDeckBuilderViewport() {
        return Boolean(window.matchMedia?.(
            '(orientation: portrait) and (max-width: 768px), (orientation: landscape) and (max-height: 560px)',
        )?.matches);
    }

    function isLowHeightTabletDeckBuilderViewport() {
        return Boolean(window.matchMedia?.(
            '(orientation: landscape) and (min-width: 960px) and (min-height: 561px) and (max-height: 700px) and (max-width: 1366px) and (max-aspect-ratio: 8/5)',
        )?.matches);
    }

    /* Where "Your Decks" is a row under the summoners (deck-builder.css, the
       landscape tablet tier keeps it a side column) and a second row fits. */
    function isTallDesktopDeckBuilderViewport() {
        return Boolean(window.matchMedia?.(
            '(orientation: landscape) and (min-height: 900px) and (min-aspect-ratio: 1701/1000), '
            + '(orientation: landscape) and (min-height: 900px) and (min-width: 1561px)',
        )?.matches);
    }

    function isPortraitDeckBuilderViewport() {
        return Boolean(window.matchMedia?.(
            '(orientation: portrait) and (max-width: 768px)',
        )?.matches);
    }

    function setDeckBuilderFiltersOpen(open, { returnFocus = false } = {}) {
        const popover = document.getElementById('deck-builder-filter-popover');
        const toggle = document.getElementById('deck-builder-mobile-filters-toggle');
        if (!popover || !toggle) return;
        const expanded = Boolean(open) && isTightDeckBuilderViewport();
        popover.classList.toggle('is-open', expanded);
        toggle.setAttribute('aria-expanded', String(expanded));
        if (!expanded && returnFocus) toggle.focus();
    }

    function setDeckBuilderMobilePane(pane) {
        const content = document.getElementById('deck-builder-main');
        const catalogBtn = document.getElementById('deck-builder-view-cards');
        const deckBtn = document.getElementById('deck-builder-view-current');
        if (!content || !catalogBtn || !deckBtn) return;
        const nextPane = pane === 'deck' ? 'deck' : 'catalog';
        content.dataset.mobilePane = nextPane;
        catalogBtn.setAttribute('aria-pressed', String(nextPane === 'catalog'));
        deckBtn.setAttribute('aria-pressed', String(nextPane === 'deck'));
    }

    function syncResponsiveWorkspace() {
        reserveCollectionFilterWidth();
        if (!isTightDeckBuilderViewport()) setDeckBuilderFiltersOpen(false);
        if (!isPortraitDeckBuilderViewport()) setDeckBuilderMobilePane('catalog');
    }

    function updateCharacterPagination() {
        const prevBtn = document.getElementById('db-character-prev');
        const nextBtn = document.getElementById('db-character-next');
        const pageInfo = document.getElementById('db-character-page-info');
        const paginationWrap = document.getElementById('db-character-pagination');

        if (paginationWrap) {
            paginationWrap.style.display = charactersData.length > 0 ? 'flex' : 'none';
        }
        if (prevBtn) prevBtn.disabled = characterPage <= 1;
        if (nextBtn) nextBtn.disabled = characterPageIsDone;
        if (pageInfo) {
            pageInfo.textContent = uiText('common.page', `Page ${characterPage}`, {
                page: characterPage,
            });
        }
    }

    function updateSavedDeckPagination(pagination) {
        lastSavedDeckPagination = {
            page: Number(pagination?.page) || 1,
            totalPages: Number(pagination?.totalPages) || 1,
        };
        const prevBtn = document.getElementById('saved-deck-prev');
        const nextBtn = document.getElementById('saved-deck-next');
        const pageInfo = document.getElementById('saved-deck-page-info');
        const paginationWrap = document.getElementById('saved-deck-pagination');

        if (paginationWrap) {
            paginationWrap.hidden = lastSavedDeckPagination.totalPages <= 1;
        }
        if (prevBtn) prevBtn.disabled = lastSavedDeckPagination.page <= 1;
        if (nextBtn) nextBtn.disabled = lastSavedDeckPagination.page >= lastSavedDeckPagination.totalPages;
        if (pageInfo) {
            pageInfo.textContent = uiText('common.pageOf', `Page ${lastSavedDeckPagination.page} of ${lastSavedDeckPagination.totalPages}`, {
                page: lastSavedDeckPagination.page,
                total: lastSavedDeckPagination.totalPages,
            });
        }
    }

    function clearSavedDeckState() {
        lastSavedDeckRenderRequestId += 1;
        savedDecksData = [];
        savedDeckPage = 1;
        currentSavedDeckPageSize = savedDeckPageSize();
        updateSavedDeckPagination({ page: 1, totalPages: 1 });
    }

    function upsertCharacterCache(list) {
        list.forEach((source) => {
            if (!source || typeof source !== 'object') return;
            const normalized = {
                id: source.deckKey || (source.name ? source.name.split(' ')[0] : source.characterId),
                name: source.name,
                description: source.title || source.bio,
                picture: source.picture,
                imageUrl: sanitizeImageUrlForDirectClient(source.imageUrl),
                theme: source.name ? source.name.split(' ')[0].toLowerCase() : 'moon',
                characterId: source.characterId || null,
            };
            if (!normalized.id) return;
            characterById.set(normalized.id, normalized);
            if (normalized.characterId) {
                characterById.set(normalized.characterId, normalized);
            }
            characterPortraitByKey.set(normalized.id, normalized.imageUrl || null);
            if (normalized.characterId) {
                characterPortraitByKey.set(normalized.characterId, normalized.imageUrl || null);
            }
        });
    }

    async function fetchCharacterDetails(characterIds) {
        const uniqueIds = Array.from(new Set((characterIds || []).map((id) => String(id || '').trim()).filter(Boolean)));
        if (uniqueIds.length === 0) return [];
        const headers = await buildAuthHeaders();
        const language = i18n?.getLanguage?.() || 'en';
        const list = [];

        // The details endpoint intentionally caps each request. Saved decks are
        // not capped to the same count, so hydrate every bounded batch instead
        // of silently losing identities after the first server page.
        for (let offset = 0; offset < uniqueIds.length; offset += CHARACTER_DETAILS_BATCH_SIZE) {
            const batchIds = uniqueIds.slice(offset, offset + CHARACTER_DETAILS_BATCH_SIZE);
            const params = new URLSearchParams({ characterIds: batchIds.join(',') });
            params.set('lang', language);
            const response = await fetch(`/api/characters/details?${params.toString()}`, { headers });
            if (!response.ok) {
                throw new Error('Failed to fetch character details');
            }
            const data = await response.json();
            const batch = Array.isArray(data) ? data : [];
            list.push(...batch);
        }

        if ((i18n?.getLanguage?.() || 'en') !== language) return [];
        upsertCharacterCache(list);
        return list;
    }

    async function ensureDeckCharacterPortraits(decks, { refreshDetails = false } = {}) {
        if (!Array.isArray(decks) || decks.length === 0) return;
        const missingIds = Array.from(new Set(
            decks
                .map((d) => d?.character)
                .filter((id) => (
                    typeof id === 'string'
                    && id
                    && (refreshDetails || (
                        !characterById.has(id)
                        || !sanitizeImageUrlForDirectClient(characterPortraitByKey.get(id))
                    ))
                ))
        ));
        if (missingIds.length === 0) return;
        try {
            await fetchCharacterDetails(missingIds);
        } catch (err) {
            console.warn('[DeckBuilder] Failed to load deck character details:', err);
        }
    }

    async function ensureCharacterLoaded(characterId) {
        if (!characterId) return null;
        const cached = characterById.get(characterId);
        if (cached) return cached;
        const list = await fetchCharacterDetails([characterId]);
        return list[0] || characterById.get(characterId) || null;
    }

    function setSelectedCharacterDisplay(characterId) {
        const charDisplay = document.getElementById('selected-character-display');
        if (!charDisplay) return;
        const charData = characterById.get(characterId) || charactersData.find((char) => char.id === characterId || char.characterId === characterId);
        charDisplay.textContent = charData?.name || characterId || '';
    }

    function invalidateLocalizedCharacterCache() {
        // Portrait URLs are language-neutral and remain available for immediate
        // paint, but names/titles are not. Clearing only the localized records
        // makes the next open hydrate every off-page saved-deck summoner in the
        // newly selected language instead of trusting an old cached identity.
        // The chooser keeps the page it shows until the reread replaces its
        // words in place: emptying it here hid the pager for a round trip.
        characterById.clear();
        chooserView = null;
        chooserPageRead = null;
        rootView = null;
    }

    /** One page of the summoner roster, in the player's language: `{ language, data }`. */
    async function requestCharactersPage(cursor) {
        const headers = await buildAuthHeaders();
        const params = new URLSearchParams({ limit: String(CHARACTER_PAGE_SIZE) });
        const language = i18n?.getLanguage?.() || 'en';
        params.set('lang', language);
        if (cursor) params.set('cursor', cursor);
        const response = await fetch(`/api/deck-builder/characters-page?${params.toString()}`, { headers });
        if (!response.ok) throw new Error('Failed to fetch characters');
        return { language, data: await response.json() };
    }

    async function loadCharactersPage(targetPage = 1, { force = false } = {}) {
        const normalizedPage = Math.max(1, Number(targetPage) || 1);
        if (force) {
            // A forced read starts again from the first cursor, but what is on
            // screen stays until the answer arrives.
            characterPageCursors = new Map([[1, null]]);
        }
        const cursor = characterPageCursors.has(normalizedPage)
            ? characterPageCursors.get(normalizedPage)
            : undefined;
        if (normalizedPage > 1 && cursor === undefined) {
            return false;
        }

        try {
            const { language, data } = await requestCharactersPage(cursor);
            if ((i18n?.getLanguage?.() || 'en') !== language) return false;
            const list = Array.isArray(data?.items) ? data.items : [];
            upsertCharacterCache(list);
            charactersData = list
                .map((char) => characterById.get(char.deckKey || char.characterId))
                .filter(Boolean);
            characterPage = normalizedPage;
            characterPageNextCursor = typeof data?.nextCursor === 'string' ? data.nextCursor : null;
            characterPageIsDone = Boolean(data?.isDone);
            if (characterPageNextCursor) {
                characterPageCursors.set(normalizedPage + 1, characterPageNextCursor);
            } else {
                characterPageCursors.delete(normalizedPage + 1);
            }
            return true;
        } catch (error) {
            // A navigation cancels the read: the page is leaving, not failing.
            if (!isPageLeaving()) console.error('[DeckBuilder] Failed to load characters:', error);
            charactersData = [];
            characterPageNextCursor = null;
            characterPageIsDone = true;
            return false;
        }
    }

    /**
     * Initialize the deck builder
     */
    function init() {
        createDeckBuilderModal();
        setupEventListeners();
        setupI18nListeners();
        setupIntentWarm();
        setupPrebuildYield();
        applyDeckBuilderTranslations();
        reserveCollectionFilterWidth();
    }

    /* Intent on the Collection's Decks tab — a pointer over it, keyboard focus,
       the first touch — warms the whole root view (the saved decks included),
       memoised, so the press that follows presents it as it stands. This is
       the only warm that reads the deck list. Delegated: the tab is the
       Collection's markup. */
    function setupIntentWarm() {
        if (intentWarmListenersReady) return;
        intentWarmListenersReady = true;
        const warmOnIntent = (event) => {
            if (event.target?.closest?.('#collection-tab-deckbuilder')) void warm({ intent: true });
        };
        document.addEventListener('pointerover', warmOnIntent, { passive: true });
        document.addEventListener('focusin', warmOnIntent);
        document.addEventListener('touchstart', warmOnIntent, { passive: true });
    }

    function setupI18nListeners() {
        if (i18nListenersReady) return;
        i18nListenersReady = true;
        window.addEventListener('moonfall:languagechange', () => {
            const refreshId = ++lastLanguageRefreshId;
            invalidateLocalizedCharacterCache();
            applyDeckBuilderTranslations();
            syncReadOnlyDeckPresentation();
            updateCollectionFilterOptions({ force: true });
            reserveCollectionFilterWidth();
            updateCharacterPagination();
            updateCollectionPagination(lastCollectionPagination);
            updateDeckPagination(lastDeckPagination);
            updateSavedDeckPagination(lastSavedDeckPagination);
            if (!isOpen) return;
            void (async () => {
                await loadCharactersPage(1, { force: true });
                if (refreshId !== lastLanguageRefreshId) return;
                renderCharacterSelectionGrid();
                await loadSavedDecks({ refreshCharacterDetails: true });
                if (refreshId !== lastLanguageRefreshId) return;
                if (currentDeck.character) {
                    await fetchCharacterDetails([currentDeck.character]);
                    if (refreshId !== lastLanguageRefreshId) return;
                    setSelectedCharacterDisplay(currentDeck.character);
                    await renderCollection(undefined, { refresh: true });
                    if (refreshId !== lastLanguageRefreshId) return;
                    renderDeck();
                    updateSaveButton();
                } else {
                    renderDeck();
                }
            })();
        });
    }

    /**
     * Create the deck builder modal HTML
     */
    function createDeckBuilderModal() {
        const modal = document.createElement('div');
        modal.id = 'deck-builder-modal';
        modal.className = 'deck-builder-modal';
        modal.innerHTML = `
            <div class="deck-builder-container" id="deck-builder-container">
                <div class="deck-builder-header">
                    <h2 data-i18n="deckBuilder.title">Decks</h2>
                    <button type="button" class="deck-builder-close" id="deck-builder-close" aria-label="Close" data-i18n-aria-label="store.close">&times;</button>
                </div>

                <!-- Character Selection -->
                <div class="deck-builder-character-select" id="character-select-section">
                    <!-- Players did not know this row starts a new deck (the
                         owner, 27 October 2026), so it says so. -->
                    <div class="db-character-header">
                        <h3 data-i18n="deckBuilder.newDeckTitle">Create a Deck</h3>
                        <p class="db-character-hint" data-i18n="deckBuilder.newDeckHint">Choose a summoner to start building</p>
                    </div>
                     <div class="db-character-grid" id="db-character-grid">
                        <!-- Dynamic content loaded via JS -->
                    </div>
                    <div class="deck-builder-pagination db-character-pagination" id="db-character-pagination">
                        <button class="btn deck-btn" id="db-character-prev" data-i18n="common.prev" disabled>Prev</button>
                        <div class="deck-builder-page-info" id="db-character-page-info" data-i18n="common.pageOne" data-i18n-runtime="true">Page 1</div>
                        <button class="btn deck-btn" id="db-character-next" data-i18n="common.next" disabled>Next</button>
                    </div>
                </div>

                <div class="deck-builder-view-switcher" role="group" aria-label="Decks" data-i18n-aria-label="collection.tabs.deckBuilder">
                    <button type="button" class="deck-builder-view-btn" id="deck-builder-view-cards" aria-pressed="true" data-i18n="collection.tabs.cards">Cards</button>
                    <button type="button" class="deck-builder-view-btn" id="deck-builder-view-current" aria-pressed="false" data-i18n="deckBuilder.currentDeck">Current Deck</button>
                </div>

                <div class="deck-builder-content" id="deck-builder-main" data-mobile-pane="catalog" style="display: none;">
                    <!-- Left: Available Cards -->
                    <div class="deck-builder-collection">
                        <div class="deck-builder-section-header">
                            <div class="deck-builder-tabs">
                                <button class="deck-tab active" data-tab="characters" data-i18n="deckBuilder.characters">Characters</button>
                                <button class="deck-tab" data-tab="spells" data-i18n="deckBuilder.spells">Spells</button>
                            </div>
                            <div class="deck-builder-controls">
                                <label class="collection-visually-hidden" for="deck-builder-search" data-i18n="collection.filters.searchLabel">Search cards</label>
                                <input type="text" id="deck-builder-search" name="deck-builder-search" autocomplete="off" placeholder="Search cards..." class="deck-builder-search" data-i18n-placeholder="deckBuilder.searchPlaceholder" aria-label="Search cards" data-i18n-aria-label="collection.filters.searchLabel">
                                <button type="button" class="deck-builder-mobile-filters-toggle" id="deck-builder-mobile-filters-toggle" aria-expanded="false" aria-controls="deck-builder-filter-popover" data-i18n="collection.filters.mobileToggle">Filters</button>
                                <div class="deck-builder-filter-popover" id="deck-builder-filter-popover" role="group" aria-label="Card filters" data-i18n-aria-label="collection.filters.mobileGroup">
                                    <label class="collection-visually-hidden" for="filter-collection" data-i18n="collection.filters.collectionLabel">Collection</label>
                                    <select id="filter-collection" class="deck-filter" aria-label="Collection" data-i18n-aria-label="collection.filters.collectionLabel">
                                        <!-- Collections are filled in from the player's own
                                             holdings; only the two constants ship here. -->
                                        <option value="" data-i18n="deckBuilder.filters.allCollections">All Collections</option>
                                        <option value="Stellar" data-i18n="deckBuilder.filters.stellar">Stellar</option>
                                    </select>
                                    <label class="collection-visually-hidden" for="filter-mana" data-i18n="collection.filters.manaLabel">Mana cost</label>
                                    <select id="filter-mana" class="deck-filter" aria-label="Mana cost" data-i18n-aria-label="collection.filters.manaLabel">
                                        <option value="" data-i18n="deckBuilder.filters.manaAll">Mana: All</option>
                                        <option value="0">0</option>
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="6">6</option>
                                        <option value="7+">7+</option>
                                    </select>
                                    <label class="collection-visually-hidden" for="sort-cards" data-i18n="collection.filters.sortLabel">Sort cards</label>
                                    <select id="sort-cards" class="deck-filter" aria-label="Sort cards" data-i18n-aria-label="collection.filters.sortLabel">
                                        <option value="name" data-i18n="deckBuilder.sort.nameAsc">Name (A-Z)</option>
                                        <option value="cost-asc" data-i18n="deckBuilder.sort.manaAsc" selected>Mana ↑</option>
                                        <option value="cost-desc" data-i18n="deckBuilder.sort.manaDesc">Mana ↓</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div class="deck-builder-cards" id="deck-builder-collection-cards">
                            <!-- Cards will be populated here -->
                        </div>
                        <div class="deck-builder-pagination" id="deck-builder-pagination">
                            <button class="btn deck-btn" id="deck-builder-prev" data-i18n="common.prev" disabled>Prev</button>
                            <div class="deck-builder-page-info" id="deck-builder-page-info" data-i18n="common.pageOne" data-i18n-runtime="true">Page 1</div>
                            <button class="btn deck-btn" id="deck-builder-next" data-i18n="common.next" disabled>Next</button>
                        </div>
                    </div>

                    <!-- Right: Current Deck -->
                    <div class="deck-builder-deck">
                        <div class="deck-builder-section-header">
                            <!-- A starter deck opens in this same editor, locked: the
                                 heading says which one it is (syncReadOnlyDeckPresentation). -->
                            <h3 class="deck-builder-deck-heading">
                                <span class="deck-heading-current" data-i18n="deckBuilder.currentDeck">Current Deck</span>
                                <span class="deck-heading-starter" data-i18n="deckBuilder.starterDeckHeading">Starter Deck</span>
                            </h3>
                            <input type="text" id="deck-name-input" name="deck-name" autocomplete="off" placeholder="Deck Name" class="deck-name-input" maxlength="50" data-i18n-placeholder="deckBuilder.deckNamePlaceholder" aria-label="Deck Name" data-i18n-aria-label="deckBuilder.deckNamePlaceholder">
                        </div>
                        <div class="deck-builder-deck-info">
                            <span id="selected-character-display"></span>
                            <span class="deck-card-count-summary"><span id="deck-card-count">0</span> / ${MAX_DECK_SIZE}</span>
                            <span class="deck-status" id="deck-status" role="status" aria-live="polite"></span>
                        </div>
                        <div class="deck-builder-deck-cards" id="deck-builder-deck-cards">
                            <!-- Selected cards will be shown here -->
                        </div>
                        <div class="deck-builder-pagination deck-current-pagination" id="deck-current-pagination" hidden>
                            <button class="btn deck-btn" id="deck-current-prev" data-i18n="common.prev" disabled>Prev</button>
                            <div class="deck-builder-page-info" id="deck-current-page-info" data-i18n="common.pageOne" data-i18n-runtime="true">Page 1</div>
                            <button class="btn deck-btn" id="deck-current-next" data-i18n="common.next" disabled>Next</button>
                        </div>
                        <div class="deck-builder-actions">
                            <button id="deck-save-btn" class="deck-btn deck-btn-primary" data-i18n="deckBuilder.saveDeck" disabled>Save Deck</button>
                            <button id="deck-clear-btn" class="deck-btn deck-btn-secondary" data-i18n="deckBuilder.cancel">Cancel</button>
                        </div>
                    </div>
                </div>

                <!-- Saved Decks List -->
                <div class="deck-builder-saved">
                    <div class="deck-builder-saved-header">
                        <h3 data-i18n="deckBuilder.yourDecks">Your Decks</h3>
                    </div>
                    <div class="deck-list" id="deck-list">
                        <!-- Saved decks will be listed here -->
                    </div>
                    <div class="deck-builder-pagination deck-saved-pagination" id="saved-deck-pagination" hidden>
                        <button class="btn deck-btn" id="saved-deck-prev" data-i18n="common.prev" disabled>Prev</button>
                        <div class="deck-builder-page-info" id="saved-deck-page-info" data-i18n="common.pageOne" data-i18n-runtime="true">Page 1</div>
                        <button class="btn deck-btn" id="saved-deck-next" data-i18n="common.next" disabled>Next</button>
                    </div>
                </div>

            </div>
        `;
        document.body.appendChild(modal);
        applyDeckBuilderTranslations();
    }

    /**
     * Setup event listeners
     */
    function setupEventListeners() {
        // Close button
        document.getElementById('deck-builder-close')?.addEventListener('click', () => close());

        // The hover intent sees every pointer move, wherever it lands.
        document.addEventListener('pointermove', hoverIntent.observeMove, { capture: true, passive: true });

        // Click outside to close
        document.getElementById('deck-builder-modal')?.addEventListener('click', (e) => {
            if (e.target.id === 'deck-builder-modal') close();
        });

        // Character selection (Event Delegation)
        const characterGrid = document.getElementById('db-character-grid');
        characterGrid?.addEventListener('click', (e) => {
            const card = e.target.closest('.db-character-card');
            if (card) {
                const character = card.dataset.charId;
                selectCharacter(character);
            }
        });
        characterGrid?.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            const card = event.target.closest('.db-character-card');
            if (!card) return;
            event.preventDefault();
            selectCharacter(card.dataset.charId);
        });

        // Tab switching
        document.querySelectorAll('#deck-builder-container .deck-tab[data-tab]').forEach(tab => {
            tab.addEventListener('click', (e) => {
                const tabName = tab.dataset.tab;
                switchTab(tabName);
            });
        });

        // Search
        document.getElementById('deck-builder-search')?.addEventListener('input', (e) => {
            markSearchBusy();
            filterCards(e.target.value);
        });

        document.getElementById('deck-builder-mobile-filters-toggle')?.addEventListener('click', () => {
            const popover = document.getElementById('deck-builder-filter-popover');
            setDeckBuilderFiltersOpen(!popover?.classList.contains('is-open'));
        });

        document.getElementById('deck-builder-view-cards')?.addEventListener('click', () => {
            setDeckBuilderMobilePane('catalog');
            syncCollectionPageSize({ preserveOffset: true });
            void renderCollection(undefined, { resetScroll: true });
        });
        document.getElementById('deck-builder-view-current')?.addEventListener('click', () => {
            setDeckBuilderMobilePane('deck');
            syncDeckPageSize({ preserveOffset: true });
            renderDeck();
        });

        const deckBuilderContainer = document.getElementById('deck-builder-container');
        deckBuilderContainer?.addEventListener('pointerdown', (event) => {
            const popover = document.getElementById('deck-builder-filter-popover');
            const toggle = document.getElementById('deck-builder-mobile-filters-toggle');
            if (!popover?.classList.contains('is-open')) return;
            if (popover.contains(event.target) || toggle?.contains(event.target)) return;
            setDeckBuilderFiltersOpen(false);
        });
        deckBuilderContainer?.addEventListener('keydown', (event) => {
            const popover = document.getElementById('deck-builder-filter-popover');
            if (event.key !== 'Escape' || !popover?.classList.contains('is-open')) return;
            event.stopPropagation();
            setDeckBuilderFiltersOpen(false, { returnFocus: true });
        });

        // Deck name input
        document.getElementById('deck-name-input')?.addEventListener('input', updateSaveButton);

        // Save button
        const saveBtn = document.getElementById('deck-save-btn');
        if (saveBtn) {
            saveBtn.addEventListener('click', () => {
                const guard = MoonfallClickGuard;
                const runner = () => {
                    if (MoonfallCloudGuard && typeof MoonfallCloudGuard.run === 'function') {
                        return MoonfallCloudGuard.run('decks.save', () => Promise.resolve(saveDeck()));
                    }
                    return Promise.resolve(saveDeck());
                };
                if (guard && typeof guard.guard === 'function') {
                    guard.guard(saveBtn, runner).catch((err) => console.error('[DeckBuilder] save guard failed:', err));
                } else {
                    runner().catch((err) => console.error('[DeckBuilder] save failed:', err));
                }
            });
        }

        // Clear button
        document.getElementById('deck-clear-btn')?.addEventListener('click', () => clearDeck());

        // Filters and Sort
        document.getElementById('filter-collection')?.addEventListener('change', (e) => {
            currentFilters.collection = e.target.value;
            collectionPage = 1;
            renderCollection(undefined, { resetScroll: true });
        });
        document.getElementById('filter-mana')?.addEventListener('change', (e) => {
            currentFilters.mana = e.target.value;
            collectionPage = 1;
            renderCollection(undefined, { resetScroll: true });
        });
        document.getElementById('sort-cards')?.addEventListener('change', (e) => {
            currentSort = e.target.value;
            collectionPage = 1;
            renderCollection(undefined, { resetScroll: true });
        });

        // A page turn keeps the page on screen until the next one is whole;
        // the button answers the press meanwhile.
        /* A press while a page is on its way asks for the page after (or
           before) the one asked (fast hands) — never past the last page, and
           never the page already asked for again (review of wave D, 28
           September 2026): Next pressed past the last page asked the server
           for a page that does not exist, dropping the answer already on its
           way, and a Prev after it counted from that phantom page; a second
           Prev on its way to page 1 started the same read over. The
           Collection's pager has the same rule (collection.js turnPage). */
        const turnCatalogPage = (button, delta) => {
            const last = Math.max(1, Number(lastCollectionPagination.totalPages) || 1);
            const asked = Math.min(Math.max(1, collectionPage + delta), Math.max(last, collectionPage));
            if (asked === collectionPage) return;
            collectionPage = asked;
            // Back to the page on screen: the turn ends here.
            if (endCatalogTurnOnShownPage()) return;
            acknowledgeCatalogPress(button);
            renderCollection(undefined, { resetScroll: true });
        };
        document.getElementById('deck-builder-prev')?.addEventListener('click', (event) => {
            turnCatalogPage(event.currentTarget, -1);
        });

        document.getElementById('deck-builder-next')?.addEventListener('click', (event) => {
            turnCatalogPage(event.currentTarget, 1);
        });

        document.getElementById('deck-current-prev')?.addEventListener('click', () => {
            deckPage = Math.max(1, deckPage - 1);
            renderDeck();
        });

        document.getElementById('deck-current-next')?.addEventListener('click', () => {
            deckPage += 1;
            renderDeck();
        });

        // The next page is read and its portraits settle before it replaces the
        // current one, so the press is acknowledged on the button (the shared
        // click guard) rather than by a page that empties first.
        const pageCharacters = (button, targetPage) => {
            const runner = async () => {
                const loaded = await loadCharactersPage(targetPage);
                if (!loaded) return;
                await renderCharacterSelectionGrid().committed;
            };
            const guard = MoonfallClickGuard;
            const running = guard && typeof guard.guard === 'function'
                ? guard.guard(button, runner)
                : runner();
            // The guard re-enables its button when it settles; the pager owns
            // the real disabled state (first page, last page).
            void Promise.resolve(running)
                .catch((err) => console.warn('[DeckBuilder] character paging failed:', err))
                .finally(updateCharacterPagination);
        };

        const turnCharacters = (button, direction) => {
            if (direction === 'prev') {
                if (characterPage <= 1) return;
                pageCharacters(button, characterPage - 1);
                return;
            }
            if (characterPageIsDone) return;
            pageCharacters(button, characterPage + 1);
        };
        document.getElementById('db-character-prev')?.addEventListener('click', (event) => {
            turnCharacters(event.currentTarget, 'prev');
        });

        document.getElementById('db-character-next')?.addEventListener('click', (event) => {
            turnCharacters(event.currentTarget, 'next');
        });

        /* A finger swipes the catalog's pages, and the summoners', on a touch
           screen (screen-fit round 1, 28 October 2026): the turn the button on
           that side makes, its acknowledgement and guards included. It is
           called, not clicked: a press that starts on a card arms
           TouchPreview's click swallow for 700 ms after the lift, and a
           synthesized click inside that window was eaten (Chrome, real touch). */
        const swipeLikeButtons = (surface, buttonFor, turn) => {
            if (!surface) return;
            SwipePager.attach(surface, {
                follow: surface,
                canSwipe: (direction) => {
                    const button = buttonFor(direction);
                    return Boolean(button) && !button.disabled;
                },
                onSwipe: (direction) => {
                    const button = buttonFor(direction);
                    if (button && !button.disabled) turn(button, direction);
                },
            });
        };
        swipeLikeButtons(
            document.getElementById('deck-builder-collection-cards'),
            (direction) => document.getElementById(direction === 'next' ? 'deck-builder-next' : 'deck-builder-prev'),
            (button, direction) => turnCatalogPage(button, direction === 'next' ? 1 : -1),
        );
        swipeLikeButtons(
            document.getElementById('db-character-grid'),
            (direction) => document.getElementById(direction === 'next' ? 'db-character-next' : 'db-character-prev'),
            turnCharacters,
        );

        document.getElementById('saved-deck-prev')?.addEventListener('click', () => {
            savedDeckPage = Math.max(1, savedDeckPage - 1);
            renderDeckList(savedDecksData);
        });

        document.getElementById('saved-deck-next')?.addEventListener('click', () => {
            savedDeckPage += 1;
            renderDeckList(savedDecksData);
        });

        window.addEventListener('moonfall:viewport-change', () => {
            if (!isOpen) return;
            syncResponsiveWorkspace();
            if (syncSavedDeckPageSize({ preserveOffset: true })) {
                renderDeckList(savedDecksData);
            }
            if (syncDeckPageSize({ preserveOffset: true })) {
                renderDeck();
            }
            if (!currentDeck.character) return;
            const main = document.getElementById('deck-builder-main');
            if (!main || main.getClientRects().length === 0) return;
            refitCatalogPage();
            // The custom viewport event can arrive before the compact grid has
            // inherited its final post-resize height. Measure again after two
            // paint frames even when the page capacity stayed unchanged; the
            // fitted CSS scale still needs the settled geometry.
            scheduleSettledCollectionMeasurement();
        });

        setupDeckDropTarget();

        // Cardback Manager
    }

    function setupDeckDropTarget() {
        const deckList = document.getElementById('deck-builder-deck-cards');
        if (!deckList) return;

        /* THE DROP TARGET IS THE BOX THE PLAYER AIMS AT.
           `.deck-builder-deck` is the painted panel — the rounded, dark-backed
           box with 16px of padding plus its own header and footer rows.
           `#deck-builder-deck-cards` is only the grid inside it, and binding
           the listeners there made every release in the surrounding band land
           on the panel the player was aiming at and do nothing. Reported
           against the phone layout, where the aim is a fingertip, but the gap
           is the panel's padding on every size. */
        const target = deckList.closest('.deck-builder-deck') || deckList;

        if (target._dragoverHandler) {
            target.removeEventListener('dragover', target._dragoverHandler);
        }
        if (target._dragleaveHandler) {
            target.removeEventListener('dragleave', target._dragleaveHandler);
        }
        if (target._dropHandler) {
            target.removeEventListener('drop', target._dropHandler);
        }

        target._dragoverHandler = (event) => {
            if (isReadOnlyDeckMode()) return;
            const payload = event.dataTransfer?.getData?.('text/plain') || draggedDeckCardId;
            if (!payload) return;
            event.preventDefault();
            target.classList.add('drag-over');
            if (event.dataTransfer) {
                event.dataTransfer.dropEffect = 'copy';
            }
        };

        target._dragleaveHandler = (event) => {
            /* Crossing onto a child still fires `dragleave` on the panel, so a
               naive handler drops the highlight while the pointer is still
               inside the very box that accepts. */
            if (event?.relatedTarget && target.contains(event.relatedTarget)) return;
            target.classList.remove('drag-over');
        };

        target._dropHandler = (event) => {
            if (isReadOnlyDeckMode()) return;
            event.preventDefault();
            event.stopPropagation();
            target.classList.remove('drag-over');
            const payload = event.dataTransfer?.getData?.('text/plain') || draggedDeckCardId;
            if (!payload) return;
            addCardToDeck(payload);
            draggedDeckCardId = null;
        };

        target.addEventListener('dragover', target._dragoverHandler);
        target.addEventListener('dragleave', target._dragleaveHandler);
        target.addEventListener('drop', target._dropHandler);
    }

    /**
     * Helper to toggle visibility of main/decks/cardbacks sections
     * @param {boolean} showRoot - If true, shows decks/cardbacks. If false, hides them.
     */
    function toggleDeckSections(showRoot) {
        const container = document.getElementById('deck-builder-container');
        if (!container) return;
        container.classList.toggle('deck-builder-root-mode', showRoot);
        const savedSection = container.querySelector('.deck-builder-saved');
        if (savedSection) savedSection.style.display = showRoot ? '' : 'none';
        const cardbacksSection = container.querySelector('.deck-builder-cardbacks');
        if (cardbacksSection) cardbacksSection.style.display = showRoot ? '' : 'none';
    }

    /**
     * Select a character
     */
    function selectCharacter(characterId) {
        currentDeck.character = characterId;
        collectionPage = 1;
        deckPage = 1;
        // A new session: nothing of the last one's catalog is kept.
        resetCatalog();

        // Hide character selection, show main deck builder. Clear the inline
        // display (rather than hardcoding 'grid') so the STYLESHEET owns the
        // layout: a 2-column grid on desktop, a stacked block on a phone
        // (mobile.css). An inline 'grid' would beat every mobile rule.
        document.getElementById('character-select-section').style.display = 'none';
        document.getElementById('deck-builder-main').style.display = '';

        // Hide other root sections
        toggleDeckSections(false);

        // Update character display
        setSelectedCharacterDisplay(characterId);
        ensureCharacterLoaded(characterId)
            .then(() => {
                if (currentDeck.character === characterId) {
                    setSelectedCharacterDisplay(characterId);
                }
            })
            .catch((error) => {
                console.warn('[DeckBuilder] Failed to load selected character details:', error);
            });

        const renderPromise = renderCollection();
        if (isReadOnlyDeckMode()) setDeckBuilderMobilePane('deck');
        updateSaveButton();
        return renderPromise;
    }

    /**
     * Switch between Characters and Spells tabs
     */
    function switchTab(tabName) {
        const isTypeChange = activeTab !== tabName;
        activeTab = tabName;
        collectionPage = 1;

        if (isTypeChange) {
            currentFilters.collection = '';
            currentFilters.mana = '';
            const collectionFilter = document.getElementById('filter-collection');
            const manaFilter = document.getElementById('filter-mana');
            if (collectionFilter) collectionFilter.value = '';
            if (manaFilter) manaFilter.value = '';
        }

        // Update tab buttons
        document.querySelectorAll('#deck-builder-container .deck-tab[data-tab]').forEach(tab => {
            tab.classList.toggle('active', tab.dataset.tab === tabName);
        });

        return renderCollection();
    }

    function normalizeOpenArgs(preSelectedCharacterOrOptions) {
        if (
            preSelectedCharacterOrOptions &&
            typeof preSelectedCharacterOrOptions === 'object' &&
            !Array.isArray(preSelectedCharacterOrOptions)
        ) {
            return {
                preSelectedCharacter: preSelectedCharacterOrOptions.preSelectedCharacter || null,
                options: preSelectedCharacterOrOptions,
            };
        }
        return {
            preSelectedCharacter: preSelectedCharacterOrOptions || null,
            options: {},
        };
    }

    function resolveEmbeddedHost(targetHost) {
        if (!targetHost) return null;
        if (typeof targetHost === 'string') {
            return document.querySelector(targetHost);
        }
        if (targetHost instanceof Element) {
            return targetHost;
        }
        return null;
    }

    function setPresentationMode(options = {}) {
        const modal = document.getElementById('deck-builder-modal');
        const container = document.getElementById('deck-builder-container');
        if (!modal || !container) return;

        const nextEmbeddedHost = resolveEmbeddedHost(options.embeddedHost);
        if (nextEmbeddedHost) {
            if (container.parentElement !== nextEmbeddedHost) {
                nextEmbeddedHost.appendChild(container);
            }
            modal.style.display = 'none';
            modal.classList.remove('deck-builder-from-collection');
            container.classList.add('deck-builder-embedded');
            isEmbeddedView = true;
            embeddedHost = nextEmbeddedHost;
            return;
        }

        if (container.parentElement !== modal) {
            modal.appendChild(container);
        }
        container.classList.remove('deck-builder-embedded');
        isEmbeddedView = false;
        embeddedHost = null;
        modal.style.display = 'flex';
        modal.classList.toggle('deck-builder-from-collection', options.source === 'collection');
    }

    function rootViewKey() {
        const language = i18n?.getLanguage?.() || 'en';
        const account = Firebase?.getUser?.()?.uid || '';
        return `${language}|${account}`;
    }

    /** The DOM holds the current account's first chooser page and its decks. */
    function rootViewIsCurrent() {
        return Boolean(rootView)
            && rootView.key === rootViewKey()
            && characterPage === 1
            && Boolean(document.getElementById('db-character-grid')?.childElementCount)
            && Boolean(document.getElementById('deck-list')?.childElementCount);
    }

    /** A root view read without error, and within ROOT_VIEW_FRESH_MS: adopted as it stands. */
    function rootViewIsFresh() {
        return rootViewIsCurrent()
            && rootView.complete === true
            && Date.now() - rootView.at <= ROOT_VIEW_FRESH_MS;
    }

    /* A press adopts only an answer read AHEAD of it (a warm on intent) and
       only once: an open that read its own view, or already adopted one, is
       re-read by the next open — a refresh that fails must still clear what
       it showed. */
    function rootViewIsAdoptable() {
        return rootViewIsFresh() && rootView.warmed === true;
    }

    /** The DOM holds the current account's first chooser page, read within the window. */
    function chooserViewIsFresh(key) {
        return Boolean(chooserView)
            && chooserView.key === key
            && chooserView.complete === true
            && characterPage === 1
            && Boolean(document.getElementById('db-character-grid')?.childElementCount)
            && Date.now() - chooserView.at <= ROOT_VIEW_FRESH_MS;
    }

    function markRootViewStale() {
        if (rootView) rootView.at = 0;
        if (chooserView) chooserView.at = 0;
        if (chooserPageRead) chooserPageRead.at = 0;
    }

    /* THE CHOOSER'S FIRST PAGE, READ WITHOUT ITS PICTURES (`warm({ pictures:
       false })`). The Collection reads it once its own page's reads have
       answered, and asks for its portraits only once that page's pictures
       have settled (`warm()`): data a page ahead is cheap, pictures wait
       their turn (the owner, 26 September 2026). The chooser is painted from
       a read kept here within ROOT_VIEW_FRESH_MS, never read again for it;
       the module's page state must still hold that read (a chooser paged
       since, or a language refresh, replaced it). */
    let chooserPageRead = null; // { key, at, complete, items }
    let chooserPageTask = null; // { key, promise }
    // Bumped when the Collection closes (dropWarm): a warm still reading paints nothing.
    let warmDropEpoch = 0;
    /* THE ROSTER'S PAGES AHEAD, AS DATA. The saved decks' summoners are
       mostly the roster's own, and one the deck builder does not know yet
       costs the tab a details read chained after the deck list: 480 ms after
       a 210 ms list in the round-15 probe (28 September 2026, cable seat,
       warm visit), with the press 380 ms after hover intent — the tab held
       with its loading state for summoners (Kitsu, Einar, Ilyra) its visible
       decks did not even use. So the data warm also reads up to
       CHOOSER_DATA_PAGES_AHEAD roster pages after the first (four summoners
       each; data pages are cheap, the owner, 26 September 2026) into the
       summoner cache only: no picture, nothing painted, the chooser's own
       page state untouched. The cursors it learns are the chooser pager's. */
    const CHOOSER_DATA_PAGES_AHEAD = 2;
    let chooserPagesAhead = null; // { read, promise }
    // The first-page read whose pages ahead are in the cache: read once per read.
    let chooserPagesAheadOf = null;

    function readChooserPagesAhead(read) {
        if (!read || chooserPagesAheadOf === read) return Promise.resolve();
        if (chooserPagesAhead && chooserPagesAhead.read === read) return chooserPagesAhead.promise;
        const ahead = { read, promise: null };
        ahead.promise = (async () => {
            for (let page = 2; page <= 1 + CHOOSER_DATA_PAGES_AHEAD; page += 1) {
                const cursor = characterPageCursors.get(page);
                if (typeof cursor !== 'string' || !cursor) break;
                const { language, data } = await requestCharactersPage(cursor);
                if (rootViewKey() !== read.key || (i18n?.getLanguage?.() || 'en') !== language) return;
                upsertCharacterCache(Array.isArray(data?.items) ? data.items : []);
                const next = typeof data?.nextCursor === 'string' ? data.nextCursor : null;
                // The pager's own read of an earlier page may have moved on meanwhile.
                if (next && !characterPageCursors.has(page + 1)) characterPageCursors.set(page + 1, next);
                if (!next) break;
            }
            chooserPagesAheadOf = read;
        })().catch(() => undefined).finally(() => {
            if (chooserPagesAhead === ahead) chooserPagesAhead = null;
        });
        chooserPagesAhead = ahead;
        return ahead.promise;
    }

    function chooserPageReadIsFresh(key) {
        return Boolean(chooserPageRead)
            && chooserPageRead.key === key
            && chooserPageRead.complete === true
            && characterPage === 1
            && charactersData === chooserPageRead.items
            && Date.now() - chooserPageRead.at <= ROOT_VIEW_FRESH_MS;
    }

    /** The chooser's first page, read (or the read in hand); null when the account or language changed meanwhile. */
    function readChooserPage() {
        const key = rootViewKey();
        if (chooserPageTask && chooserPageTask.key === key) return chooserPageTask.promise;
        if (chooserPageReadIsFresh(key)) return Promise.resolve(chooserPageRead);
        const task = { key, promise: null };
        task.promise = (async () => {
            const loaded = await loadCharactersPage(1, { force: true });
            if (rootViewKey() !== key) return null;
            // A failed or empty read is painted as it is and never reused.
            chooserPageRead = {
                key,
                at: Date.now(),
                complete: loaded === true && charactersData.length > 0,
                items: charactersData,
            };
            return chooserPageRead;
        })().finally(() => {
            if (chooserPageTask === task) chooserPageTask = null;
        });
        chooserPageTask = task;
        return task.promise;
    }

    /**
     * Read the chooser's first page — one summoner page — and render it, or
     * reuse one read within the window. Memoised like the root view. Resolves
     * with `chooserView`, or null when the account or language changed while
     * it was read, or the Collection closed meanwhile with the tab not open.
     */
    function prepareChooserView() {
        const key = rootViewKey();
        if (chooserViewTask && chooserViewTask.key === key) return chooserViewTask.promise;
        if (chooserViewIsFresh(key)) return Promise.resolve(chooserView);
        const task = { key, promise: null };
        const epoch = warmDropEpoch;
        task.promise = (async () => {
            const read = await readChooserPage();
            if (rootViewKey() !== key || !read) return null;
            if (!isOpen && epoch !== warmDropEpoch) return null;
            const chooser = renderCharacterSelectionGrid();
            // As old as its read: a view painted from a read in hand keeps that read's age.
            chooserView = { key, at: read.at, complete: read.complete, settlement: chooser.settled };
            return chooserView;
        })().finally(() => {
            if (chooserViewTask === task) chooserViewTask = null;
        });
        chooserViewTask = task;
        return task.promise;
    }

    /**
     * Read the chooser's first page and the saved decks TOGETHER, render both,
     * and record when every portrait they show has settled. Memoised: a warm
     * in flight is joined, a view read within ROOT_VIEW_FRESH_MS is reused
     * without a read (unless `revalidate`), and a chooser page read within the
     * window — the Collection's open warms it — is reused either way. Resolves
     * once the view is in the DOM; `rootViewSettlement` resolves when its
     * portraits have settled (capped).
     */
    function prepareRootView({ revalidate = false } = {}) {
        const key = rootViewKey();
        if (rootViewTask && rootViewTask.key === key) return rootViewTask.promise;
        if (!revalidate && rootViewIsFresh()) return Promise.resolve(true);
        const task = { key, promise: null };
        const warmed = !isOpen;
        task.promise = (async () => {
            const [chooser, deckList] = await Promise.all([
                prepareChooserView(),
                loadSavedDecks(),
            ]);
            if (rootViewKey() !== key || !chooser) return false;
            const view = {
                key,
                // As old as its older half: a reused chooser page keeps its age.
                at: chooser.at,
                complete: chooser.complete && Boolean(deckList),
                warmed,
                settled: false,
            };
            rootView = view;
            rootViewSettlement = Promise.all([chooser.settlement, deckList?.settled]).then(() => {
                view.settled = true;
                return true;
            });
            return true;
        })().finally(() => {
            if (rootViewTask === task) rootViewTask = null;
        });
        rootViewTask = task;
        return task.promise;
    }

    /**
     * Warm the Decks tab ahead of the press. Best-effort (docs/asset-loading.md
     * law 3): failures are the open's to report, never the warm's.
     *   - Without intent (the Collection's open): the chooser's first summoner
     *     page only, in two steps — `pictures: false` reads its data and asks
     *     for no picture (once the Collection's own page has its answers);
     *     the bare warm paints it from that read, its four portraits at low
     *     priority (once the Collection's own page's pictures have settled).
     *     The saved decks are a whole list — every deck, the 21 starters and
     *     their summoners' details — and are never read for a tab the player
     *     has not reached for.
     *   - With intent (a pointer, focus or the first touch on the tab): the
     *     whole root view, which a press within ROOT_VIEW_FRESH_MS adopts.
     */
    function warm({ intent = false, pictures = true } = {}) {
        if (isOpen) return Promise.resolve();
        let prepared;
        if (intent) prepared = prepareRootView().then(() => rootViewSettlement);
        else if (pictures) prepared = prepareChooserView().then((chooser) => chooser?.settlement);
        else {
            prepared = readChooserPage().then((read) => (
                read?.complete ? readChooserPagesAhead(read) : undefined
            ));
        }
        return prepared.then(() => undefined, () => undefined);
    }

    /**
     * The player moved on (the Collection closed): what a warm started for
     * this tab and never finished is ended — the portraits still loading, and
     * a read still out, which then paints nothing. Settled portraits keep
     * their pixels for the next entry, which rebuilds only the ended tiles.
     * An open tab is its own close's to tidy.
     */
    function dropWarm() {
        if (isOpen) return;
        warmDropEpoch += 1;
        // A deck list still being read (a warm on intent) renders nothing.
        lastSavedDeckRequestId += 1;
        disposeClosedSurfaceImages(document.getElementById('deck-builder-container'));
    }

    function beginEntryHold() {
        releaseEntryHold('superseded');
        entryHold = SurfaceEntryHold.hold(document.getElementById('deck-builder-container'), {
            label: () => uiText('deckBuilder.loadingDecks', 'Loading your decks...'),
        });
    }

    function releaseEntryHold(reason = 'ready') {
        const hold = entryHold;
        entryHold = null;
        hold?.release(reason);
    }

    /**
     * Open the deck builder
     */
    async function open(preSelectedCharacterOrOptions = null) {
        const { preSelectedCharacter, options } = normalizeOpenArgs(preSelectedCharacterOrOptions);
        isOpen = true;
        collectionPage = 1;
        deckPage = 1;
        onCloseCallback = typeof options.onClose === 'function' ? options.onClose : null;

        setPresentationMode(options);

        // Reset state upfront so user can interact during async load
        currentDeck = { name: '', character: null, cardIds: [] };
        activeTab = 'characters';
        editingDeckId = null;
        readOnlyDeck = null;
        document.getElementById('deck-name-input').value = '';
        syncReadOnlyDeckPresentation();
        document.getElementById('selected-character-display').textContent = '';
        setDeckBuilderFiltersOpen(false);
        setDeckBuilderMobilePane('catalog');
        syncResponsiveWorkspace();
        setCurrentSearch(document.getElementById('deck-builder-search')?.value || '');

        document.getElementById('character-select-section').style.display = '';
        document.getElementById('deck-builder-main').style.display = 'none';
        toggleDeckSections(true);

        // Each open has its own portrait-retry budget.
        clearSavedDeckPortraitRetry();

        /* THE TAB'S FIRST FRAME IS THE FINISHED ONE. A prepared view (see
           warm()) is presented as it stands, in this task. One read ahead of
           this press within ROOT_VIEW_FRESH_MS (a warm on intent) is adopted,
           once: nothing is read again. Any other was still correct a moment
           ago, so it stays up while a revalidation reconciles it.
           Otherwise the body is held as one unit — one loading state if the
           wait outlasts a few frames — until both reads have answered and every
           portrait they show has settled, capped once there is content. */
        const pageSizeChanged = syncSavedDeckPageSize({ preserveOffset: true });
        const adoptable = !pageSizeChanged && rootViewIsAdoptable();
        const presentable = !pageSizeChanged && rootViewIsCurrent() && rootView.settled === true;
        if (presentable && adoptable) {
            rootView.warmed = false;
            // The one thing an adopted view may still owe: a portrait missing
            // from its visible page, asked for by summoner id alone.
            retryMissingSavedDeckPortraits();
        } else if (presentable) {
            // Owed a fresh read; one already in flight is joined, not repeated.
            void prepareRootView({ revalidate: true });
        } else {
            beginEntryHold();
            await prepareRootView({ revalidate: !adoptable });
            if (rootView) rootView.warmed = false;
            if (!isOpen) return;
            const hold = entryHold;
            hold?.armCap(DECK_BUILDER_REVEAL_CAP_MS);
            void rootViewSettlement.then(() => {
                if (entryHold === hold) releaseEntryHold();
            });
            // A view adopted as it stands renders nothing, so nothing else
            // starts its retries.
            if (hasMissingSavedDeckPortrait()) scheduleSavedDeckPortraitRetry();
        }

        if (options.editDeckId) {
            const targetDeck = Array.isArray(savedDecksData)
                ? savedDecksData.find((d) => d.id === options.editDeckId)
                : null;
            if (targetDeck) {
                // The deck view has its own staged presentation; the chooser's
                // portraits are not what the player is about to see.
                releaseEntryHold();
                await handleDeckAction(
                    targetDeck.id,
                    isOfficialDeck(targetDeck) ? 'view' : 'edit',
                    savedDecksData,
                );
                return;
            }
        }

        const kept = takeKeptEdit();
        if (preSelectedCharacter) {
            releaseEntryHold();
            await selectCharacter(preSelectedCharacter);
        } else if (kept && !currentDeck.character) {
            releaseEntryHold();
            await reopenKeptEdit(kept);
        } else if (currentDeck.character) {
            // User already selected a character during async load — respect it
            await renderCollection();
            updateSaveButton();
        } else {
            renderDeck();
        }

    }

    function characterPortraitSource(char) {
        return char.imageUrl || getCharacterPortraitUrl(char.id) || '';
    }

    /** What a summoner card's portrait node was loaded from. */
    function characterPortraitKey(char) {
        const source = characterPortraitSource(char);
        return source ? `src:${source}` : `id:${char.characterId || char.id || ''}`;
    }

    /** Words change with the language; the card and its portrait stay put. */
    function applyCharacterCardText(card, char) {
        const setText = (element, value) => {
            if (element && element.textContent !== value) element.textContent = value;
        };
        const className = 'db-character-card ' + (char.theme || '');
        if (card.className !== className) card.className = className;
        if (card.getAttribute('aria-label') !== (char.name || '')) card.setAttribute('aria-label', char.name || '');
        const img = card.querySelector('img.db-char-image');
        if (img && img.alt !== (char.name || '')) img.alt = char.name || '';
        setText(card.querySelector('.db-char-name'), char.name || '');
        setText(card.querySelector('.db-char-desc'), char.description || '');
    }

    function buildCharacterCard(char) {
        const imagePath = characterPortraitSource(char);

        // Character name/description (and the id used for the portrait path) can be
        // AI-generated from user prompts for Moon Dream summoners, so they are
        // untrusted. Build with DOM APIs (textContent / setAttribute) so neither
        // text nor attribute contexts can be broken out of.
        const card = document.createElement('div');
        card.dataset.charId = char.id;
        card.dataset.portraitKey = characterPortraitKey(char);
        card.setAttribute('role', 'button');
        card.tabIndex = 0;

        const imageWrapper = document.createElement('div');
        imageWrapper.className = 'db-char-image-wrapper db-char-image-loading';
        const img = document.createElement('img');
        img.className = 'db-char-image';
        // Eager, not lazy: the chooser is prepared while the tab is hidden, and
        // a lazy image with no box is never fetched.
        img.loading = 'eager';
        img.decoding = 'async';
        if (!isOpen) img.fetchPriority = 'low';
        img.addEventListener('load', () => {
            imageWrapper.classList.remove('db-char-image-loading');
            imageWrapper.classList.remove('db-char-image-error');
            imageWrapper.classList.add('db-char-image-loaded');
        });
        img.addEventListener('error', () => {
            imageWrapper.classList.add('db-char-image-loading');
            imageWrapper.classList.add('db-char-image-error');
        });
        if (imagePath) {
            loadDeckBuilderExactImage(img, imagePath, { fit: 'contain' });
        } else {
            const identity = char.characterId || char.id;
            if (identity && typeof Cards?.resolveCardArt === 'function') {
                img.dataset.deckBuilderArtOwner = 'true';
                try {
                    const resolution = Promise.resolve(Cards.resolveCardArt(
                        {
                            id: identity,
                            imageUrl: undefined,
                            picture: char.picture,
                        },
                        img,
                        {
                            directArtOnly: true,
                            rasterReserveScale: 1,
                            fit: 'contain',
                        },
                    ));
                    void resolution.catch(() => undefined);
                    trackPortraitReadiness(img, resolution);
                } catch (_) {
                    // The known identity stays neutral and retryable; this
                    // optional presentation task must not break the chooser.
                }
            }
        }
        imageWrapper.appendChild(img);

        const info = document.createElement('div');
        info.className = 'db-char-info';
        const nameEl = document.createElement('div');
        nameEl.className = 'db-char-name';
        const descEl = document.createElement('div');
        descEl.className = 'db-char-desc';
        info.appendChild(nameEl);
        info.appendChild(descEl);

        card.appendChild(imageWrapper);
        card.appendChild(info);
        applyCharacterCardText(card, char);
        return card;
    }

    /**
     * Render the character selection grid. Reconciles against what it shows:
     * a summoner already on the page keeps its card and portrait node (only
     * its words are refreshed), and a new page is staged and swapped in whole.
     * Returns `{ committed, settled }` (see commitDeckBuilderNodes).
     */
    function renderCharacterSelectionGrid() {
        const grid = document.getElementById('db-character-grid');
        const nothing = { committed: Promise.resolve(false), settled: Promise.resolve(false) };
        if (!grid) return nothing;
        const requestId = ++characterGridRenderRequestId;
        const list = Array.isArray(charactersData) ? charactersData : [];

        if (list.length === 0) {
            disposeDeckBuilderExactImages(grid);
            grid.textContent = '';
            const error = document.createElement('div');
            error.className = 'error';
            error.textContent = uiText('deckBuilder.loadCharactersFailed', 'Failed to load characters');
            grid.appendChild(error);
            updateCharacterPagination();
            return nothing;
        }

        const existing = new Map(
            Array.from(
                grid.querySelectorAll(':scope > .db-character-card'),
                (card) => /** @type {HTMLElement} */ (card),
            ).map((card) => [card.dataset.charId, card]),
        );
        const fresh = [];
        const cards = list.map((char) => {
            const current = existing.get(char.id);
            if (
                current
                && current.dataset.portraitKey === characterPortraitKey(char)
                && portraitIsLive(current.querySelector('img.db-char-image'))
            ) {
                existing.delete(char.id);
                applyCharacterCardText(current, char);
                return current;
            }
            const card = buildCharacterCard(char);
            fresh.push(card);
            return card;
        });
        return commitDeckBuilderNodes(grid, cards, fresh, {
            isCurrent: () => requestId === characterGridRenderRequestId,
            portraitSelector: 'img.db-char-image',
            onCommit: updateCharacterPagination,
        });
    }

    /**
     * Close the deck builder
     */
    /**
     * Post-save handoff. In the embedded collection view the deck builder stays
     * mounted and live, so a save must NOT tear it down — it only resets to the
     * character browse view and refreshes the saved-decks list. Tearing it down
     * via close() would set isEmbeddedView=false while the container stays in the
     * collection host, and the next save's close() would then relocate the
     * container into the hidden modal, blanking the embedded deck builder.
     * The modal flow (deck selector "Create New Deck") still fully closes.
     */
    /* CLOSING ENDS WHAT NEVER FINISHED, AND KEEPS WHAT DID.
       Every load still pending on a closed surface is ended, so no hidden
       retry outlives it (docs/asset-loading.md law 2). The root view's settled
       portraits keep their pixels: they are exactly what the next open
       presents, and ending them would turn a warm re-entry into a reload. */
    function disposeClosedSurfaceImages(container) {
        let rootPortraitEnded = false;
        let chooserPortraitEnded = false;
        container?.querySelectorAll?.(
            'img[data-deck-builder-exact-source], img[data-deck-builder-art-owner]',
        ).forEach((image) => {
            const inRootView = Boolean(image.closest('#db-character-grid, #deck-list'));
            if (inRootView && image.getAttribute('src') && portraitIsSettled(image)) return;
            Cards?.disposeCardArtImage?.(image);
            window.MoonfallRetryingImage?.dispose?.(image);
            if (inRootView) rootPortraitEnded = true;
            if (image.closest('#db-character-grid')) chooserPortraitEnded = true;
        });
        container?.querySelectorAll?.('.deck-builder-paint-stage').forEach((stage) => stage.remove());
        // A view with an ended portrait is no longer one the next entry can
        // present — or reuse — as it stands; the next warm or open rebuilds
        // those tiles.
        if (rootPortraitEnded) rootView = null;
        if (chooserPortraitEnded) chooserView = null;
    }

    function finishAfterSave() {
        if (isEmbeddedView) {
            editingDeckId = null;
            clearDeck();
            loadSavedDecks();
            return;
        }
        // Saved: nothing of it is left to keep.
        close({ keepEdit: false });
    }

    function currentAccountId() {
        return Firebase?.getUser?.()?.uid || '';
    }

    /** The edit on screen, when it holds something its player would lose. */
    function unsavedEdit() {
        if (isReadOnlyDeckMode() || !currentDeck.character) return null;
        const nameInput = /** @type {HTMLInputElement | null} */ (document.getElementById('deck-name-input'));
        const name = nameInput?.value || '';
        const cardIds = [...currentDeck.cardIds];
        if (editingDeckId) {
            const saved = Array.isArray(savedDecksData)
                ? savedDecksData.find((deck) => deck.id === editingDeckId)
                : null;
            const unchanged = saved
                && name.trim() === deckDisplayName(saved).trim()
                && saved.character === currentDeck.character
                && JSON.stringify(saved.cardIds || []) === JSON.stringify(cardIds);
            if (unchanged) return null;
        } else if (!cardIds.length && !name.trim()) {
            return null;
        }
        return {
            account: currentAccountId(),
            editingDeckId,
            name,
            character: currentDeck.character,
            cardIds,
        };
    }

    /** The kept edit, once, for the account it belongs to; another account ends it. */
    function takeKeptEdit() {
        const kept = keptEdit;
        keptEdit = null;
        return kept && kept.account === currentAccountId() ? kept : null;
    }

    /** Back into the edit as it was closed: the same path as editing a saved deck. */
    async function reopenKeptEdit(kept) {
        // A deck deleted meanwhile comes back as a new one.
        const stillSaved = Boolean(kept.editingDeckId) && Array.isArray(savedDecksData)
            && savedDecksData.some((deck) => deck.id === kept.editingDeckId);
        readOnlyDeck = null;
        syncReadOnlyDeckPresentation();
        editingDeckId = stillSaved ? kept.editingDeckId : null;
        /** @type {HTMLInputElement} */ (document.getElementById('deck-name-input')).value = kept.name;
        currentDeck = { name: kept.name, character: kept.character, cardIds: [...kept.cardIds] };
        await selectCharacter(kept.character);
        renderDeck();
        updateSaveButton();
    }

    function close({ keepEdit = true } = {}) {
        // An open that has not put its edit back yet leaves the kept one alone.
        const edit = keepEdit ? unsavedEdit() : null;
        if (edit || !keepEdit) keptEdit = edit;
        isOpen = false;
        for (const controller of collectionWarmOwners) controller.abort();
        collectionWarmController = null;
        // The page built ahead has no reader once the surface closes.
        dropCatalogPrebuild();
        releaseEntryHold('close');
        clearSavedDeckPortraitRetry();
        disposeClosedSurfaceImages(document.getElementById('deck-builder-container'));
        // A queued prefetch has no reader once the surface closes.
        collectionPagePrefetch.cancel();
        const modal = document.getElementById('deck-builder-modal');
        const container = document.getElementById('deck-builder-container');
        // Decide embedded vs modal from the live DOM, not a flag that earlier
        // calls may have left stale, so a redundant close() can never relocate
        // an embedded container into the modal.
        const stillEmbedded = !!(container && embeddedHost && embeddedHost.contains(container));

        if (modal) {
            modal.classList.remove('deck-builder-from-collection');
            if (!stillEmbedded) {
                modal.style.display = 'none';
            }
        }

        if (!stillEmbedded) {
            if (container) {
                container.classList.remove('deck-builder-embedded');
            }
            embeddedHost = null;
        }

        // Nothing is read or rendered for a closed surface — a hidden render
        // would restart loads that closing just ended. The next entry (the
        // Collection's warm, or this open) re-reads instead, behind the view.
        markRootViewStale();
        clearDeck({ refreshChooser: false });
        editingDeckId = null;
        isEmbeddedView = false;
        // The next opener may be a different account with different holdings.
        availableCollections = [];
        collectionsResolved = false;
        renderedCollectionOptionsKey = null;

        const callback = onCloseCallback;
        onCloseCallback = null;
        if (typeof callback === 'function') {
            try {
                callback();
            } catch (err) {
                console.warn('[DeckBuilder] close callback failed:', err);
            }
        }
    }

    /**
     * Load all available cards
     */
    /**
     * Load user's saved decks
     */
    function showDeckListMessage(text) {
        const deckList = document.getElementById('deck-list');
        if (!deckList) return;
        lastSavedDeckRenderRequestId += 1;
        disposeDeckBuilderExactImages(deckList);
        deckList.removeAttribute('aria-busy');
        const message = document.createElement('p');
        message.className = 'deck-list-empty';
        message.textContent = text;
        deckList.replaceChildren(message);
    }

    /**
     * Load user's saved decks. Resolves with the list render's
     * `{ committed, settled }` once it is in the DOM or staged behind the
     * current page.
     */
    async function loadSavedDecks({ refreshCharacterDetails = false } = {}) {
        const deckList = document.getElementById('deck-list');
        if (!deckList) return null;
        const requestId = ++lastSavedDeckRequestId;

        try {
            if (MoonfallCloud?.decks) {
                const [personalDecks, officialDecks] = await Promise.all([
                    MoonfallCloud.decks.list(),
                    MoonfallCloud.decks.listOfficial(),
                ]);
                if (requestId !== lastSavedDeckRequestId) return null;
                const decks = mergeDeckBuilderDecks(personalDecks, officialDecks);
                await ensureDeckCharacterPortraits(decks || [], {
                    refreshDetails: refreshCharacterDetails,
                });
                if (requestId !== lastSavedDeckRequestId) return null;
                return renderDeckList(decks || [], { loadRequestId: requestId });
            }
            if (requestId !== lastSavedDeckRequestId) return null;
            clearSavedDeckState();
            showDeckListMessage(uiText('deckBuilder.signInToSaveDecks', 'Sign in to save decks'));
        } catch (error) {
            if (requestId !== lastSavedDeckRequestId) return null;
            console.error('[DeckBuilder] Failed to load decks:', error);
            clearSavedDeckState();
            showDeckListMessage(uiText('deckBuilder.loadDecksFailed', 'Failed to load decks'));
        }
        return null;
    }

    function unbindSavedDeckPortraitRetryListeners() {
        if (!savedDeckPortraitRetryListenersBound) return;
        savedDeckPortraitRetryListenersBound = false;
        window.removeEventListener('online', wakeSavedDeckPortraitRetry);
        window.removeEventListener('pageshow', wakeSavedDeckPortraitRetry);
        document.removeEventListener('visibilitychange', wakeSavedDeckPortraitRetry);
    }

    function clearSavedDeckPortraitRetry({ resetAttempt = true } = {}) {
        if (savedDeckPortraitRetryTimer !== null) {
            clearTimeout(savedDeckPortraitRetryTimer);
            savedDeckPortraitRetryTimer = null;
        }
        unbindSavedDeckPortraitRetryListeners();
        if (resetAttempt) {
            savedDeckPortraitRetryAttempt = 0;
            savedDeckPortraitRetryInFlight = false;
            savedDeckPortraitRetryGeneration += 1;
        }
    }

    /** Summoners of the saved decks on the visible page whose portrait is still missing. */
    function missingVisibleSavedDeckPortraitIds() {
        const { pageItems = [] } = paginateList(savedDecksData, currentSavedDeckPageSize, savedDeckPage);
        return Array.from(new Set(
            pageItems
                .map((deck) => deck?.character)
                .filter((id) => typeof id === 'string' && id && !getCharacterPortraitUrl(id)),
        ));
    }

    function hasMissingSavedDeckPortrait() {
        return missingVisibleSavedDeckPortraitIds().length > 0;
    }

    function savedDeckPortraitRetryBudgetSpent() {
        return savedDeckPortraitRetryAttempt >= SAVED_DECK_PORTRAIT_RETRY_DELAYS_MS.length;
    }

    /* A MISSING PORTRAIT IS RETRIED WITHIN A BUDGET: one attempt per
       SAVED_DECK_PORTRAIT_RETRY_DELAYS_MS entry per open, network and
       visibility wakes included, for the decks on the visible page, asking the
       details route for the summoners still missing and nothing else. The deck
       list is never read again for a portrait: it was, every 30 s without end,
       with every deck summoner's details, while one deck lacked its art. */
    function retryMissingSavedDeckPortraits() {
        if (!isOpen || document.visibilityState === 'hidden') return;
        if (savedDeckPortraitRetryTimer !== null) {
            clearTimeout(savedDeckPortraitRetryTimer);
            savedDeckPortraitRetryTimer = null;
        }
        if (savedDeckPortraitRetryInFlight) return;
        if (savedDeckPortraitRetryBudgetSpent()) {
            unbindSavedDeckPortraitRetryListeners();
            return;
        }
        const missingIds = missingVisibleSavedDeckPortraitIds();
        if (missingIds.length === 0) return;
        savedDeckPortraitRetryAttempt += 1;
        savedDeckPortraitRetryInFlight = true;
        const generation = savedDeckPortraitRetryGeneration;
        void fetchCharacterDetails(missingIds).catch((err) => {
            console.warn('[DeckBuilder] Failed to retry saved-deck portraits:', err);
        }).then(() => {
            if (generation !== savedDeckPortraitRetryGeneration) return;
            savedDeckPortraitRetryInFlight = false;
            if (!isOpen) return;
            // Reconciles in place: only a tile whose portrait arrived is
            // rebuilt, and its commit schedules the next attempt if the
            // visible page still misses one.
            renderDeckList(savedDecksData);
        });
    }

    function wakeSavedDeckPortraitRetry() {
        if (document.visibilityState === 'hidden' || !hasMissingSavedDeckPortrait()) return;
        retryMissingSavedDeckPortraits();
    }

    function scheduleSavedDeckPortraitRetry() {
        if (!isOpen || savedDeckPortraitRetryTimer !== null || savedDeckPortraitRetryInFlight) return;
        if (savedDeckPortraitRetryBudgetSpent()) {
            // This open's budget is spent; the next open starts a new one.
            unbindSavedDeckPortraitRetryListeners();
            return;
        }
        const delay = SAVED_DECK_PORTRAIT_RETRY_DELAYS_MS[savedDeckPortraitRetryAttempt];
        savedDeckPortraitRetryTimer = window.setTimeout(() => {
            savedDeckPortraitRetryTimer = null;
            retryMissingSavedDeckPortraits();
        }, delay);
        if (!savedDeckPortraitRetryListenersBound) {
            savedDeckPortraitRetryListenersBound = true;
            window.addEventListener('online', wakeSavedDeckPortraitRetry, { passive: true });
            window.addEventListener('pageshow', wakeSavedDeckPortraitRetry, { passive: true });
            document.addEventListener('visibilitychange', wakeSavedDeckPortraitRetry, { passive: true });
        }
    }

    /** Everything a saved-deck tile prints, and the portrait it shows. */
    function describeSavedDeck(deck) {
        const official = isOfficialDeck(deck);
        const displayName = deckDisplayName(deck);
        const cardCount = Number.isFinite(deck.cardCount)
            ? deck.cardCount
            : (Array.isArray(deck.cardIds) ? deck.cardIds.length : 0);
        const isDraft = !official && cardCount < MIN_DECK_SIZE;
        const draftLabel = isDraft
            ? uiText('deckBuilder.draftBadge', `${cardCount}/${MIN_DECK_SIZE}`, {
                count: cardCount,
                required: MIN_DECK_SIZE,
            })
            : '';
        const character = characterById.get(deck.character);
        const summonerName = character?.name || String(deck.character || '');
        const portraitSource = getCharacterPortraitUrl(deck.character) || '';
        return {
            official,
            displayName,
            isDraft,
            draftLabel,
            summonerName,
            portraitSource,
            signature: JSON.stringify([official, displayName, isDraft, draftLabel, summonerName, portraitSource]),
        };
    }

    /* The tile's printed face: its name bar and actions. A tile whose words
       change (a language switch, a draft reaching 15 cards) gets a new face on
       the SAME tile, so its portrait node — and its pixels — never move. */
    function buildSavedDeckFace(deck, description) {
        const { official, displayName, isDraft, draftLabel, summonerName } = description;
        const nameBar = document.createElement('div');
        nameBar.className = 'deck-list-name-bar';
        const nameSpan = document.createElement('span');
        nameSpan.className = 'deck-list-name';
        nameSpan.textContent = displayName;
        nameBar.appendChild(nameSpan);

        const summonerSpan = document.createElement('span');
        summonerSpan.className = 'deck-list-summoner';
        summonerSpan.textContent = summonerName;
        nameBar.appendChild(summonerSpan);

        if (isDraft) {
            const draftBadge = document.createElement('span');
            draftBadge.className = 'deck-list-draft-badge';
            draftBadge.textContent = draftLabel;
            nameBar.appendChild(draftBadge);
        }

        if (official) {
            const officialBadge = document.createElement('span');
            officialBadge.className = 'deck-list-official-badge';
            const lockIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            lockIcon.setAttribute('viewBox', '0 0 24 24');
            lockIcon.setAttribute('aria-hidden', 'true');
            lockIcon.setAttribute('focusable', 'false');
            const lockPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            lockPath.setAttribute('d', 'M7 10V8a5 5 0 0 1 10 0v2h1a2 2 0 0 1 2 2v8H4v-8a2 2 0 0 1 2-2h1Zm2 0h6V8a3 3 0 0 0-6 0v2Zm3 4a2 2 0 0 0-1 3.73V19h2v-1.27A2 2 0 0 0 12 14Z');
            lockPath.setAttribute('fill', 'currentColor');
            lockIcon.appendChild(lockPath);
            officialBadge.appendChild(lockIcon);
            officialBadge.appendChild(document.createTextNode(
                uiText('deckSelector.officialStarter', 'Starter'),
            ));
            nameBar.appendChild(officialBadge);
        }

        const openButton = document.createElement('button');
        openButton.type = 'button';
        openButton.className = official ? 'deck-list-view' : 'deck-list-edit';
        openButton.setAttribute(
            'aria-label',
            official
                ? uiText(
                    'deckBuilder.viewPredefinedDeck',
                    `View predefined deck: ${displayName}`,
                    { name: displayName },
                )
                : [displayName, summonerName, draftLabel].filter(Boolean).join(' — ')
        );
        openButton.appendChild(nameBar);
        openButton.addEventListener('click', () => {
            handleDeckAction(deck.id, official ? 'view' : 'edit', savedDecksData);
        });

        const actions = document.createElement('div');
        actions.className = 'deck-list-actions';
        if (!official) {
            const deleteBtn = document.createElement('button');
            deleteBtn.type = 'button';
            deleteBtn.className = 'deck-action-btn deck-action-delete';
            deleteBtn.dataset.action = 'delete';
            const deleteLabel = `${uiText('deckBuilder.delete', 'Delete')}: ${displayName}`;
            deleteBtn.title = deleteLabel;
            deleteBtn.setAttribute('aria-label', deleteLabel);
            deleteBtn.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 3h6l1 2h4v2H4V5h4l1-2Zm-2 6h10l-1 11H8L7 9Zm3 2v7h2v-7h-2Zm4 0v7h2v-7h-2Z" fill="currentColor"/></svg>';
            deleteBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                handleDeckAction(deck.id, 'delete', savedDecksData);
            });
            actions.appendChild(deleteBtn);
        }
        return actions.childElementCount > 0 ? [openButton, actions] : [openButton];
    }

    function applySavedDeckFace(item, deck, description) {
        item.classList.toggle('deck-list-item-official', description.official);
        if (description.official) item.dataset.readonly = 'true';
        else delete item.dataset.readonly;
        item.classList.toggle('deck-list-item-draft', description.isDraft);
        item.dataset.renderSignature = description.signature;
        item.querySelectorAll(':scope > .deck-list-edit, :scope > .deck-list-view, :scope > .deck-list-actions')
            .forEach((face) => face.remove());
        item.append(...buildSavedDeckFace(deck, description));
    }

    function buildSavedDeckItem(deck, description) {
        const item = document.createElement('div');
        item.className = 'deck-list-item';
        item.dataset.deckId = deck.id;
        item.dataset.portraitKey = description.portraitSource;

        // Preserve the complete landscape composition: CSS contains the
        // direct summoner image without cropping it, and the tablet rail
        // gives that same IMG a dedicated identity panel. The one visible
        // node is the only copy: it is staged hidden until it has decoded
        // (commitDeckBuilderNodes), so there is no off-DOM duplicate and no
        // post-mount enhancement — the rejected crop/zoom switch about a
        // second after first paint.
        if (description.portraitSource) {
            const portraitBackground = document.createElement('img');
            portraitBackground.className = 'deck-list-portrait-bg';
            portraitBackground.alt = '';
            portraitBackground.setAttribute('aria-hidden', 'true');
            portraitBackground.draggable = false;
            // Eager: a staged or hidden tile has no box, so a lazy one would
            // never be fetched.
            portraitBackground.setAttribute('loading', 'eager');
            portraitBackground.setAttribute('decoding', 'async');
            if (!isOpen) portraitBackground.fetchPriority = 'low';
            item.dataset.portraitSrc = description.portraitSource;
            item.dataset.portraitQuality = 'direct';
            portraitBackground.dataset.portraitSrc = description.portraitSource;
            portraitBackground.dataset.originalPortraitSrc = description.portraitSource;
            item.classList.add('deck-list-item-has-bg');
            portraitBackground.addEventListener('error', () => {
                portraitBackground.dataset.portraitLoadFailed = 'true';
                portraitBackground.dataset.portraitRenderRequest = 'retrying';
                item.dataset.portraitQuality = 'retrying';
            });
            portraitBackground.addEventListener('load', () => {
                delete portraitBackground.dataset.portraitLoadFailed;
                portraitBackground.dataset.portraitRenderRequest = 'settled';
                item.dataset.portraitQuality = 'direct';
            });
            loadDeckBuilderExactImage(
                portraitBackground,
                description.portraitSource,
                { fit: 'contain' },
            );
            item.appendChild(portraitBackground);
        } else {
            item.classList.add('deck-list-item-portrait-pending');
            item.dataset.portraitQuality = 'retrying';
        }
        applySavedDeckFace(item, deck, description);
        return item;
    }

    /**
     * Render the deck list. Reconciles by deck: a tile already on the page
     * keeps its node and portrait (a changed face is reprinted in place), and
     * a page with new tiles is staged and swapped in whole — no skeleton ever
     * replaces a page the player can see. Returns `{ committed, settled }`.
     */
    function renderDeckList(decks, { loadRequestId = null } = {}) {
        const deckList = document.getElementById('deck-list');
        const done = { committed: Promise.resolve(true), settled: Promise.resolve(true) };
        if (!deckList) return done;
        const renderRequestId = ++lastSavedDeckRenderRequestId;

        savedDecksData = decks;
        syncSavedDeckPageSize({ preserveOffset: true });

        if (decks.length === 0) {
            showDeckListMessage(uiText('deckBuilder.noSavedDecks', 'No saved decks yet'));
            updateSavedDeckPagination({ page: 1, totalPages: 1 });
            return done;
        }

        const pagination = paginateList(savedDecksData, currentSavedDeckPageSize, savedDeckPage);
        savedDeckPage = pagination.page;
        const pageDecks = pagination.pageItems;

        const existing = new Map(
            Array.from(
                deckList.querySelectorAll(':scope > .deck-list-item[data-deck-id]'),
                (item) => /** @type {HTMLElement} */ (item),
            ).map((item) => [item.dataset.deckId, item]),
        );
        const fresh = [];
        const items = pageDecks.map((deck) => {
            const description = describeSavedDeck(deck);
            const current = existing.get(deck.id);
            if (
                current
                && current.dataset.portraitKey === description.portraitSource
                && portraitIsLive(current.querySelector(':scope > img.deck-list-portrait-bg'))
            ) {
                existing.delete(deck.id);
                if (current.dataset.renderSignature !== description.signature) {
                    applySavedDeckFace(current, deck, description);
                }
                return current;
            }
            const item = buildSavedDeckItem(deck, description);
            fresh.push(item);
            return item;
        });

        const staged = fresh.length > 0 && deckBuilderIsPresented();
        if (staged) deckList.setAttribute('aria-busy', 'true');
        return commitDeckBuilderNodes(deckList, items, fresh, {
            isCurrent: () => (
                renderRequestId === lastSavedDeckRenderRequestId
                && (loadRequestId === null || loadRequestId === lastSavedDeckRequestId)
            ),
            portraitSelector: ':scope > img.deck-list-portrait-bg',
            onCommit: () => {
                deckList.removeAttribute('aria-busy');
                // A page with nothing missing stops the timer but refunds
                // nothing: paging away and back never earns more retries.
                if (isOpen && hasMissingSavedDeckPortrait()) scheduleSavedDeckPortraitRetry();
                else clearSavedDeckPortraitRetry({ resetAttempt: false });
                updateSavedDeckPagination(pagination);
            },
        });
    }

    /**
     * Handle deck list actions
     */
    async function handleDeckAction(deckId, action, decks) {
        const deck = decks.find((candidate) => candidate.id === deckId);
        if (!deck) return;
        const official = isOfficialDeck(deck);
        if (official && action !== 'view') return;

        switch (action) {
            case 'view':
                if (!official) return;
                readOnlyDeck = deck;
                editingDeckId = null;
                currentDeck = {
                    name: deckDisplayName(deck),
                    character: deck.character || null,
                    cardIds: [...deck.cardIds],
                };
                syncReadOnlyDeckPresentation();
                if (currentDeck.character) {
                    await selectCharacter(currentDeck.character);
                }
                renderDeck();
                updateSaveButton();
                break;

            case 'edit':
                readOnlyDeck = null;
                syncReadOnlyDeckPresentation();
                editingDeckId = deckId;
                document.getElementById('deck-name-input').value = deckDisplayName(deck);
                currentDeck = {
                    name: deckDisplayName(deck),
                    character: deck.character || null,
                    cardIds: [...deck.cardIds]
                };

                if (currentDeck.character) {
                    await selectCharacter(currentDeck.character);
                } else {
                    document.getElementById('character-select-section').style.display = '';
                    document.getElementById('deck-builder-main').style.display = 'none';
                    toggleDeckSections(true);
                    renderCharacterSelectionGrid();
                }

                renderDeck();
                updateSaveButton();
                break;

            case 'delete':
                if (confirm(uiText('deckBuilder.deleteConfirm', 'Delete this deck?'))) {
                    try {
                        await MoonfallCloud.decks.delete(deckId);
                        markRootViewStale();
                        await loadSavedDecks();
                    } catch (error) {
                        console.error('[DeckBuilder] Failed to delete:', error);
                    }
                }
                break;
        }
    }

    /**
     * The catalog has a page on screen worth keeping through a re-query: its
     * tiles, or the committed answer that nothing matches.
     */
    function catalogShowsCommittedPage(container) {
        if (!container) return false;
        if (container.querySelector(':scope > .deck-card-item')) return true;
        /* The committed empty answer says so itself. It used to be read off
           the grid's raster state, which every re-query overwrites with
           'loading', so a second keystroke while the first read was out no
           longer recognised it and put "Loading cards..." up (fix review #8). */
        return Boolean(container.querySelector(':scope > .collection-empty[data-committed-empty]'));
    }

    /** How many cards the committed page on screen shows, or null when none is. */
    function committedCatalogEntryCount(container) {
        if (!catalogShowsCommittedPage(container)) return null;
        return container.querySelectorAll(':scope > .deck-card-item').length;
    }

    /* EVERY EDITING SESSION STARTS FROM NOTHING. A re-query keeps the page on
       screen, and a closed or cleared builder used to leave its page in the
       grid: the next session found it and kept it as "the page on screen" —
       the previous deck's ✓ Selected marks, its tab and filters — for as long
       as its own page took to settle (fix review #1, 25 September 2026). The
       old session's read or settling stage is superseded, and its pager
       acknowledgement and loading state are released with it, so no busy
       button and no loading state outlives a close (fix review #10). */
    function resetCatalog() {
        lastCollectionRequestId += 1;
        for (const controller of collectionWarmOwners) controller.abort();
        collectionWarmController = null;
        shownCatalogTurn = null;
        catalogReadOnItsWay = null;
        keptCatalogRead = null;
        dropCatalogPrebuild();
        releaseCatalogPress();
        releaseSearchBusy();
        const container = document.getElementById('deck-builder-collection-cards');
        markCatalogStale(container, false);
        if (!container) return;
        disposeDeckBuilderExactImages(container);
        container.textContent = '';
        container.removeAttribute('aria-busy');
        delete container.dataset.rasterState;
    }

    /*
     * A PRESS BACK TO THE PAGE ON SCREEN ENDS THE TURN (review of wave E, 28
     * September 2026). Prev pressed while Next's page was on its way read the
     * page on screen again and built it from nothing in the paint stage, the
     * page dimmed past the grace until that read answered — where the
     * summoner select and the avatar picker end the turn at once. A press
     * asking, with another page on its way, for the very page on screen — the
     * same question (tab, summoner, filters, sort, search, language), page
     * and capacity (collectionPageRequestKey), its answers still current —
     * now drops the page on its way in the task of the press and reads
     * nothing. A page of an older question is never kept: the key is the
     * question it answered, and only its commit records it. The read on its
     * way, when it is the page after the one on screen, is kept for the next
     * Next (takeKeptCatalogRead), and the page built ahead carries on.
     */
    function endCatalogTurnOnShownPage() {
        const shown = shownCatalogTurn;
        if (!shown || shown.requestId === lastCollectionRequestId || !isOpen) return false;
        const container = document.getElementById('deck-builder-collection-cards');
        if (!container?.querySelector(':scope > .deck-card-item')) return false;
        if (shown.key !== collectionPageRequestKey(collectionPage)) return false;
        const onItsWay = catalogReadOnItsWay;
        // The render on its way answers nothing now.
        lastCollectionRequestId += 1;
        if (collectionWarmController !== shown.warmController) collectionWarmController?.abort();
        collectionWarmController = shown.warmController;
        shown.requestId = lastCollectionRequestId;
        catalogReadOnItsWay = null;
        container.querySelectorAll(
            ':scope > .deck-builder-collection-paint-stage:not(.deck-builder-collection-prebuild-stage)',
        ).forEach((stage) => {
            disposeDeckBuilderExactImages(stage);
            stage.remove();
        });
        container.removeAttribute('aria-busy');
        container.dataset.rasterState = 'ready';
        markCatalogStale(container, false);
        releaseCatalogPress();
        releaseSearchBusy();
        const aheadKey = collectionPageRequestKey((Number(shown.pagination?.page) || 1) + 1);
        if (onItsWay?.key === aheadKey) keepCatalogRead(onItsWay);
        else collectionPagePrefetch.schedule(shown.pagination);
        scheduleCatalogPrebuild(shown.pagination, lastCollectionRequestId);
        return true;
    }

    /** A read a turn left behind: the next render of its page takes it, and the page built ahead reads it. */
    function keepCatalogRead({ key, promise }) {
        const kept = { key, promise };
        keptCatalogRead = kept;
        promise.then((pagination) => {
            if (keptCatalogRead !== kept || !pagination) return;
            prefetchedCatalogPage = { key, pagination };
            resumeCatalogPrebuildOnData(key);
        }, () => {
            if (keptCatalogRead === kept) keptCatalogRead = null;
        });
    }

    /**
     * The kept read of this page, if any (a failed one answers null: the page
     * is read again). Keyed by the whole question, so it never answers another.
     */
    function takeKeptCatalogRead(key) {
        const kept = keptCatalogRead;
        if (kept?.key !== key) return null;
        keptCatalogRead = null;
        return kept.promise.catch(() => null);
    }

    function markCatalogStale(container, stale) {
        clearTimeout(collectionStaleTimer);
        collectionStaleTimer = null;
        if (!container) return;
        if (!stale) {
            container.classList.remove('deck-builder-cards--stale');
            return;
        }
        if (container.classList.contains('deck-builder-cards--stale')) return;
        collectionStaleTimer = setTimeout(() => {
            collectionStaleTimer = null;
            container.classList.add('deck-builder-cards--stale');
        }, COLLECTION_STALE_GRACE_MS);
    }

    /**
     * Answer a press on the element pressed: `aria-busy` at once, the visible
     * busy look only once the wait is long enough to see.
     */
    function acknowledgeCatalogPress(element) {
        /* A PRESS ON A BUTTON ALREADY WAITING KEEPS ITS ONE LOOK. Next pressed
           again while its page is on its way asks for the page after (the
           latest press wins, renderCollection); releasing and re-arming the
           look took the pending state off for a frame and put it back 60 ms
           later — a blink on every quick press. The button's own press state
           answers the press; the look stays until the page it now waits for
           lands. The look keeps the button pressable (deck-builder.css): the
           shared `.btn-pending` takes a button out of hit-testing, and a Next
           pressed while a page loaded fell through to whatever lay under it
           (snappy round 15, `deck-builder-fast-next`: the third press was
           never acknowledged and the catalog ended on the second's page in all
           twelve cells). */
        if (collectionPagerAck && collectionPagerAck.element === element) return;
        releaseCatalogPress();
        if (!element?.classList) return;
        element.setAttribute('aria-busy', 'true');
        const timer = setTimeout(() => element.classList.add('btn-pending'), PRESS_ACK_DELAY_MS);
        const release = () => {
            clearTimeout(timer);
            element.removeAttribute('aria-busy');
            element.classList.remove('btn-pending');
        };
        release.element = element;
        collectionPagerAck = release;
    }

    function releaseCatalogPress() {
        const release = collectionPagerAck;
        collectionPagerAck = null;
        release?.();
    }

    /* A KEYSTROKE IN THE SEARCH IS ANSWERED ON THE FIELD. The catalog keeps
       its page through a re-query (its loading state only past the grace), so a fast
       query showed nothing but its typed letters until its answer landed,
       600-1200 ms later on the phone seats (snappy round 15,
       `deck-builder-fast-search`: "slow acknowledgement" in every cell). The
       field says it is being answered (`aria-busy`) from the first keystroke
       until the answer to the last lands, fails, or is overtaken by another
       question — one state, never re-armed per keystroke. */
    function markSearchBusy() {
        const search = document.getElementById('deck-builder-search');
        if (search && search.getAttribute('aria-busy') !== 'true') search.setAttribute('aria-busy', 'true');
    }

    function releaseSearchBusy() {
        document.getElementById('deck-builder-search')?.removeAttribute('aria-busy');
    }

    /** The column count a balanced catalog of `entryCount` cards lays out in, or null. */
    function balancedCollectionColumns(entryCount) {
        if (isMobileDeckBuilderUi() || collectionGridColumns <= 0) return null;
        return Math.max(1, Math.min(collectionGridColumns, Math.max(1, Number(entryCount) || 0)));
    }

    /**
     * THE CATALOG'S NEXT PAGE, BUILT WHILE THIS ONE IS READ — the Collection
     * grid's twin (collection.js `scheduleCardsPrebuild`).
     *
     * Measured 25 September 2026 on the Collection (snappy round 13, veteran
     * account, Chrome desktop): a page turn waited 1.75-2.0 s for its eight
     * tiles' rasters, in 15-19 main-thread tasks of 50-130 ms, with no network
     * at all. The catalog has the same shape, so its next page's tiles are
     * built and settled ahead, in a hidden paint stage in the grid, one tile
     * per idle period, from the very answer the page turn will read
     * (`prefetchedCatalogPage`). renderCollection takes each settled tile at
     * its place; with all of them in hand the page turn needs no read, no warm
     * and no raster, and swaps within the task of the press.
     *
     * One page ahead at most. Any render drops what it does not take (a
     * filter, a search, a tab, a deck, the page turn itself), and so do
     * closing and a reset. A render of the same question and page (the tab
     * already shown, picked again; a refresh) takes nothing and keeps it: it
     * carries on after the commit, keeping every tile the next page's answer
     * still draws. An input yields: the tile in flight is dropped, and the
     * build resumes once input has been quiet. The deck marker stays current
     * on the tiles waiting here as on the page: updateCollectionCardMarker
     * reaches every tile in the grid.
     *
     * Tiles are built from the still card: a Stellar card's clips (three
     * preloading videos, one playing) are not fetched for a page nobody may
     * open. The commit attaches them (`attachCatalogStellarClips`).
     */
    function scheduleCatalogPrebuild(pagination, requestId) {
        const page = Number(pagination?.page) || 0;
        const hasNextPage = page > 0 && page < (Number(pagination?.totalPages) || 0);
        const current = isOpen && requestId === lastCollectionRequestId;
        const kept = catalogPrebuild;
        if (
            kept?.taken
            && hasNextPage
            && current
            && kept.page === page + 1
            && kept.key === collectionPageRequestKey(page + 1)
            && (!kept.stage || kept.stage.isConnected)
        ) {
            // Kept by its page's own render: it carries on, checking its tiles
            // against the next page's answer as it stands.
            kept.requestId = requestId;
            kept.taken = false;
            kept.awaitingData = false;
            kept.index = 0;
            requestCatalogPrebuildIdle(kept);
            return;
        }
        dropCatalogPrebuild();
        if (!hasNextPage || !current) return;
        const prebuild = {
            page: page + 1,
            key: collectionPageRequestKey(page + 1),
            requestId,
            /** @type {any} the answer the tiles are built from */
            pagination: null,
            /** @type {HTMLElement | null} */
            stage: null,
            index: 0,
            /** @type {Array<HTMLElement | undefined>} the settled tiles, at their page index */
            tiles: [],
            /** @type {HTMLElement | null} the tile whose raster is settling now */
            inFlight: null,
            /** @type {{ cancel: () => void } | null} */
            idle: null,
            // The next page's answer is still on its way; its arrival resumes the build.
            awaitingData: false,
            // A render took it: it builds nothing more.
            taken: false,
        };
        catalogPrebuild = prebuild;
        requestCatalogPrebuildIdle(prebuild);
    }

    function requestCatalogPrebuildIdle(prebuild, delayMs = 0) {
        if (prebuild.idle || prebuild.taken || catalogPrebuild !== prebuild) return;
        const run = (deadline) => {
            prebuild.idle = null;
            runCatalogPrebuildSlice(prebuild, deadline);
        };
        const hasIdle = typeof window.requestIdleCallback === 'function';
        if (hasIdle && delayMs <= 0) {
            const handle = window.requestIdleCallback(run);
            prebuild.idle = { cancel: () => window.cancelIdleCallback?.(handle) };
            return;
        }
        const now = () => window.performance?.now?.() ?? Date.now();
        let handle = null;
        const timer = setTimeout(() => {
            if (hasIdle) {
                handle = window.requestIdleCallback(run);
                return;
            }
            const start = now();
            run({
                didTimeout: false,
                timeRemaining: () => Math.max(0, PREBUILD_FALLBACK_SLICE_MS - (now() - start)),
            });
        }, hasIdle ? delayMs : Math.max(delayMs, PREBUILD_FALLBACK_DELAY_MS));
        prebuild.idle = {
            cancel: () => {
                clearTimeout(timer);
                if (handle !== null) window.cancelIdleCallback?.(handle);
            },
        };
    }

    function catalogPrebuildIsCurrent(prebuild) {
        return catalogPrebuild === prebuild
            && !prebuild.taken
            && isOpen
            && prebuild.requestId === lastCollectionRequestId
            && collectionPage === prebuild.page - 1
            && collectionPageRequestKey(prebuild.page) === prebuild.key
            && (!prebuild.stage || prebuild.stage.isConnected);
    }

    /** Laid out where the player can see it: no hidden tab, view or modal above it. */
    function catalogOnScreen(container) {
        if (!container?.isConnected) return false;
        for (let node = container; node && node !== document.documentElement; node = node.parentElement) {
            if (window.getComputedStyle?.(node)?.display === 'none') return false;
        }
        return true;
    }

    /** Everything a catalog tile's pixels are made of that its page's answer does not decide. */
    function catalogTileSignature(entry, container) {
        const { card, variant, ownedQty } = entry;
        return JSON.stringify([
            buildCardElementData(card, variant, variant === 'stellar' ? getStellarAsset(card.id) : null),
            variant,
            Number(ownedQty) || 0,
            isCardBalanceBanned(card),
            // The raster was settled at this geometry.
            container?.style?.getPropertyValue?.('--catalog-card-scale') || '',
            Number(window.devicePixelRatio) || 1,
        ]);
    }

    function discardPrebuiltCatalogTile(node) {
        disposeDeckBuilderExactImages(node);
        node.remove();
    }

    /** One idle period's work: at most one tile, built and set settling. */
    function runCatalogPrebuildSlice(prebuild, deadline) {
        if (!catalogPrebuildIsCurrent(prebuild)) {
            if (catalogPrebuild === prebuild && !prebuild.taken) dropCatalogPrebuild();
            return;
        }
        const container = document.getElementById('deck-builder-collection-cards');
        if (!container) {
            dropCatalogPrebuild();
            return;
        }
        if (!catalogOnScreen(container)) {
            // Nothing to settle against while hidden; look again later.
            requestCatalogPrebuildIdle(prebuild, PREBUILD_HIDDEN_RECHECK_MS);
            return;
        }
        if (!(Number(deadline?.timeRemaining?.()) >= PREBUILD_MIN_IDLE_MS)) {
            requestCatalogPrebuildIdle(prebuild);
            return;
        }
        if (prefetchedCatalogPage?.key !== prebuild.key) {
            prebuild.awaitingData = true;
            return;
        }
        if (prebuild.pagination !== prefetchedCatalogPage.pagination) {
            // The answer the page turn will take (read again after a refresh).
            prebuild.pagination = prefetchedCatalogPage.pagination;
            pruneCatalogPrebuild(prebuild, container);
        }
        const items = Array.isArray(prebuild.pagination?.items) ? prebuild.pagination.items : [];
        // Past cards with no id, and those a build carried on from its page's render holds already.
        while (
            prebuild.index < items.length
            && (!items[prebuild.index]?.card?.id || prebuild.tiles[prebuild.index])
        ) prebuild.index += 1;
        if (prebuild.index >= items.length) return;

        const index = prebuild.index;
        const entry = items[index];
        const { wrapper, raster } = buildCatalogTile(entry, { stillOnly: true });
        wrapper.dataset.tileSignature = catalogTileSignature(entry, container);
        if (entry.variant === 'stellar' && getStellarAsset(entry.card.id)?.status === 'complete') {
            catalogTilesAwaitingClips.set(wrapper, entry.card);
        }
        if (!prebuild.stage) {
            const stage = document.createElement('div');
            stage.className = 'deck-builder-collection-paint-stage deck-builder-collection-prebuild-stage';
            stage.setAttribute('aria-hidden', 'true');
            // Laid out as the page will commit, so each raster settles at its geometry.
            const columns = balancedCollectionColumns(items.length);
            if (columns !== null) stage.style.setProperty('--deckbuilder-column-count', String(columns));
            container.appendChild(stage);
            prebuild.stage = stage;
        }
        prebuild.stage.appendChild(wrapper);
        if (!raster) {
            prebuild.tiles[index] = wrapper;
            prebuild.index += 1;
            requestCatalogPrebuildIdle(prebuild);
            return;
        }
        prebuild.inFlight = wrapper;
        void settleCollectionCardRaster(raster.cardElement, raster.cardData).then((settled) => {
            // Dropped meanwhile: an input, a render, a close.
            if (prebuild.inFlight !== wrapper) return;
            prebuild.inFlight = null;
            // A raster that did not settle is the page turn's to build and wait for.
            if (settled && wrapper.isConnected) prebuild.tiles[index] = wrapper;
            else discardPrebuiltCatalogTile(wrapper);
            prebuild.index += 1;
            requestCatalogPrebuildIdle(prebuild);
        });
    }

    /**
     * The settled tiles, re-seated on the answer the build now follows: each
     * goes to the index of the card it still draws exactly; the rest go.
     */
    function pruneCatalogPrebuild(prebuild, container) {
        const bySignature = new Map();
        prebuild.tiles.forEach((node) => {
            if (node && node.parentElement === prebuild.stage) bySignature.set(node.dataset.tileSignature, node);
            else if (node) discardPrebuiltCatalogTile(node);
        });
        /** @type {Array<HTMLElement | undefined>} */
        const tiles = [];
        const items = Array.isArray(prebuild.pagination?.items) ? prebuild.pagination.items : [];
        items.forEach((entry, index) => {
            if (!entry?.card?.id) return;
            const signature = catalogTileSignature(entry, container);
            const node = bySignature.get(signature);
            if (!node) return;
            bySignature.delete(signature);
            tiles[index] = node;
        });
        bySignature.forEach((node) => discardPrebuiltCatalogTile(node));
        prebuild.tiles = tiles;
    }

    /** A Stellar tile built ahead from its still gets its clips as it commits, as a fresh build would have them. */
    function attachCatalogStellarClips(tile) {
        const card = catalogTilesAwaitingClips.get(tile);
        if (!card) return;
        catalogTilesAwaitingClips.delete(tile);
        const cardEl = tile.querySelector(':scope > .deck-builder-card');
        const imageEl = cardEl?.querySelector('.card-image');
        if (!cardEl || !imageEl || cardEl.querySelector('.stellar-loop-video')) return;
        const cardData = buildCardElementData(card, 'stellar', getStellarAsset(card.id));
        Cards?.attachStellarVideo?.(cardEl, imageEl, cardData);
        Cards?.autoPlayStellarVideo?.(cardEl);
    }

    /** The next page's answer arrived after the build asked for it. */
    function resumeCatalogPrebuildOnData(key) {
        const prebuild = catalogPrebuild;
        if (!prebuild?.awaitingData || prebuild.key !== key) return;
        prebuild.awaitingData = false;
        requestCatalogPrebuildIdle(prebuild);
    }

    /**
     * An input takes the main thread back: the tile in flight goes, and the
     * build waits until input has been quiet (PREBUILD_INPUT_QUIET_MS), not
     * just for the next idle period, which falls inside a held press.
     */
    function yieldCatalogPrebuild() {
        const prebuild = catalogPrebuild;
        if (!prebuild || prebuild.taken || (!prebuild.inFlight && !prebuild.idle)) return;
        const inFlight = prebuild.inFlight;
        prebuild.inFlight = null;
        if (inFlight) discardPrebuiltCatalogTile(inFlight);
        prebuild.idle?.cancel();
        prebuild.idle = null;
        requestCatalogPrebuildIdle(prebuild, PREBUILD_INPUT_QUIET_MS);
    }

    function setupPrebuildYield() {
        if (prebuildYieldListenersReady) return;
        prebuildYieldListenersReady = true;
        ['pointerdown', 'keydown', 'wheel', 'touchstart'].forEach((type) => {
            document.addEventListener(type, yieldCatalogPrebuild, { capture: true, passive: true });
        });
    }

    /** A render starts: the build stops (the tile in flight goes); what settled waits for its plan. */
    function takeCatalogPrebuild() {
        const prebuild = catalogPrebuild;
        if (!prebuild || prebuild.taken) return;
        prebuild.taken = true;
        prebuild.idle?.cancel();
        prebuild.idle = null;
        const inFlight = prebuild.inFlight;
        prebuild.inFlight = null;
        if (inFlight) discardPrebuiltCatalogTile(inFlight);
    }

    /** Whether the page built ahead is the one after `page` of this very question. */
    function catalogPrebuildIsAheadOf(page) {
        const prebuild = catalogPrebuild;
        return Boolean(prebuild)
            && prebuild.page === page + 1
            && prebuild.key === collectionPageRequestKey(page + 1);
    }

    /**
     * The settled tiles built ahead from this very answer, at their page index,
     * each still what a fresh build would draw.
     */
    function adoptablePrebuiltTiles(pagination, entries, container) {
        const prebuild = catalogPrebuild;
        /** @type {Array<HTMLElement | undefined>} */
        const tiles = [];
        if (!prebuild?.stage || prebuild.pagination !== pagination) return tiles;
        entries.forEach((entry, index) => {
            const node = prebuild.tiles[index];
            if (!node || node.parentElement !== prebuild.stage || !entry?.card?.id) return;
            if (node.dataset.tileSignature !== catalogTileSignature(entry, container)) return;
            tiles[index] = node;
        });
        return tiles;
    }

    function dropCatalogPrebuild() {
        const prebuild = catalogPrebuild;
        if (!prebuild) return;
        catalogPrebuild = null;
        prebuild.taken = true;
        prebuild.idle?.cancel();
        prebuild.idle = null;
        prebuild.inFlight = null;
        prebuild.tiles = [];
        if (prebuild.stage) {
            disposeDeckBuilderExactImages(prebuild.stage);
            prebuild.stage.remove();
        }
    }

    /**
     * One catalog tile, built detached: its face, badges, deck marker and
     * handlers. Returns the wrapper and its raster, if any. Shared by the
     * page render and the next page built ahead (`scheduleCatalogPrebuild`).
     */
    function buildCatalogTile(entry, { stillOnly = false } = {}) {
        const { card, variant, ownedQty } = entry;
        /** @type {{ cardElement: HTMLElement, cardData: any } | null} */
        let raster = null;
        const deckCardId = formatDeckCardId(card.id, variant);
        const safeDomId = deckCardId.replace(/[^a-zA-Z0-9_-]/g, '-');
        const inDeck = currentDeck.cardIds.includes(deckCardId);
        const isOwned = ownedQty > 0;
        const isBalanceBanned = isCardBalanceBanned(card);
        const isSelectable = isOwned && !isBalanceBanned;
        /* A starter deck opens in this editor locked: its catalog is there to
           browse and inspect, and nothing in it goes into the deck. */
        const locked = isReadOnlyDeckMode();
        const stellarAsset = variant === 'stellar' ? getStellarAsset(card.id) : null;

        const cardWrapper = document.createElement('div');
        const classes = ['deck-card-item'];
        if (variant === 'stellar') classes.push('deck-card-item-stellar');
        if (inDeck) classes.push('in-deck');
        if (!isOwned) classes.push('unowned');
        if (isBalanceBanned) classes.push('balance-banned');
        cardWrapper.className = classes.join(' ');
        cardWrapper.dataset.cardId = deckCardId;
        cardWrapper.dataset.baseCardId = card.id;
        cardWrapper.dataset.variant = variant;
        cardWrapper.draggable = isSelectable && !locked;
        cardWrapper.setAttribute('role', 'button');
        cardWrapper.setAttribute('aria-label', card.name || '');
        cardWrapper.setAttribute('aria-pressed', String(inDeck));
        cardWrapper.tabIndex = isSelectable ? 0 : -1;
        if (!isSelectable || locked) cardWrapper.setAttribute('aria-disabled', 'true');

        if (isSelectable) {
            const dragStart = (event) => {
                if (isReadOnlyDeckMode()) {
                    event.preventDefault();
                    return;
                }
                draggedDeckCardId = deckCardId;
                cardWrapper.classList.add('dragging');
                if (event.dataTransfer) {
                    event.dataTransfer.setData('text/plain', deckCardId);
                    event.dataTransfer.effectAllowed = 'copy';
                }
            };
            const dragEnd = () => {
                draggedDeckCardId = null;
                cardWrapper.classList.remove('dragging');
            };
            cardWrapper._dragstartHandler = dragStart;
            cardWrapper._dragendHandler = dragEnd;
            cardWrapper.addEventListener('dragstart', dragStart);
            cardWrapper.addEventListener('dragend', dragEnd);
        }

        if (Cards?.createCardElement) {
            const cardData = buildCardElementData(card, variant, stillOnly ? null : stellarAsset);
            const cardEl = Cards.createCardElement(
                cardData,
                `deck-builder-${safeDomId}`,
                cardFaceRenderOptions(),
            );
            cardEl.classList.add('deck-builder-card');
            cardWrapper.appendChild(cardEl);
            raster = { cardElement: cardEl, cardData };
            if (CardTilt?.bindHost) {
                CardTilt.bindHost(cardWrapper, cardEl);
            }

            if (variant === 'stellar') {
                const stellarBadge = document.createElement('div');
                stellarBadge.className = 'deck-card-stellar-badge';
                stellarBadge.textContent = `✦ ${uiText('common.stellar', 'Stellar')}`;
                cardWrapper.appendChild(stellarBadge);
            }

            if (inDeck) {
                const checkmark = document.createElement('div');
                checkmark.className = 'deck-card-check';
                checkmark.textContent = `✓ ${uiText('deckBuilder.selected', 'Selected')}`;
                cardWrapper.appendChild(checkmark);
            }

            if (!isOwned) {
                const lock = document.createElement('div');
                lock.className = 'deck-card-lock';
                lock.textContent = '🔒';
                lock.title = uiText('deckBuilder.status.notOwned', 'This card is not in your collection yet.');
                cardWrapper.appendChild(lock);
            }

        } else {
            const directImage = sanitizeImageUrlForDirectClient(card.image) || '';
            cardWrapper.innerHTML = `
                <div class="deck-card-image">
                    <img alt="${escapeHtml(card.name)}" loading="lazy">
                </div>
                <div class="deck-card-name">${escapeHtml(card.name)}</div>
                <div class="deck-card-cost">${card.cost || 0}</div>
                ${variant === 'stellar' ? `<div class="deck-card-stellar-badge">✦ ${escapeHtml(uiText('common.stellar', 'Stellar'))}</div>` : ''}
                ${inDeck ? `<div class="deck-card-check">✓ ${escapeHtml(uiText('deckBuilder.selected', 'Selected'))}</div>` : ''}
                ${!isOwned ? '<div class="deck-card-lock">🔒</div>' : ''}
            `;
            const fallbackArt = cardWrapper.querySelector('.deck-card-image img');
            if (directImage) {
                loadDeckBuilderExactImage(fallbackArt, directImage, {
                    fit: card.type === 'Character' ? 'cover' : 'contain',
                });
            } else {
                fallbackArt?.classList.add('card-art-unavailable');
            }
        }

        if (isBalanceBanned) {
            const bannedBadge = document.createElement('div');
            bannedBadge.className = 'deck-card-banned-badge';
            bannedBadge.textContent = uiText('deckBuilder.bannedBadge', 'Banned');
            bannedBadge.title = uiText(
                'deckBuilder.status.balanceBanned',
                'This card is temporarily banned from decks while it is rebalanced.',
            );
            cardWrapper.appendChild(bannedBadge);
        }

        // Click Handler
        const activateCard = () => {
            if (isReadOnlyDeckMode()) {
                announceLockedDeck();
                return;
            }
            if (!isOwned) {
                showStatus(uiText('deckBuilder.status.notOwned', 'This card is not in your collection yet.'));
                return;
            }
            if (isBalanceBanned) {
                showStatus(uiText(
                    'deckBuilder.status.balanceBanned',
                    'This card is temporarily banned from decks while it is rebalanced.',
                ));
                return;
            }
            toggleCardInDeck(deckCardId);
        };
        cardWrapper.addEventListener('click', activateCard);
        cardWrapper.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            activateCard();
        });

        // Hover Preview
        if (Cards?.createCardElement) {
            let suppressHoverPreviewUntilLeave = false;
            let hoverActive = false;
            let hoverGeneration = 0;
            // The hover a neighbour's preview handed to this card, while it builds.
            let handoffEnter = null;
            // Touch: tap inspects; the separately labelled control acts.
            // The guard below admits TouchPreview's synthetic mouseenter
            // while rejecting the compatibility event from the real tap.
            TouchPreview?.attach?.(cardWrapper, {
                // Tap inspects; the labelled control adds or removes. The
                // label is resolved per tap so it always names what will
                // actually happen to THIS card in its current state.
                // A locked starter deck offers no control: every tap inspects.
                action: () => (isReadOnlyDeckMode() ? null : {
                    label: cardWrapper.classList.contains('in-deck')
                        ? uiText('game.actionRemoveFromDeck', 'Remove from deck')
                        : uiText('game.actionAddToDeck', 'Add to deck'),
                    // Dispatch the click rather than calling the handler
                    // directly, so the action travels the exact path a real
                    // press does and nothing bound to that click is skipped.
                    run: (el) => el.dispatchEvent(new MouseEvent('click', { bubbles: true })),
                }),
            });
            const presentHoverPreview = async () => {
                hoverActive = true;
                const generation = ++hoverGeneration;
                // The preview's raster steps share cards.js's queue with a
                // tile built ahead; the page the player is reading goes first.
                yieldCatalogPrebuild();
                clearHoverPreview({ keepHandedOff: true });

                const sourceCard = cardWrapper.querySelector(':scope > .card');
                const prepareRaster = Cards.prepareCardRasterForInteraction;
                if (typeof prepareRaster === 'function') {
                    let rasterReady = false;
                    try {
                        rasterReady = await prepareRaster(sourceCard, 'hover');
                    } catch (_) {
                        rasterReady = false;
                    }
                    if (rasterReady !== true) return;
                }
                if (
                    !hoverActive
                    || generation !== hoverGeneration
                    || !cardWrapper.isConnected
                    || !isOpen
                ) return;
                const sourceRect = cardWrapper.getBoundingClientRect();

                const preview = document.createElement('div');
                preview.id = 'card-hover-preview';
                preview.className = 'card-hover-preview';
                preview.style.zIndex = HOVER_PREVIEW_Z_INDEX;
                preview.style.left = `${Math.round(sourceRect.left)}px`;
                preview.style.top = `${Math.round(sourceRect.top)}px`;
                preview.style.visibility = 'hidden';

                const cardData = buildCardElementData(card, variant, stellarAsset);

                const cardEl = Cards.createCardElement(
                    cardData,
                    `hover-preview-${safeDomId}`,
                    cardFaceRenderOptions(),
                );
                cardEl.style.transformOrigin = HOVER_PREVIEW_OPTIONS.origin;
                cardEl.style.setProperty('--card-hover-transform', 'scale(1) rotate(0deg) translateY(0%)');
                preview.appendChild(cardEl);


                const putDown = () => {
                    if (variant === 'stellar') {
                        Cards.setStellarHover(cardEl, false);
                    }
                    preview.remove();
                };
                preview.addEventListener('mouseleave', () => {
                    // Handed off, it no longer takes the pointer, so the next
                    // move reports a leave; it stays until the neighbour's
                    // preview is revealed.
                    if (HoverHandoff?.isHandedOff?.(preview)) return;
                    putDown();
                });
                preview.addEventListener('click', () => {
                    suppressHoverPreviewUntilLeave = true;
                    if (variant === 'stellar') {
                        Cards.setStellarHover(cardEl, false);
                    }
                    /* The enlarged card covers its tile, so a press usually
                       lands here: on a locked starter deck it says why. */
                    if (isReadOnlyDeckMode()) {
                        announceLockedDeck();
                    } else if (isBalanceBanned) {
                        showStatus(uiText(
                            'deckBuilder.status.balanceBanned',
                            'This card is temporarily banned from decks while it is rebalanced.',
                        ));
                    } else {
                        toggleCardInDeck(deckCardId);
                    }
                    preview.remove();
                });
                preview.style.cursor = isBalanceBanned || isReadOnlyDeckMode() ? 'not-allowed' : 'pointer';

                preview.addEventListener('wheel', (e) => {
                    forwardWheelScroll(preview, e, [
                        '.deck-builder-cards',
                        '.deck-builder-collection',
                        '.collection-content',
                        '.deck-builder-container',
                    ]);
                }, { passive: false });

                HoverHandoff?.bind?.(preview, {
                    source: cardWrapper,
                    selector: '.deck-card-item',
                    clear: clearHoverPreview,
                    handoff: true,
                    release: putDown,
                });

                document.body.appendChild(preview);
                applyStableHoverPreviewScale(cardEl, variant, sourceRect, preview, sourceCard);

                // The source may have settled only on pointer entry. Build
                // at final geometry while hidden, then transfer from that
                // locked full-card authority (or its decoded LRU) before
                // allowing a browser paint. A failed transfer never falls
                // through to a soft/pending enlarged image.
                let settledRaster = false;
                try {
                    settledRaster = await Cards.settleCardRasterForPresentation?.(
                        cardEl,
                        cardData,
                        { oversample: 1, stableGeometry: true, sourceCard, coverVisualExpansion: true },
                    ) === true;
                } catch (_) {
                    settledRaster = false;
                }
                if (
                    !settledRaster
                    || !hoverActive
                    || generation !== hoverGeneration
                    || !cardWrapper.isConnected
                    || !isOpen
                    // A tear-down from elsewhere (the shared clear, a
                    // screen's sweep) took it. The settle refuses a card
                    // already off the page; this covers the few microtasks
                    // between its last check and this line.
                    || !preview.isConnected
                ) {
                    preview.remove();
                    return;
                }

                if (CardTilt?.registerPreview) {
                    CardTilt.registerPreview(cardEl);
                }
                if (variant === 'stellar') {
                    Cards.setStellarHover(cardEl, true);
                }
                preview.style.removeProperty('visibility');
                // The preview this card took the hover from leaves in this task.
                HoverHandoff?.releaseHandedOff?.();
            };
            cardWrapper.addEventListener('mouseenter', (event) => {
                if (suppressHoverPreviewUntilLeave) return;
                if (event?.isTrusted !== false && isMobileDeckBuilderUi()) return;
                if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
                // Cards that appeared under a resting pointer are not being pointed at.
                if (!hoverIntent.admits(event)) return;
                /* A neighbour's preview handed this card the hover and made
                   itself transparent to the pointer; the browser then
                   reports its own enter onto this card on the next move.
                   That is the same hover, not a new one: restarting it
                   would drop the build under way. */
                const handoff = HoverHandoff?.isHandoffEnter?.(event) ? {} : null;
                if (!handoff && handoffEnter) return;
                if (handoff) handoffEnter = handoff;
                const endHover = HoverHandoff?.beginHover?.();
                void presentHoverPreview().finally(() => {
                    if (handoffEnter === handoff) handoffEnter = null;
                    endHover?.();
                });
            });
            TouchPreview?.attach?.(cardWrapper);

            cardWrapper.addEventListener('mouseleave', (e) => {
                suppressHoverPreviewUntilLeave = false;
                hoverActive = false;
                hoverGeneration += 1;
                // The pointer really left: its next enter is a new hover.
                handoffEnter = null;
                const preview = currentHoverPreview();
                if (preview && e.relatedTarget && (preview === e.relatedTarget || preview.contains(e.relatedTarget))) {
                    return;
                }
                if (preview) {
                    const previewCard = preview.querySelector('.card');
                    if (previewCard?.classList?.contains('stellar-card') && Cards?.setStellarHover) {
                        Cards.setStellarHover(previewCard, false);
                    }
                    preview.remove();
                }
            });
        }
        return { wrapper: cardWrapper, raster };
    }

    /**
     * Render card collection.
     *
     * A re-query (a page turn, a filter, a keystroke in the search, a tab)
     * keeps the page on screen until the next one is whole: the new tiles
     * settle in the hidden paint stage, and the commit swaps the tiles, the
     * pager and the column count in the same task. "Loading cards..." is for
     * a catalog with nothing to show (the first open, a retry).
     */
    async function renderCollection(filter = currentSearch, { resetScroll = false, refresh = false } = {}) {
        const container = document.getElementById('deck-builder-collection-cards');
        if (!container) {
            releaseSearchBusy();
            return;
        }
        syncCollectionPageSize({ preserveOffset: true });
        const searchText = setCurrentSearch(filter);
        const requestId = ++lastCollectionRequestId;
        if (collectionWarmController !== shownCatalogTurn?.warmController) collectionWarmController?.abort();
        const warmController = new AbortController();
        collectionWarmOwners.add(warmController);
        warmController.signal.addEventListener('abort', () => collectionWarmOwners.delete(warmController), { once: true });
        collectionWarmController = warmController;
        const previousShown = shownCatalogTurn;
        // The question this render answers, whatever its page (shownCatalogTurn).
        const questionKey = collectionPageRequestKey(0);
        const scrollTop = resetScroll ? 0 : container.scrollTop;
        // The page built ahead stops building; what settled waits for the plan.
        takeCatalogPrebuild();
        // A superseded request's stage never commits; it goes now.
        container.querySelectorAll(
            ':scope > .deck-builder-collection-paint-stage:not(.deck-builder-collection-prebuild-stage)',
        ).forEach((stage) => {
            disposeDeckBuilderExactImages(stage);
            stage.remove();
        });
        const keepsPage = catalogShowsCommittedPage(container);
        if (!keepsPage) {
            dropCatalogPrebuild();
            disposeDeckBuilderExactImages(container);
            previousShown?.warmController.abort();
            container.textContent = '';
            const loading = document.createElement('div');
            loading.className = 'collection-empty';
            loading.textContent = uiText('deckBuilder.loadingCards', 'Loading cards...');
            container.appendChild(loading);
        }
        markCatalogStale(container, keepsPage);
        container.setAttribute('aria-busy', 'true');
        container.dataset.rasterState = 'loading';
        const finishRender = () => {
            markCatalogStale(container, false);
            releaseCatalogPress();
            releaseSearchBusy();
        };
        // Say it BEFORE the await, not after: the phone's Filters button is
        // tapped inside this window, and an unmarked bootstrap list is exactly
        // what read as "filters don't include all options desktop has".
        // The width reservation has to happen here too — measuring a control
        // inside a display:none modal returns zero, so an init-time-only
        // reservation never binds.
        reserveCollectionFilterWidth();
        updateCollectionFilterOptions();

        if (refresh) {
            // Stale after mutations.
            collectionPagePrefetch.invalidate();
            prefetchedCatalogPage = null;
            // So is the page on screen, and any read a turn left behind.
            shownCatalogTurn = null;
            keptCatalogRead = null;
        }
        let pagination;
        try {
            const page = collectionPage;
            const key = collectionPageRequestKey(page);
            const prefetched = refresh ? null : (takeKeptCatalogRead(key) || collectionPagePrefetch.take(page));
            const read = prefetched
                ? Promise.resolve(prefetched).then((answer) => answer || fetchCollectionPage({ refresh, page }))
                : fetchCollectionPage({ refresh, page });
            catalogReadOnItsWay = { key, promise: read, requestId };
            pagination = await read;
        } catch (err) {
            if (requestId !== lastCollectionRequestId) return;
            console.error('[DeckBuilder] Failed to render collection:', err);
            finishRender();
            updateCollectionPagination(lastCollectionPagination);
            dropCatalogPrebuild();
            disposeDeckBuilderExactImages(container);
            container.textContent = '';
            container.removeAttribute('aria-busy');
            container.dataset.rasterState = 'failed';
            const failed = document.createElement('div');
            failed.className = 'collection-empty';
            failed.textContent = uiText('deckBuilder.loadCardsFailed', 'Failed to load cards.');
            container.appendChild(failed);
            // A dropped request on mobile data used to be terminal: the catalog
            // stayed empty and the collection dropdown stayed on its bootstrap
            // pair, with no way back short of closing the builder.
            const retry = document.createElement('button');
            retry.type = 'button';
            retry.id = 'deck-builder-collection-retry';
            retry.className = 'btn deck-btn deck-builder-collection-retry';
            retry.textContent = uiText('deckBuilder.retryLoadCards', 'Retry');
            retry.addEventListener('click', () => {
                void renderCollection(undefined, { resetScroll: true, refresh: true });
            });
            container.appendChild(retry);
            // Re-assert the dropdown's state: the list is still unresolved, and
            // the control must keep saying so rather than reading as complete.
            updateCollectionFilterOptions();
            return;
        }

        if (requestId !== lastCollectionRequestId) return;
        collectionPage = Number(pagination?.page) || 1;
        if (Array.isArray(pagination?.collections)) {
            availableCollections = pagination.collections;
            collectionsResolved = true;
        }
        updateCollectionFilterOptions();
        collectionPagePrefetch.schedule(pagination);
        currentPageEntries = Array.isArray(pagination?.items) ? pagination.items : [];
        currentPageEntries.forEach((entry) => {
            if (!entry?.card?.id) return;
            upsertCardCache([{
                ...entry.card,
                ownedNormalQty: entry.variant === 'normal' ? entry.ownedQty : (getOwnedNormalQty(entry.card.id) || 0),
                ownedStellarQty: entry.ownedStellarQty || (entry.variant === 'stellar' ? entry.ownedQty : (getOwnedStellarQty(entry.card.id) || 0)),
            }]);
            if (entry.variant === 'normal' && Number(entry.ownedQty) > 0) {
                ownedCardMap.set(entry.card.id, Number(entry.ownedQty) || 0);
                if (!ownedCards.includes(entry.card.id)) ownedCards.push(entry.card.id);
            }
            if (entry.variant === 'stellar' && Number(entry.ownedQty) > 0) {
                ownedCardStellarMap.set(entry.card.id, Number(entry.ownedQty) || 0);
                if (!ownedCards.includes(entry.card.id)) ownedCards.push(entry.card.id);
            }
        });
        const displayCards = currentPageEntries;
        // The column count changes with the page it lays out, in the commit;
        // a grid with nothing on screen takes it now, so the stage settles at
        // the committed geometry.
        if (!keepsPage) applyCollectionGridColumns(displayCards.length);

        // Tiles settled ahead from this very answer need no warm and no raster.
        const aheadBeforeWarm = adoptablePrebuiltTiles(pagination, displayCards, container);
        const toWarm = displayCards.filter((_, index) => !aheadBeforeWarm[index]);
        const preloader = AssetPreloader;
        if (typeof preloader?.warmCards === 'function' && toWarm.length > 0) {
            const visibleCards = toWarm.map(({ card, variant }) => buildCardElementData(
                card,
                variant,
                variant === 'stellar' ? getStellarAsset(card.id) : null,
            ));
            try {
                await waitForBoundedCardWarm(preloader.warmCards(
                    visibleCards,
                    { priority: 'high', signal: warmController.signal },
                ));
            } catch (_) {
                // The bounded helper already treats warming as an optimization;
                // the direct authoritative card URL remains the render fallback.
            }
            if (requestId !== lastCollectionRequestId) return;
        }

        if (pagination.totalItems === 0) {
            const empty = document.createElement('div');
            empty.className = 'collection-empty';
            empty.dataset.committedEmpty = 'true';
            empty.textContent = uiText('deckBuilder.noCardsMatch', 'No cards match your filters.');
            dropCatalogPrebuild();
            disposeDeckBuilderExactImages(container);
            clearHoverPreview();
            container.replaceChildren(empty);
            applyCollectionGridColumns(0);
            updateCollectionPagination(pagination);
            container.removeAttribute('aria-busy');
            container.dataset.rasterState = 'empty';
            shownCatalogTurn = collectionPageRequestKey(0) === questionKey
                ? { key: collectionPageRequestKey(collectionPage), requestId, pagination, warmController }
                : null;
            previousShown?.warmController.abort();
            finishRender();
            return;
        }

        const stage = document.createElement('div');
        stage.className = 'deck-builder-collection-paint-stage';
        stage.setAttribute('aria-hidden', 'true');
        const stageColumns = keepsPage ? balancedCollectionColumns(displayCards.length) : null;
        if (stageColumns !== null) stage.style.setProperty('--deckbuilder-column-count', String(stageColumns));
        container.appendChild(stage);
        container.dataset.rasterState = 'settling';
        const rasterCandidates = [];

        // Asked again after the warm: only a tile still waiting ahead is taken.
        const ahead = adoptablePrebuiltTiles(pagination, displayCards, container);
        displayCards.forEach((entry, index) => {
            if (ahead[index]) {
                // From its stage to this one in the same task: it never leaves
                // the document, so its settled raster stays owned.
                stage.appendChild(ahead[index]);
                return;
            }
            const { wrapper, raster } = buildCatalogTile(entry);
            stage.appendChild(wrapper);
            if (raster) rasterCandidates.push(raster);
        });
        // The rest of the page built ahead answers nothing now, unless this
        // render took none of it and that page is still the one after this:
        // kept, it carries on after the commit (scheduleCatalogPrebuild).
        const keepAhead = !ahead.some(Boolean) && catalogPrebuildIsAheadOf(collectionPage);
        if (!keepAhead) dropCatalogPrebuild();

        const settlements = rasterCandidates.map(({ cardElement, cardData }) => (
            settleCollectionCardRaster(cardElement, cardData)
        ));

        // Bounded atomic commit (docs/asset-loading.md law 2 + cohort rule):
        // the page appears together when every raster settles, or at the cap
        // with per-card retrying art wells. Metadata already arrived — a
        // slow or failed raster degrades that one card, never the grid.
        let stageCommitted = false;
        const commitStage = () => {
            if (stageCommitted) return;
            const isCurrent = requestId === lastCollectionRequestId
                && isOpen
                && stage.isConnected
                && document.getElementById('deck-builder-collection-cards') === container;
            if (!isCurrent) {
                stage.remove();
                return;
            }
            stageCommitted = true;
            const readyItems = Array.from(stage.children);
            // The page built ahead, kept by this render, is not the page.
            const aheadStage = keepAhead && catalogPrebuild?.stage?.parentElement === container
                ? catalogPrebuild.stage
                : null;
            Array.from(container.children).forEach((child) => {
                if (child !== stage && child !== aheadStage) disposeDeckBuilderExactImages(child);
            });
            if (keepsPage) clearHoverPreview();
            markCatalogStale(container, false);
            container.replaceChildren(...readyItems, ...(aheadStage ? [aheadStage] : []));
            hoverIntent.disarm();
            ahead.forEach((tile) => {
                if (tile) attachCatalogStellarClips(tile);
            });
            applyCollectionGridColumns(displayCards.length);
            updateCollectionPagination(pagination);
            container.removeAttribute('aria-busy');
            container.dataset.rasterState = 'ready';
            container.scrollTop = scrollTop;
            shownCatalogTurn = collectionPageRequestKey(0) === questionKey
                ? { key: collectionPageRequestKey(collectionPage), requestId, pagination, warmController }
                : null;
            previousShown?.warmController.abort();
            finishRender();
            scheduleCatalogPrebuild(pagination, requestId);
        };
        const revealCap = setTimeout(commitStage, DECK_BUILDER_REVEAL_CAP_MS);
        void Promise.all(settlements).then((results) => {
            clearTimeout(revealCap);
            if (!results.every((result) => result === true)) {
                console.warn('[DeckBuilder] Some card rasters did not settle; committing with retrying art wells.');
            }
            commitStage();
        }).catch(() => {
            clearTimeout(revealCap);
            commitStage();
        });
    }

    /**
     * Filter cards by search term
     */
    function filterCards(searchTerm) {
        collectionPage = 1;
        setCurrentSearch(searchTerm);
        renderCollection(undefined, { resetScroll: true });
    }

    /* Every tile carrying the card: while a re-query keeps the page on
       screen, its twin is settling in the hidden stage with the in-deck state
       it was built with, and the page that commits must agree with the deck
       (fix review #7, 25 September 2026). */
    function updateCollectionCardMarker(deckCardId, inDeck) {
        const container = document.getElementById('deck-builder-collection-cards');
        if (!container || !deckCardId) return;
        container.querySelectorAll('.deck-card-item').forEach((cardEl) => {
            if (cardEl.dataset.cardId !== deckCardId) return;
            cardEl.classList.toggle('in-deck', inDeck);
            cardEl.setAttribute('aria-pressed', String(inDeck));
            const existing = cardEl.querySelector('.deck-card-check');
            if (inDeck) {
                if (!existing) {
                    const checkmark = document.createElement('div');
                    checkmark.className = 'deck-card-check';
                    checkmark.textContent = `✓ ${uiText('deckBuilder.selected', 'Selected')}`;
                    cardEl.appendChild(checkmark);
                }
            } else if (existing) {
                existing.remove();
            }
        });
    }

    /**
     * A deck holds ONE print of a card, so choosing the other face swaps rather
     * than adds. The card count deliberately does not move — which is exactly
     * why the swap has to say so. Silent, it read as a dead press: "sometimes
     * pressing add to deck was not registering".
     */
    function announceVariantSwap(baseId, variant) {
        const name = getCachedCard(baseId)?.name || '';
        showStatus(variant === 'stellar'
            ? uiText('deckBuilder.status.swappedToStellar', `Swapped in the Stellar print of ${name}`, { name })
            : uiText('deckBuilder.status.swappedToNormal', `Swapped in the normal print of ${name}`, { name }));
    }

    /**
     * Toggle a card in/out of the current deck
     */
    function toggleCardInDeck(cardId) {
        if (isReadOnlyDeckMode()) return;
        const { baseId, variant } = parseDeckCardId(cardId);
        if (!baseId) return;
        const normalizedId = formatDeckCardId(baseId, variant);
        const index = currentDeck.cardIds.indexOf(normalizedId);

        let revealCardId = null;
        if (index >= 0) {
            currentDeck.cardIds.splice(index, 1);
            updateCollectionCardMarker(normalizedId, false);
        } else {
            if (isDeckCardBalanceBanned(normalizedId)) {
                showStatus(uiText(
                    'deckBuilder.status.balanceBanned',
                    'This card is temporarily banned from decks while it is rebalanced.',
                ));
                return;
            }
            const otherVariant = formatDeckCardId(baseId, variant === 'stellar' ? 'normal' : 'stellar');
            const otherIndex = currentDeck.cardIds.indexOf(otherVariant);
            const swapped = otherIndex >= 0;
            if (swapped) {
                currentDeck.cardIds.splice(otherIndex, 1);
                updateCollectionCardMarker(otherVariant, false);
            }
            if (currentDeck.cardIds.length >= MAX_DECK_SIZE) {
                showStatus(uiText('deckBuilder.status.deckFull', `Deck is full (max ${MAX_DECK_SIZE} cards)`, {
                    max: MAX_DECK_SIZE,
                }));
                return;
            }
            currentDeck.cardIds.push(normalizedId);
            updateCollectionCardMarker(normalizedId, true);
            revealCardId = normalizedId;
            if (swapped) announceVariantSwap(baseId, variant);
        }

        renderDeck({ revealCardId });
        updateSaveButton();
    }

    /**
     * Add a card to the deck without toggling it out.
     */
    function addCardToDeck(cardId) {
        if (isReadOnlyDeckMode()) return;
        const { baseId, variant } = parseDeckCardId(cardId);
        if (!baseId) return;
        if (getOwnedVariantQty(baseId, variant) <= 0) {
            showStatus(uiText('deckBuilder.status.notOwned', 'This card is not in your collection yet.'));
            return;
        }
        const normalizedId = formatDeckCardId(baseId, variant);
        if (isDeckCardBalanceBanned(normalizedId)) {
            showStatus(uiText(
                'deckBuilder.status.balanceBanned',
                'This card is temporarily banned from decks while it is rebalanced.',
            ));
            return;
        }
        if (currentDeck.cardIds.includes(normalizedId)) {
            return;
        }

        const otherVariant = formatDeckCardId(baseId, variant === 'stellar' ? 'normal' : 'stellar');
        const otherIndex = currentDeck.cardIds.indexOf(otherVariant);
        const swapped = otherIndex >= 0;
        if (swapped) {
            currentDeck.cardIds.splice(otherIndex, 1);
            updateCollectionCardMarker(otherVariant, false);
        }

        if (currentDeck.cardIds.length >= MAX_DECK_SIZE) {
            showStatus(uiText('deckBuilder.status.deckFull', `Deck is full (max ${MAX_DECK_SIZE} cards)`, {
                max: MAX_DECK_SIZE,
            }));
            return;
        }

        currentDeck.cardIds.push(normalizedId);
        updateCollectionCardMarker(normalizedId, true);
        if (swapped) announceVariantSwap(baseId, variant);
        renderDeck({ revealCardId: normalizedId });
        updateSaveButton();
    }

    /* A SETTLED MINI MAY BE A CANVAS, NOT AN IMG.
       The lane renders `<img class="deck-card-mini-art">`, but the raster
       sampler REPLACES that node with a `<canvas>` whenever it materialises the
       bitmap — and a canvas has no `naturalWidth`. Asking for one returns
       `undefined`, so this predicate said "not decoded" about art that was
       fully painted, `settleDeckMiniArt` returned false, and every tile kept
       `deck-card-mini-art-unsettled` — which is `opacity: 0.4` plus a shimmer
       overlay. Measured on WebKit in the read-only deck viewer: six tiles, each
       a 42x40 canvas with 576/576 opaque pixels, all stuck at opacity 0.4 and
       `rasterState: 'partial'` for good. The art was there the whole time,
       permanently dimmed. Ask each node for the size IT actually has. */
    function hasDecodedDeckMiniPixels(image) {
        if (!image) return false;
        if (image.tagName === 'CANVAS') {
            return Number(image.width) > 0 && Number(image.height) > 0;
        }
        return Number(image.naturalWidth) > 0 && Number(image.naturalHeight) > 0;
    }

    async function settleDeckMiniArt(image, sourceUrl, card) {
        const loadCardArt = Cards?.loadCardArtImage
            || Cards?.enhanceCardArt;
        if (!image) return false;
        /* THE NODE THIS RESOLVES IS NOT THE NODE IT RETURNS.
           `cards.js` materialises a bitmap with `next.replaceWith(canvas)`, so
           the `<img>` handed in here is DETACHED the moment the sampler
           succeeds — and every check below that asks `image.isConnected` then
           reports failure about art that rendered perfectly. Hold the host
           instead and re-read the live art node after each await; the
           replacement carries the same class and dataset. */
        const host = image.parentElement;
        const liveArt = () => host?.querySelector('.deck-card-mini-art') || image;
        if (!sourceUrl) {
            const identity = typeof card?.id === 'string' ? card.id.trim() : '';
            if (identity && typeof Cards?.resolveCardArt === 'function') {
                image.dataset.deckBuilderArtOwner = 'true';
                image.classList.remove('card-art-unavailable');
                try {
                    await Cards.resolveCardArt(
                        {
                            id: identity,
                            imageUrl: undefined,
                            picture: card?.picture,
                        },
                        image,
                        { directArtOnly: true, rasterReserveScale: 1 },
                    );
                } catch (_) {
                    return false;
                }
                const settledArt = liveArt();
                if (!settledArt.isConnected || !hasDecodedDeckMiniPixels(settledArt)) return false;
                return ['enhanced', 'direct'].includes(settledArt.dataset.cardArtQuality || '');
            }
            return false;
        }
        if (typeof loadCardArt !== 'function') {
            loadDeckBuilderExactImage(image, sourceUrl, { fit: 'cover', oversample: 1 });
            return false;
        }
        /* ONE REQUEST PER PICTURE. A mini is built without a source so that
           the shared loader makes the first write: it sets the element's
           request mode (cards.js, useSharedArtRequestMode) and only then its
           source, so the element and the sampler share one request. The art
           hosts vary on Origin; a `src` in the markup had already started a
           no-CORS request, and the sampler's CORS request downloaded the same
           picture again. Without the shared loader, the bare sampler samples a
           picture the element already shows, so it is given one here. */
        if (typeof Cards?.loadCardArtImage !== 'function') {
            image.setAttribute('src', sourceUrl);
            image.dataset.cardArtSource = sourceUrl;
        }
        try {
            await loadCardArt(image, sourceUrl, {
                fit: 'cover',
                oversample: 1,
            });
            let art = liveArt();
            if (!art.isConnected || art.dataset.cardArtSource !== sourceUrl) return false;
            // `decode()` belongs to HTMLImageElement; a canvas is already drawn.
            if (!hasDecodedDeckMiniPixels(art) && typeof art.decode === 'function') {
                await art.decode();
                art = liveArt();
            }
            if (!art.isConnected || !hasDecodedDeckMiniPixels(art)) return false;
            return ['enhanced', 'direct'].includes(art.dataset.cardArtQuality || '');
        } catch (_) {
            return false;
        }
    }

    function observeDeckMiniArtSettlement(settlement) {
        return new Promise((resolve) => {
            let settled = false;
            const finish = (value) => {
                if (settled) return;
                settled = true;
                clearTimeout(timeoutId);
                resolve(value === true);
            };
            const timeoutId = setTimeout(
                () => finish(false),
                DECK_MINI_ART_OBSERVATION_TIMEOUT_MS,
            );
            Promise.resolve(settlement).then(finish, () => finish(false));
        });
    }

    /* THE LANE RECONCILES; IT DOES NOT REBUILD.
       Every add or remove used to rebuild every mini on the lane page from
       markup. Each one had already settled into the sampler's canvas; each
       came back as a raw `<img decoding="async">` and ran the art pipeline
       again — raw, browser-downscaled art for a frame (or nothing while WebKit
       decodes), then the sampled swap — on the Decks tab's most frequent
       action. A mini is now kept while everything it shows is unchanged, and
       only the card that changed is built or removed. This records what each
       mini was built from and where its art stands: `settled` is null while
       the art is still settling, then the observed answer. */
    /** @type {WeakMap<Node, { signature: string, settled: boolean | null, settlement: Promise<boolean> }>} */
    const deckMiniRecords = new WeakMap();

    /** Everything a mini shows or binds; any difference rebuilds it. */
    function deckMiniSignature(entry, lane) {
        return JSON.stringify([
            entry.deckCardId,
            entry.card.cost || 0,
            entry.card.name,
            sanitizeImageUrlForDirectClient(entry.card.image) || '',
            entry.card.picture || '',
            isCardBalanceBanned(entry.card),
            lane.readOnly,
            lane.removeText,
            lane.bannedText,
            lane.capabilities,
        ]);
    }

    /** One selected-deck mini, with its remove control and previews bound. */
    function buildDeckMini(entry, { readOnly, removeText, bannedText }) {
        const deckCardId = entry.deckCardId;
        const { baseId, variant } = parseDeckCardId(deckCardId);
        const card = entry.card;
        const shell = document.createElement('div');
        shell.innerHTML = `
            <div class="deck-card-mini ${variant === 'stellar' ? 'deck-card-mini-stellar' : ''} ${readOnly ? 'deck-card-mini-readonly' : ''} ${isCardBalanceBanned(card) ? 'deck-card-mini-banned' : ''}" data-card-id="${escapeHtml(deckCardId)}">
                <img class="deck-card-mini-art" data-deck-builder-art-owner="true" alt="" aria-hidden="true" draggable="false" loading="eager" decoding="async" fetchpriority="high">
                <span class="deck-card-mini-cost">${card.cost || 0}</span>
                <span class="deck-card-mini-name">${escapeHtml(card.name)}</span>
                ${variant === 'stellar' ? '<span class="deck-card-mini-variant">✦</span>' : ''}
                ${isCardBalanceBanned(card) ? `<span class="deck-card-mini-banned-label">${escapeHtml(bannedText)}</span>` : ''}
                ${readOnly ? '' : `<button type="button" class="deck-card-remove" title="${escapeHtml(removeText)}" aria-label="${escapeHtml(`${removeText}: ${card.name}`)}">&times;</button>`}
            </div>
        `;
        const item = /** @type {HTMLElement} */ (shell.firstElementChild);

        item.querySelector('.deck-card-remove')?.addEventListener('click', (e) => {
            e.stopPropagation();
            toggleCardInDeck(deckCardId);
        });

        if (card && Cards?.createCardElement) {
            let suppressHoverPreviewUntilLeave = false;
            let hoverActive = false;
            let hoverGeneration = 0;
            // The hover a neighbour's preview handed to this mini, while it builds.
            let handoffEnter = null;
            // Touch: tap enlarges, tap again acts. The guard above already admits the
            // synthetic (untrusted) mouseenter this dispatches while rejecting the
            // compatibility one a real tap produces.
            /* Tap inspects; the labelled control acts — the same rule the
               collection grid follows, which is why ADDING a card worked on
               a phone and removing one did not. With no action declared the
               second tap fell through to the generic path, which clicks the
               LATCHED element; the latched element is the tile, while the
               remove handler is bound to the button inside it. The click
               landed on the wrong node and the card stayed in the deck.
               Reported from Android: "removing them button didn't work". */
            TouchPreview?.attach?.(item, readOnly ? undefined : {
                action: () => ({
                    label: uiText('game.actionRemoveFromDeck', 'Remove from deck'),
                    // Through the button that owns the handler, so the
                    // action travels the exact path a real press does.
                    run: (el) => el.querySelector('.deck-card-remove')
                        ?.dispatchEvent(new MouseEvent('click', { bubbles: true })),
                }),
            });
            const presentHoverPreview = async () => {
                hoverActive = true;
                const generation = ++hoverGeneration;
                // Its raster goes before a catalog tile built ahead.
                yieldCatalogPrebuild();
                clearHoverPreview({ keepHandedOff: true });

                // A kept mini outlives later card reads (a catalog page
                // refreshes the cached record), so the preview reads the
                // card as it is now, not as it was when the mini was built.
                const liveCard = getCachedCard(baseId) || card;
                const stellarAsset = variant === 'stellar' ? getStellarAsset(liveCard.id) : null;
                const cardData = buildCardElementData(liveCard, variant, stellarAsset);
                const warm = AssetPreloader?.warmCards?.(
                    [cardData],
                    { priority: 'high' },
                );
                if (warm && typeof warm.then === 'function') {
                    await waitForBoundedCardWarm(warm);
                }
                if (
                    !hoverActive
                    || generation !== hoverGeneration
                    || !item.isConnected
                    || !isOpen
                ) return;

                // Center preview on screen so it doesn't block sidebar navigation
                const vw = window.innerWidth || document.documentElement.clientWidth;
                const vh = window.innerHeight || document.documentElement.clientHeight;
                const centerAnchor = { left: vw / 2, top: vh / 2, width: 0, height: 0 };

                const preview = document.createElement('div');
                preview.className = 'card-hover-preview';
                preview.id = 'card-hover-preview';
                preview.style.zIndex = HOVER_PREVIEW_Z_INDEX;
                preview.style.left = `${Math.round(vw / 2)}px`;
                preview.style.top = `${Math.round(vh / 2)}px`;
                preview.style.visibility = 'hidden';

                const cardEl = Cards.createCardElement(
                    cardData,
                    'preview-card',
                    cardFaceRenderOptions(),
                );
                cardEl.style.transformOrigin = HOVER_PREVIEW_OPTIONS.origin;
                cardEl.style.setProperty('--card-hover-transform', 'scale(1) rotate(0deg) translateY(0%)');
                preview.appendChild(cardEl);
                const putDown = () => {
                    if (variant === 'stellar') {
                        Cards.setStellarHover(cardEl, false);
                    }
                    preview.remove();
                };
                HoverHandoff?.bind?.(preview, {
                    source: item,
                    selector: '.deck-card-mini',
                    clear: clearHoverPreview,
                    handoff: true,
                    release: putDown,
                });
                document.body.appendChild(preview);

                // The selected-deck thumbnail may be a deliberately small
                // derived raster. Never stretch/copy it into the enlarged
                // preview. The authority source was just warmed above, so
                // set final geometry first and materialize popup-sized pixels
                // while the preview remains hidden — for as long as the shared
                // popup's cap, never longer. docs/asset-loading.md law 2, as
                // cards.js showCardPreviewFor does it: gate the art swap, never
                // the element. A mini paints no card of its own, so on a slow
                // link its picture is still on the wire here; waiting on the
                // settle outright held the popup hidden for the whole download
                // and removed it when the load gave up, with the pointer still
                // on the mini (real/deck-mini-cold-hover.real.spec.js).
                applyStableHoverPreviewScale(cardEl, variant, centerAnchor, preview);
                let settled = false;
                try {
                    const settlement = Cards.settleCardRasterForPresentation?.(
                        cardEl,
                        cardData,
                        { oversample: 1, stableGeometry: true, coverVisualExpansion: true },
                    );
                    settled = await (Cards?.withHoverPreviewArtGrace
                        ? Cards.withHoverPreviewArtGrace(settlement)
                        : settlement);
                } catch (_) {
                    settled = false;
                }
                if (
                    !hoverActive
                    || generation !== hoverGeneration
                    || !item.isConnected
                    || !isOpen
                    // A tear-down from elsewhere (the shared clear, a
                    // screen's sweep) took it. The settle refuses a card
                    // already off the page; this covers the few microtasks
                    // between its last check and this line.
                    || !preview.isConnected
                ) {
                    preview.remove();
                    return;
                }
                // `null` is the cap: the art is still on its way and lands
                // behind the frame and text. `false` ended this attempt: keep
                // the face inspectable without its undecoded paint, never
                // invented art.
                if (settled === false) Cards?.presentUnavailableArtWell?.(cardEl);
                if (CardTilt?.registerPreview) {
                    CardTilt.registerPreview(cardEl);
                }


                if (variant === 'stellar') {
                    Cards.setStellarHover(cardEl, true);
                }

                preview.addEventListener('mouseleave', () => {
                    // Handed off, it no longer takes the pointer, so the next
                    // move reports a leave; it stays until the neighbour's
                    // preview is revealed.
                    if (HoverHandoff?.isHandedOff?.(preview)) return;
                    putDown();
                });
                preview.addEventListener('click', () => {
                    suppressHoverPreviewUntilLeave = true;
                    if (variant === 'stellar') {
                        Cards.setStellarHover(cardEl, false);
                    }
                    if (!readOnly) toggleCardInDeck(deckCardId);
                    preview.remove();
                });
                preview.style.cursor = readOnly ? 'default' : 'pointer';

                preview.addEventListener('wheel', (e) => {
                    forwardWheelScroll(preview, e, [
                        '.deck-builder-deck-cards',
                        '.deck-builder-container',
                        '.collection-content',
                    ]);
                }, { passive: false });

                preview.style.removeProperty('visibility');
                // The preview this mini took the hover from leaves in this task.
                HoverHandoff?.releaseHandedOff?.();
            };
            item.addEventListener('mouseenter', (event) => {
                if (suppressHoverPreviewUntilLeave) return;
                /* The same guard the card POOL beside this one carries, and
                   for the same reason: on a touch device the ONLY event
                   that may open a preview is TouchPreview's own synthetic
                   (untrusted) one. A trusted mouseenter there is a
                   compatibility or boundary event — the browser re-hit-tests
                   under the resting finger whenever the element beneath it
                   changes, and the act control hiding is exactly such a
                   change. Without this, a deck tile that happened to sit
                   under the control opened a centred preview the player
                   never asked for. The 700ms suppression window below is a
                   TIME bound, not a device one, so it does not cover a
                   boundary event that arrives later. */
                if (event?.isTrusted !== false && isMobileDeckBuilderUi()) return;
                if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
                if (!hoverIntent.admits(event)) return;
                /* A neighbour's preview handed this mini the hover and made
                   itself transparent to the pointer; the browser then reports
                   its own enter onto this mini on the next move. That is the
                   same hover, not a new one. */
                const handoff = HoverHandoff?.isHandoffEnter?.(event) ? {} : null;
                if (!handoff && handoffEnter) return;
                if (handoff) handoffEnter = handoff;
                const endHover = HoverHandoff?.beginHover?.();
                void presentHoverPreview().finally(() => {
                    if (handoffEnter === handoff) handoffEnter = null;
                    endHover?.();
                });
            });
            item.addEventListener('mouseleave', (e) => {
                suppressHoverPreviewUntilLeave = false;
                hoverActive = false;
                hoverGeneration += 1;
                // The pointer really left: its next enter is a new hover.
                handoffEnter = null;
                const preview = currentHoverPreview();
                const toward = /** @type {Node | null} */ (e.relatedTarget);
                if (preview && toward && (preview === toward || preview.contains(toward))) {
                    return;
                }
                if (preview) {
                    const previewCard = preview.querySelector('.card');
                    if (previewCard?.classList?.contains('stellar-card') && Cards?.setStellarHover) {
                        Cards.setStellarHover(previewCard, false);
                    }
                    preview.remove();
                }
            });
        }
        return item;
    }

    /** Start a new mini's art, and record where it stands. */
    function startDeckMiniArt(item, card) {
        const record = deckMiniRecords.get(item);
        const image = item.querySelector('.deck-card-mini-art');
        const sourceUrl = sanitizeImageUrlForDirectClient(card?.image) || '';
        // This deadline observes presentation readiness only. The exact
        // visible-image owner keeps retrying its source after the lane is
        // released, so transport failure cannot strand aria-busy or turn
        // into replacement artwork.
        const settlement = settleDeckMiniArt(image, sourceUrl, card);
        record.settlement = observeDeckMiniArtSettlement(settlement).then((settled) => {
            record.settled = settled;
            return settled;
        });
        /* A rebuild used to request every mini's art again, so a mini that
           missed the observation window was retried by the next toggle. A kept
           mini is not rebuilt; the loader behind it keeps retrying on its own,
           so when its art does land the unfinished state lifts right then. */
        void Promise.resolve(settlement).then((settled) => {
            if (settled !== true || record.settled !== false) return;
            record.settled = true;
            item.classList.toggle('deck-card-mini-art-unsettled', false);
            const lane = item.parentElement;
            if (lane?.id === 'deck-builder-deck-cards' && !lane.hasAttribute('aria-busy')) {
                presentDeckLaneArt(lane);
            }
        }, () => undefined);
    }

    /* The lane's art state, read from its minis: a mini whose art has not
       settled reads as unfinished, and the lane is ready only when every mini
       is. `toggle` with a force writes nothing when the class already agrees,
       so a kept, settled mini is not touched. */
    function presentDeckLaneArt(container) {
        let ready = true;
        Array.from(container.children).forEach((item) => {
            const record = deckMiniRecords.get(item);
            if (!record) return;
            item.classList.toggle('deck-card-mini-art-unsettled', record.settled !== true);
            ready = ready && record.settled === true;
        });
        container.removeAttribute('aria-busy');
        const rasterState = ready ? 'ready' : 'partial';
        if (container.dataset.rasterState !== rasterState) container.dataset.rasterState = rasterState;
    }

    /* Only what changed moves. A kept mini is never detached — a detach drops
       its hover and restarts the unfinished shimmer — and a new one goes
       straight to its place. What leaves (a removed card, the empty-deck
       line) is disposed first. */
    function commitDeckLane(container, laneItems) {
        const wanted = new Set(laneItems);
        let removedMini = false;
        Array.from(container.childNodes).forEach((child) => {
            if (wanted.has(child)) return;
            if (/** @type {Element} */ (child).classList?.contains('deck-card-mini')) removedMini = true;
            disposeDeckBuilderExactImages(child);
            child.remove();
        });
        laneItems.forEach((item, index) => {
            const current = container.children[index] || null;
            if (current !== item) container.insertBefore(item, current);
        });
        /* A removal slides the cards after it up under the pointer that pressed
           Remove. An addition is pressed on the catalog, whose hovered card
           stays hovered: disarming then would re-enter it on the next move. */
        if (removedMini) hoverIntent.disarm();
    }

    /**
     * Render the current deck
     */
    function renderDeck({ revealCardId = null } = {}) {
        const container = document.getElementById('deck-builder-deck-cards');
        const countEl = document.getElementById('deck-card-count');
        const readOnly = isReadOnlyDeckMode();
        const renderRequestId = ++deckRenderRequestId;

        if (countEl) countEl.textContent = currentDeck.cardIds.length;
        if (!container) return;
        syncDeckPageSize({ preserveOffset: true });

        if (currentDeck.cardIds.length === 0) {
            deckPage = 1;
            updateDeckPagination({ page: 1, totalPages: 1 });
            container.classList.add('is-empty');
            container.removeAttribute('aria-busy');
            container.dataset.rasterState = 'empty';
            disposeDeckBuilderExactImages(container);
            container.textContent = '';
            const empty = document.createElement('p');
            empty.className = 'deck-empty';
            const mobileUi = !!Platform?.isMobileUi?.();
            empty.textContent = mobileUi
                ? uiText('deckBuilder.tapCardsToAdd', 'Tap cards to add them to your deck')
                : uiText('deckBuilder.clickCardsToAdd', 'Click cards to add them to your deck');
            container.appendChild(empty);
            return;
        }

        const missingCardIds = Array.from(new Set(
            currentDeck.cardIds
                .map((deckCardId) => parseDeckCardId(deckCardId).baseId)
                .filter((cardId) => cardId && !getCachedCard(cardId))
        ));

        if (missingCardIds.length > 0) {
            fetchCardDetails(missingCardIds).then(() => {
                if (isOpen) {
                    renderDeck({ revealCardId });
                }
            }).catch((err) => {
                console.warn('[DeckBuilder] Failed to fetch deck card details:', err);
            });
        }

        const deckEntries = currentDeck.cardIds.map(deckCardId => {
            const { baseId, variant } = parseDeckCardId(deckCardId);
            const card = getCachedCard(baseId);
            if (!card) return null;
            return { card, variant, deckCardId };
        }).filter(Boolean);

        if (deckEntries.length === 0) {
            container.dataset.rasterState = 'waiting-data';
            return;
        }

        deckEntries.sort((a, b) => {
            return (a.card.cost || 0) - (b.card.cost || 0) || a.card.name.localeCompare(b.card.name);
        });

        if (revealCardId) {
            const revealIndex = deckEntries.findIndex((entry) => entry.deckCardId === revealCardId);
            if (revealIndex >= 0) deckPage = Math.floor(revealIndex / deckPageSize) + 1;
        }
        const pagination = paginateList(deckEntries, deckPageSize, deckPage);
        deckPage = pagination.page;

        const lane = {
            readOnly,
            removeText: uiText('deckBuilder.remove', 'Remove'),
            bannedText: uiText('deckBuilder.bannedBadge', 'Banned'),
            // A mini built while the card renderer was still loading took the
            // exact-source art path and bound no preview; rebuild it once the
            // renderer can do both.
            capabilities: [
                typeof (Cards?.loadCardArtImage || Cards?.enhanceCardArt) === 'function',
                typeof Cards?.resolveCardArt === 'function',
                Boolean(Cards?.createCardElement),
            ].map(Number).join(''),
        };
        const kept = new Map();
        Array.from(container.children).forEach((child) => {
            const cardId = /** @type {HTMLElement} */ (child).dataset.cardId;
            if (cardId && deckMiniRecords.has(child) && !kept.has(cardId)) kept.set(cardId, child);
        });
        const built = [];
        const laneItems = pagination.pageItems.map((entry) => {
            const signature = deckMiniSignature(entry, lane);
            const existing = kept.get(entry.deckCardId);
            if (existing && deckMiniRecords.get(existing).signature === signature) {
                kept.delete(entry.deckCardId);
                return existing;
            }
            const item = buildDeckMini(entry, lane);
            deckMiniRecords.set(item, { signature, settled: null, settlement: Promise.resolve(false) });
            built.push({ item, card: entry.card });
            return item;
        });

        /* docs/asset-loading.md law 2, the same rule the catalog page beside
           this one already follows: metadata has arrived, so the lane presents
           NOW. Art is the only thing allowed to be late, and it retries one
           mini at a time without inventing replacement artwork.

           Holding the whole lane until every mini rastered is what produced the
           reported "the selected state is desynchronised from what shows up on
           the list to the right": one image that failed to settle discarded the
           entire replacement lane, so the header counted "1 / 15" while the
           list beside it still read "Tap cards to add them to your deck". A
           deck list that disagrees with the deck is worse than a soft-looking
           thumbnail. */
        commitDeckLane(container, laneItems);
        container.classList.remove('is-empty');
        updateDeckPagination(pagination);
        built.forEach(({ item, card }) => startDeckMiniArt(item, card));

        const records = laneItems.map((item) => deckMiniRecords.get(item));
        if (records.every((record) => record.settled !== null)) {
            presentDeckLaneArt(container);
            return;
        }
        container.setAttribute('aria-busy', 'true');
        container.dataset.rasterState = 'settling';
        // Kept minis still settling from an earlier render are waited on too.
        void Promise.all(records.map((record) => record.settlement)).then(() => {
            const isCurrent = renderRequestId === deckRenderRequestId
                && isOpen
                && document.getElementById('deck-builder-deck-cards') === container;
            if (!isCurrent) return;
            presentDeckLaneArt(container);
        }).catch(() => {
            if (renderRequestId !== deckRenderRequestId) return;
            laneItems.forEach((item) => item.classList.add('deck-card-mini-art-unsettled'));
            container.removeAttribute('aria-busy');
            container.dataset.rasterState = 'partial';
        });
    }

    /**
     * Update the deck status and save button
     */
    function updateSaveButton() {
        const readOnly = isReadOnlyDeckMode();
        const name = document.getElementById('deck-name-input')?.value.trim();
        const hasName = name && name.length > 0;
        const count = currentDeck.cardIds.length;
        const hasCharacter = !!currentDeck.character;
        const withinMax = count <= MAX_DECK_SIZE;
        const hasBannedCards = currentDeck.cardIds.some(isDeckCardBalanceBanned);

        const saveBtn = document.getElementById('deck-save-btn');
        if (saveBtn) {
            saveBtn.hidden = readOnly;
            saveBtn.disabled = readOnly || !(hasName && hasCharacter && withinMax && !hasBannedCards);
            const isComplete = count === MAX_DECK_SIZE;
            saveBtn.textContent = isComplete
                ? uiText('deckBuilder.saveDeck', 'Save Deck')
                : uiText('deckBuilder.saveDraft', 'Save Draft');
        }

        const statusEl = document.getElementById('deck-status');
        if (statusEl) {
            if (readOnly) {
                statusEl.textContent = uiText(
                    'deckBuilder.starterLocked',
                    "Locked: starter decks can't be edited",
                );
                statusEl.className = 'deck-status readonly';
            } else if (hasBannedCards) {
                statusEl.textContent = uiText(
                    'deckBuilder.status.removeBannedCards',
                    'Remove banned cards before saving.',
                );
                statusEl.className = 'deck-status error';
            } else if (count < MIN_DECK_SIZE) {
                statusEl.textContent = uiText('deckBuilder.status.needMoreCards', `Need ${MIN_DECK_SIZE - count} more cards`, {
                    count: MIN_DECK_SIZE - count,
                });
                statusEl.className = 'deck-status warning';
            } else if (count > MAX_DECK_SIZE) {
                statusEl.textContent = uiText('deckBuilder.status.tooManyCards', 'Too many cards!');
                statusEl.className = 'deck-status error';
            } else {
                statusEl.textContent = uiText('deckBuilder.status.readyToSave', 'Ready to save');
                statusEl.className = 'deck-status success';
            }
        }
    }

    /**
     * Save the current deck
     */
    async function saveDeck() {
        if (isReadOnlyDeckMode()) return;
        const name = document.getElementById('deck-name-input')?.value.trim();
        if (!name) return;

        if (currentDeck.cardIds.length > MAX_DECK_SIZE) {
            showStatus(uiText('deckBuilder.status.tooManyCards', 'Too many cards!'));
            return;
        }
        if (!currentDeck.character) {
            showStatus(uiText('deckBuilder.status.selectCharacter', 'Select a character first'));
            return;
        }
        if (currentDeck.cardIds.some(isDeckCardBalanceBanned)) {
            showStatus(uiText(
                'deckBuilder.status.removeBannedCards',
                'Remove banned cards before saving.',
            ), 'error');
            return;
        }

        currentDeck.name = name;
        const isIncomplete = currentDeck.cardIds.length < MIN_DECK_SIZE;

        const saveBtn = document.getElementById('deck-save-btn');
        const originalText = saveBtn.textContent;
        saveBtn.disabled = true;
        saveBtn.textContent = uiText('deckBuilder.status.saving', 'Saving...');

        try {
            if (MoonfallCloud?.decks) {
                if (editingDeckId) {
                    await MoonfallCloud.decks.update(editingDeckId, name, currentDeck.character, currentDeck.cardIds);
                } else {
                    await MoonfallCloud.decks.create(name, currentDeck.character, currentDeck.cardIds);
                    if (MoonfallAnalytics) MoonfallAnalytics.capture('deck_created', { deckSize: currentDeck.cardIds.length });
                }
                // The next entry must read the saved list again.
                markRootViewStale();

                showStatus(
                    isIncomplete
                        ? uiText('deckBuilder.status.savedDraft', 'Draft saved! Finish your deck before playing.')
                        : uiText('deckBuilder.status.saved', 'Deck saved successfully!'),
                    'success',
                );
                finishAfterSave();
            } else {
                showStatus(uiText('deckBuilder.status.cloudUnavailable', 'Error: Cloud services not available'), 'error');
            }
        } catch (error) {
            console.error('[DeckBuilder] Failed to save deck:', error);
            showStatus(uiText('deckBuilder.status.saveFailed', 'Failed to save deck'), 'error');
        } finally {
            saveBtn.disabled = false;
            saveBtn.textContent = originalText;
        }
    }

    /**
     * Show a non-blocking toast for save / validation feedback. Reuses the
     * shared `.collection-toast*` styles already shipped in deck-builder.css.
     */
    let deckBuilderToastHost = null;
    function ensureDeckBuilderToastHost() {
        if (deckBuilderToastHost && deckBuilderToastHost.isConnected) return deckBuilderToastHost;
        const host = document.createElement('div');
        host.className = 'collection-toast-host';
        document.body.appendChild(host);
        deckBuilderToastHost = host;
        return host;
    }
    /* A press on a locked starter deck's catalog says why nothing happened:
       in words, and on the padlock of the deck's heading, which shakes. */
    function announceLockedDeck() {
        showStatus(uiText('deckBuilder.starterLocked', "Locked: starter decks can't be edited"));
        const heading = document.querySelector('#deck-builder-container .deck-heading-starter');
        if (!heading?.animate || window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;
        heading.animate([
            { transform: 'translateX(0)' },
            { transform: 'translateX(-3px)' },
            { transform: 'translateX(3px)' },
            { transform: 'translateX(-2px)' },
            { transform: 'translateX(0)' },
        ], { duration: 320, easing: 'ease-in-out' });
    }

    function showStatus(msg, tone = 'info') {
        if (!msg) return;
        const host = ensureDeckBuilderToastHost();
        const toast = document.createElement('div');
        toast.className = `collection-toast collection-toast-${tone}`;
        toast.textContent = msg;
        host.appendChild(toast);
        setTimeout(() => toast.classList.add('collection-toast-exit'), 2500);
        setTimeout(() => toast.parentNode && toast.parentNode.removeChild(toast), 2900);
    }

    /**
     * Clear the current deck
     */
    function clearDeck({ refreshChooser = true } = {}) {
        currentDeck = { name: '', character: null, cardIds: [] };
        readOnlyDeck = null;
        activeTab = 'characters';
        collectionPage = 1;
        deckPage = 1;
        resetCatalog();
        document.getElementById('deck-name-input').value = '';
        editingDeckId = null;
        syncReadOnlyDeckPresentation();
        setDeckBuilderFiltersOpen(false);
        setDeckBuilderMobilePane('catalog');

        document.getElementById('character-select-section').style.display = '';
        document.getElementById('deck-builder-main').style.display = 'none';

        // Show root sections again
        toggleDeckSections(true);

        document.getElementById('selected-character-display').textContent = '';
        document.querySelectorAll('#deck-builder-container .character-btn').forEach(btn => btn.classList.remove('selected'));
        document.querySelectorAll('#deck-builder-container .deck-tab[data-tab]').forEach(tab => {
            tab.classList.toggle('active', tab.dataset.tab === 'characters');
        });

        if (refreshChooser) {
            loadCharactersPage(1, { force: true }).then(() => renderCharacterSelectionGrid());
        }
        renderDeck();
        updateSaveButton();
    }

    /**
     * Escape HTML for safe rendering
     */
    function escapeHtml(text) {
        return String(text ?? '').replace(/[&<>"']/g, (char) => ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
        }[char]));
    }

    // Public API
    return {
        init,
        open,
        close,
        warm,
        dropWarm,
        isOpen: () => isOpen,
    };
})();

// Initialize on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => DeckBuilder.init());
} else {
    DeckBuilder.init();
}

export { DeckBuilder };
